import { googleAddressToMemberFields } from "../../../../utils/googleAddressAutocomplete";
import { useCmpOrganizationNoun } from "../../../../contexts/CmpOrganizationNounContext";
import {
  applyCmpOrganizationNoun,
  getCmpBoardWording,
} from "../../../../utils/cmpOrganizationNoun";
import MemberContactAccessGrant from "../MemberContactAccessGrant";
import { getMemberOwnershipDiversityInput, MemberOwnershipDiversityField } from "../MemberOwnershipDiversityField";
import { contactSameAsPrimaryAccessMessage } from "../UpdateMember/useMemberContactAccounts";
import MissingRequiredFieldsDialog, { MissingRequiredFieldsRow } from "../../../common/dashboard/MissingRequiredFieldsDialog";
import {
  contactAccessMissingDetailsMessage,
  getMemberBillingAddressRows,
  getMemberContactRows,
  isContactAccessDetailsMissing,
  MemberContactValidationInput,
} from "../memberContactValidation";
import { memberHearAboutOptions as hearAboutOptions, memberReasonForJoiningOptions as reasonForJoiningOptions, updateMemberSocialPlatforms } from "../memberInsightOptions";
import CustomContactCards from "../UpdateMember/CustomContactCards";
import { normalizeCustomContact } from "../UpdateMember/memberContactFields";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import TrapFocus from "@mui/material/Unstable_TrapFocus";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { fetchAuthSession } from "aws-amplify/auth";
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import {
  AffinityProgramPartnerActivationStatusEnum,
  ChamberMemberCategory,
  ChamberMemberInput,
  CmpMemberCompanyDetailsInput,
  ChamberMemberProMembershipModelPaymentResponsibilityEnum,
  ChamberMemberProMembershipPricingModelEnum,
  ChamberMemberProPaymentScheduleEnum,
  CmpMemberApplicationPaymentRequestCapability,
  CmpMemberCoreFieldsFragment,
  CmpDuplicateMatchReturn,
  CmpInvoiceBillingSnapshotReturn,
  CmpInvoiceRecordFieldsFragment,
  CmpMemberLifecycleStatusEnum,
  CmpPaymentRequestStateEnum,
  CmpProspectFieldsFragment,
  CmpProspectStatusEnum,
  CmpPaymentChoiceTypeEnum,
  IdaGenerateMode,
  useDownloadCmpInvoicePdfMutation,
  useGetAllAffinityProgramPartnersForChamberQuery,
  useGetAllCategoriesQuery,
  useGetCmpManageMembersQuery,
  useGetCmpMemberDetailQuery,
  useGetCmpProspectDetailQuery,
  useSendCmpInvoicePaymentRequestMutation,
  useSubmitCmpMemberApplicationMutation,
  type CustomContactInput,
} from "../../../../graphql/types";
import { RoutePaths } from "../../../../enumStrings";
import useGetChamberFromUserIdInAuth from "../../../../hooks/useGetChamberFromUserIdInAuth";
import { useGetUserFromUserIdInAuthState } from "../../../../hooks/useGetUserFromUserIdInAuth";
import {
  useCmpGlobalSettings,
  useCmpGovernanceSettings,
  useCmpMembershipSettings,
} from "../../../../hooks/useCmpSettingsSection";
import { getCmpCreditCardPaymentsReady } from "../../../../utils/cmpCreditCardPayments";
import { usePageHeaderOverride } from "../../../Layout/PageContainer";
import { createMemberDraftKey, readCreateMemberDraft, saveCreateMemberDraft } from "./createMemberDraft";
import UnsavedChangesPrompt from "../../../common/UnsavedChangesPrompt";
import { hasUnsavedFormChanges } from "../../../common/formStateDirty";
import { FormPageShell } from "../../../common/dashboard/FormPageShell";
import {
  wizardGoldButtonSx,
  wizardOutlinedButtonSx,
} from "../../../common/dashboard/formStyles";
import {
  invoiceDetailFromRecord,
  manageInvoiceRowFromRecord,
} from "../../Invoicing/invoiceBackendAdapters";
import { downloadBase64File } from "../../Invoicing/invoiceFileDownloads";
import { InvoicePaper } from "../../Invoicing/ManageInvoices/ManageInvoiceActionModals";
import {
  InvoicePreviewZoomBar,
  InvoicePreviewZoom,
  useInvoicePreviewFitScale,
} from "../../Invoicing/ManageInvoices/InvoicePreviewChrome";
import {
  getOutboundEmailFailureMessage,
  getOutboundEmailSuccessMessage,
  wasOutboundEmailDeliveryConfirmed,
} from "../../Invoicing/invoiceDeliveryFeedback";
import {
  DesignDialog,
  DesignDialogButton,
} from "../../../common/dashboard/DesignDialog";
import ActionSuccessDialog from "../../../common/dashboard/ActionSuccessDialog";
import SearchableSelectField from "../../../common/dashboard/SearchableSelectField";
import TagInputField from "../../../common/dashboard/TagInputField";
import IdaGenerateDialog from "../../../common/dashboard/IdaGenerateDialog";
import MemberStaffNotesCard from "../MemberStaffNotesCard";
import GovernanceInvolvementCard from "../GovernanceInvolvementCard";
import { getMemberUrlFieldErrors } from "../memberUrlValidation";
import MemberBillingStep, {
  chamberGroupInsuranceAutomaticActivationDescription,
} from "../MemberBillingStep";
import MemberInvoiceDeliveryFeedback, {
  MemberInvoiceDeliveryFeedbackState,
} from "../MemberInvoiceDeliveryFeedback";
import {
  gateCreateMemberPaymentScheduleMutation,
  getMemberPaymentScheduleDisplayError,
  normalizeMemberPaymentSchedule,
  shouldShowMemberPaymentScheduleSelection,
} from "../memberPaymentSchedule";
import {
  getHasPayableMemberFacingReceivable,
  resolveCreateMemberPaymentMethod,
} from "../memberPaymentMethod";
import { mergeCommittedMemberInvoiceDelivery } from "../memberInvoiceDelivery";
import {
  getMemberApplicationPaymentRequestTargetLabel,
  resolveMemberApplicationPaymentRequestCapabilities,
} from "../memberApplicationPaymentRequestCapabilities";
import {
  runCreatedMemberApplicationMutationFlow,
  runCreatedMemberPaymentRequestDeliveryFlow,
} from "../memberApplicationMutationFlow";
import MemberLoginEmailConflictDialog from "../MemberLoginEmailConflictDialog";
import {
  applySuggestedAdministratorLoginEmail,
  MemberLoginEmailConflict,
  MemberLoginEmailConflictDecision,
  runMemberLoginAvailabilityRetryFlow,
} from "../memberLoginAvailabilityFlow";
import {
  clearCompletedMemberApplicationOperation,
  prepareMemberApplicationSubmissionInput,
  runSingleMemberApplicationSubmission,
} from "../memberApplicationOperation";
import { getMembershipInvoiceTaxRatePercentage } from "../memberMembershipPricing";
import {
  calculateFeeBreakdown,
  getNumber,
  MockMembershipModel,
} from "./createMemberFeeBreakdown";
import {
  getMemberCharityRegistrationNumberInput,
  MEMBER_CHARITY_REGISTRATION_NUMBER_FIELD_ID,
  MEMBER_CHARITY_REGISTRATION_NUMBER_HELPER,
  MEMBER_CHARITY_REGISTRATION_NUMBER_LABEL,
  MEMBER_CHARITY_REGISTRATION_NUMBER_PLACEHOLDER,
  shouldShowMemberCharityRegistrationNumber,
} from "../memberCharityRegistration";
import {
  cpaGold,
  cpaGoldDark,
  choiceControlLabelSx,
  FormCalendarDateField,
  FormCountryField,
  FormPhoneField,
  FormSelectField,
  FormTextField,
  getUpdateMemberNavigationState,
  getLogoInputValue,
  IdaGenerateControl,
  MemberDesignSurface,
  getMemberEstablishedYearOptions,
  useMemberIdaGeneration,
  LogoUploadField,
  MemberStepProgress,
  memberRequestApolloContext,
  memberWorkflowSteps as createMemberSteps,
  MultiOptionGroup,
  radioControlSx,
  SectionShell,
  storeUpdateMemberNavigationMemberId,
} from "../memberWorkflowShared";
import { cmpLongDateFormatter } from "../../../common/dashboard/formatters";
import { provinceOptions as sharedProvinceOptions } from "../../../common/utilities/ProvincePicker";
import { sortMemberCategoryOptions } from "../memberCategories";
import MemberCreatedInvoicePersistenceNotice from "../MemberCreatedInvoicePersistenceNotice";
import { getInvoicePartyFieldErrors } from "../../Invoicing/InvoicePartyFields";
import {
  canManageCmpMembers,
  getUnverifiedCreatedInvoiceContinuationCopy,
} from "../memberApplicationPermissions";
import {
  emptyMemberThirdPartyBillingSnapshot,
  fillEmptyMemberThirdPartyBillingSnapshotFields,
  getAffiliatedPrimaryCompanyBillingSnapshot,
  getMemberBillingResponsibility,
  getMemberThirdPartyBillingFields,
  getMemberThirdPartyBillingSnapshotErrors,
  mapRestartedMemberThirdPartyBillingSnapshot,
  MemberBillingResponsibilityOption,
  getMemberBillingGroupInsuranceNotSetUpMessage,
  memberBillingPrimaryCompanyNotSelectedMessage,
  memberBillingThirdPartyPayerMissingMessage,
} from "../memberThirdPartyBilling";
import { EMAIL_FORMAT_MESSAGE, enterFieldMessage, selectFieldMessage } from "../../../../utils/fieldMessages";
import { formatStaffFaxInput, formatStaffTollFreeInput, getMemberPhoneDigits, getMemberTollFreeFaxErrors } from "../../../../utils/memberTollFreeFax";
import {
  hasUnsavedMemberFormChanges,
  haveSameMemberSelectionValues,
  shouldRebaseUntouchedMemberSystemValue,
} from "../memberFormDirtyState";
import {
  getCreateMemberErrorsAfterEmailConsentChange,
  getCreateMemberErrorsAfterMembershipTypeChange,
  getCreateMemberErrorsAfterRequiredValueChange,
} from "./createMemberValidationRecovery";
import {
  CreateMemberBillingAddress,
  editBillingAddress,
  setBillingSameAsMemberAddress,
  syncBillingWithMemberAddress,
} from "./createMemberBillingAddress";
import { isTierMembershipModel } from "../../MyChamber/ChamberSettings/settingsMappers";
import {
  buildStaffErrorFallback,
  getUserFacingErrorText,
  STAFF_SUPPORT_EMAIL,
  UserFacingError,
} from "../../../../utils/userFacingError";
import RejoiningMemberCard from "./RejoiningMemberCard";

// Duplicate-check reasons arrive as codes (COMPANY_NAME, EMAIL).
const duplicateMatchReasonLabels: Record<string, string> = {
  COMPANY_NAME: "Same member name",
  EMAIL: "Same email address",
};
// Send button on the created-invoice preview. One label for the invoices
// that are still unsent.
const getCreatedInvoiceSendButtonLabel = (
  targets: ReadonlyArray<{ kind: string }>,
): string =>
  targets.length === 1 && targets[0].kind === "add-on"
    ? "Send Add-on Invoice"
    : targets.length === 2
      ? "Send Invoices"
      : "Send Invoice";

const getDuplicateMatchReasonLabel = (reason: string): string =>
  duplicateMatchReasonLabels[reason] ||
  reason
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/^\w/, (letter) => letter.toUpperCase());

// Create Member.dc.html copy. One label for every optional contact; the
// design shows no role note or grey reason under it.
// CMP Dashboard (19) accessLabel (Monday 13252201937).
export const createMemberContactAccessGrantLabel = "Check this box to grant this contact access to your member dashboard. Based on their roles, they will be allowed to view and update various sections such as company profile on the Chamber website and in the Chamber Perks App™ Global Directory, create and manage company perks, view and pay invoices, register for events and update attendee details.";

type PaymentMethodType =
  "creditCard" | "free" | "cash" | "cheque" | "bankTransfer" | "eTransfer";

type BillingResponsibilityOption = MemberBillingResponsibilityOption;

type ContactRole =
  | "primaryVoting"
  | "administrator"
  | "accountsPayable"
  | "employeeAdoption"
  | "marketingCommunication"
  | "hrEmployeeEngagement"
  | "sales";

type MembershipAdditionalCost = MockMembershipModel["additionalCosts"][number];

type ManualPaymentMethodOption = {
  id: PaymentMethodType;
  label: string;
  enabled: boolean;
};

type PrimaryMemberCompanyOption = {
  id: string;
  name: string;
};

type ContactInfo = {
  name: string;
  title: string;
  email: string;
  companyPhone: string;
  mobilePhone: string;
  ext: string;
  sameAsMainCompanyContact?: boolean;
  sameAsCompanyPrimaryContact?: boolean;
};

type ReferralTrackingInfo = {
  id: string;
  referralDate: string;
  becameMemberDate: string;
  companyReferralName: string;
  contactName: string;
  email: string;
  contactNumber: string;
  contactNumberExtension: string;
};

type AffiliatedChamberInfo = {
  isAffiliatedChamberMember: boolean;
  chamberName: string;
};

type AffinityProgramEnrollmentDraft = {
  affinityProgramPartnerId: string;
  idNumber: string;
  startDate: string;
  billingOption: string;
  notes: string;
};

type AffinityProgramEnrollment = AffinityProgramEnrollmentDraft & {
  id: string;
  affinityProgramPartnerName: string;
  status: string;
};

type BillingAddressInfo = CreateMemberBillingAddress;

type MembershipDiscountInfo = {
  enabled: boolean;
  code: string;
  description: string;
  amount: string;
};

type CustomField = {
  id: string;
  fieldName: string;
  value: string;
};

type DuplicateDialogMatch = {
  id: string;
  name: string;
  status: string;
  email?: string | null;
  matchReasons?: string[];
};

type CreateMemberLocationState = {
  cmpPrefillMember?: CmpMemberCoreFieldsFragment;
  cmpPrefillSourceMemberId?: string;
  cmpPrefillThirdPartyBillingSnapshot?: CmpInvoiceBillingSnapshotReturn | null;
};

export type CreateMemberFormState = {
  dashboardAccessRequestedContactIds?: string[];
  initialStaffNote: string;
  association: {
    isAssociateCompanyMember: boolean;
    primaryMemberCompanyId: string;
  };
  membership: {
    membershipTypeId: string;
    fullTimeEmployees: string;
    partTimeEmployees: string;
    paymentSchedule: ChamberMemberProPaymentScheduleEnum | "";
    billingResponsibility: BillingResponsibilityOption;
    paymentResponsibility: ChamberMemberProMembershipModelPaymentResponsibilityEnum | null;
    thirdPartyPayerName: string;
    thirdPartyPaymentNotes: string;
    thirdPartyBillingSnapshot: ReturnType<
      typeof emptyMemberThirdPartyBillingSnapshot
    >;
    selectedAdditionalCostIds: string[];
    membershipDiscount: MembershipDiscountInfo;
    paymentMethod: PaymentMethodType | "";
    autoRenew: boolean;
    emailCommunicationConsentConfirmed: boolean;
  };
  companyDetails: {
    // Optional: a Create Member draft saved before this field still loads.
    charityRegistrationNumber?: string;
    numberedCompany?: string;
    yearEstablished: string;
    memberOwnershipDiversity?: string[];
    howDidYouHearAboutUs: string[];
    howDidYouHearAboutUsOther: string;
    primaryReasonsForJoining: string[];
    primaryReasonForJoiningOther: string;
    activeSocialPlatforms: string[];
    boardOrCommitteeInterest: string;
  };
  insights: {
    chamberPerksAppHeardAbout: string;
    chamberPerksAppDownloaded: string;
    boardDirectorInterest: string;
    committeeInterest: string;
    governanceGroupIds: string[];
    communicationPreferences: string[];
  };
  referralTracking: ReferralTrackingInfo[];
  affiliatedChamberMember: AffiliatedChamberInfo;
  affinityProgramEnrollments: AffinityProgramEnrollment[];
  directory: {
    companyName: string;
    companyDescription: string;
    companyWebsite: string;
    googleReviewUrl: string;
    mainCompanyContactName: string;
    mainCompanyContactEmail: string;
    mainCompanyPhone: string;
    mainCompanyPhoneExt: string;
    tollFreePhone?: string;
    faxNumber?: string;
    directoryCategoryIds: ChamberMemberCategory[];
    seoTags: string[];
    showInDirectory: boolean;
  };
  address: {
    hideStreetAddressInDirectories: boolean;
    streetAddress: string;
    city: string;
    provinceOrState: string;
    postalOrZipCode: string;
    country: string;
  };
  socialLinks: {
    facebookUrl: string;
    instagramUrl: string;
    threadsUrl: string;
    linkedinUrl: string;
    xTwitterUrl: string;
    tikTokUrl: string;
    youtubeUrl: string;
  };
  contacts: Record<ContactRole, ContactInfo>;
  billingAddress: BillingAddressInfo;
  profile: {
    internalNotes: string;
    customFields: CustomField[];
  };
  logo: {
    fileName: string;
    previewUrl: string;
  };
};

const applySuggestedAdministratorEmailToFormState = (
  formState: CreateMemberFormState,
  suggestedLoginEmail: string,
): CreateMemberFormState => ({
  ...formState,
  contacts: {
    ...formState.contacts,
    administrator: {
      ...formState.contacts.administrator,
      email: suggestedLoginEmail,
    },
  },
});

type FormErrors = Record<string, string>;

const shouldMarkCmpModuleTestData = process.env.NODE_ENV === "development";

const createMemberPageHeaders: Record<
  number,
  { title: string; description: string }
> = {
  1: {
    title: "Create Member – Member Information",
    description:
      "Use this section to create a member manually who has not submitted an online application.",
  },
  2: {
    title: "Create Member – Member Contacts",
    description: "Use this section to enter this member’s contacts",
  },
  3: {
    title: "Create Member – Member Insights",
    description:
      "Use this section to enter valuable information about this member to better understand their interests, engagement, communication preferences, and opportunities for involvement.",
  },
  4: {
    title: "Create Member – Membership Type & Billing",
    description:
      "Use this section to enter the members membership type and billing details",
  },
};

// Design create-member success pop-up copy (CMP Dashboard cmNext).
export const getCreateMemberSuccessMessage = ({
  name,
  amount,
  hasInvoice,
}: {
  name: string;
  amount: string;
  hasInvoice: boolean;
}) =>
  hasInvoice
    ? `${name} has been created successfully. The new member invoice${amount ? ` of ${amount}` : ""} is ready to preview.`
    : `${name} has been created successfully.`;

const CreateMemberNavigationArrow = ({ previous = false }: { previous?: boolean }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d={previous ? "M19 12H5m7 7-7-7 7-7" : "M5 12h14m-7-7 7 7-7 7"} />
  </svg>
);

const socialPlatformOptions = [
  "LinkedIn",
  "Twitter/X",
  "Facebook",
  "Instagram",
  "TikTok",
  "YouTube",
  "None",
];

const yesNoOptions = ["Yes", "No"];

const communicationPreferenceOptions = [
  "I prefer communication from the chamber to be done via phone",
  "I prefer communication from the chamber to be done via email. Member is aware they can unsubscribe at any time.",
];

const affinityBillingOptions = [
  "(DO NOT INVOICE MEMBER) Affinity Partner pays chamber monthly for member membership",
  "Affinity Partner invoices member separately",
  "Included with chamber membership",
  "Chamber invoices member",
];

const affinityDoNotInvoicePrefix = "(DO NOT INVOICE MEMBER)";

// The step 3 mock bolds the "(DO NOT INVOICE MEMBER)" parenthetical wherever
// this billing option appears (update modal radio + enrolled table cell).
const renderAffinityBillingOptionLabel = (option: string) =>
  option.startsWith(affinityDoNotInvoicePrefix) ? (
    <>
      <strong>{affinityDoNotInvoicePrefix}</strong>
      {option.slice(affinityDoNotInvoicePrefix.length)}
    </>
  ) : (
    option
  );

const affinityEnrollmentStatusOptions = ["Active", "Inactive"];

const membershipBillingOptions: {
  value: BillingResponsibilityOption;
  title: string;
  description: string;
  descriptionBold?: boolean;
  disabled?: boolean;
}[] = [
  {
    value: "memberPays",
    title: "Member will pay",
    description: "Invoice to be sent to member for payment",
  },
  {
    value: "affiliatedPrimaryCompany",
    title: "Invoice Affiliated Primary Company",
    description:
      "Member has requested that invoice be sent to the primary company for payment",
  },
  {
    value: "otherThirdParty",
    title: "Other Third-Party Payer (Existing)",
    description:
      "Preserves the payer identity on this prefilled application. Select another billing option to replace it.",
    disabled: true,
  },
  // Step-4 spec marks the three internal-use sub-lines below as bold.
  {
    value: "chamberGroupInsurance",
    title: "Paid by Johnson Group (Chamber Insurance Plan)",
    description: chamberGroupInsuranceAutomaticActivationDescription,
    descriptionBold: true,
  },
  {
    value: "complimentary",
    title: "Complimentary No Charge Membership (For promotion/contra)",
    description: "Invoice created for Internal Use Not Sent to Member",
    descriptionBold: true,
  },
  {
    value: "freeLifetime",
    title: "Free Lifetime Membership",
    description: "Invoice created for Internal Use Not Sent to Member",
    descriptionBold: true,
  },
];

const provinceOptions = sharedProvinceOptions.map(({ label }) => label);

const normalizeProvinceOrStateLabel = (provinceOrState: string) => {
  const normalizedValue = provinceOrState.trim().toLowerCase();
  const matchingOption = sharedProvinceOptions.find(
    ({ value, label }) =>
      value.toLowerCase() === normalizedValue ||
      label.toLowerCase() === normalizedValue,
  );

  return matchingOption?.label || provinceOrState.trim();
};

const createEmptyContact = (): ContactInfo => ({
  name: "",
  title: "",
  email: "",
  companyPhone: "",
  mobilePhone: "",
  ext: "",
});

const createLocalId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const createEmptyReferralTracking = (): ReferralTrackingInfo => ({
  id: createLocalId("referral"),
  referralDate: "",
  becameMemberDate: "",
  companyReferralName: "",
  contactName: "",
  email: "",
  contactNumber: "",
  contactNumberExtension: "",
});

const createEmptyAffinityProgramEnrollmentDraft =
  (): AffinityProgramEnrollmentDraft => ({
    affinityProgramPartnerId: "",
    idNumber: "",
    startDate: "",
    billingOption: "",
    notes: "",
  });

const createInitialFormState = (): CreateMemberFormState => ({
  dashboardAccessRequestedContactIds: [],
  initialStaffNote: "",
  association: {
    isAssociateCompanyMember: false,
    primaryMemberCompanyId: "",
  },
  membership: {
    membershipTypeId: "",
    fullTimeEmployees: "",
    partTimeEmployees: "",
    paymentSchedule: "",
    billingResponsibility: "memberPays",
    paymentResponsibility: null,
    thirdPartyPayerName: "",
    thirdPartyPaymentNotes: "",
    thirdPartyBillingSnapshot: emptyMemberThirdPartyBillingSnapshot(),
    selectedAdditionalCostIds: [],
    membershipDiscount: {
      enabled: false,
      code: "",
      description: "",
      amount: "",
    },
    paymentMethod: "",
    autoRenew: false,
    emailCommunicationConsentConfirmed: true,
  },
  companyDetails: {
    charityRegistrationNumber: "",
    numberedCompany: "",
    yearEstablished: "",
    memberOwnershipDiversity: [],
    howDidYouHearAboutUs: [],
    howDidYouHearAboutUsOther: "",
    primaryReasonsForJoining: [],
    primaryReasonForJoiningOther: "",
    activeSocialPlatforms: [],
    boardOrCommitteeInterest: "",
  },
  insights: {
    chamberPerksAppHeardAbout: "",
    chamberPerksAppDownloaded: "",
    boardDirectorInterest: "",
    committeeInterest: "",
    governanceGroupIds: [],
    communicationPreferences: [],
  },
  referralTracking: [createEmptyReferralTracking()],
  affiliatedChamberMember: {
    isAffiliatedChamberMember: false,
    chamberName: "",
  },
  affinityProgramEnrollments: [],
  directory: {
    companyName: "",
    companyDescription: "",
    companyWebsite: "",
    googleReviewUrl: "",
    mainCompanyContactName: "",
    mainCompanyContactEmail: "",
    mainCompanyPhone: "",
    mainCompanyPhoneExt: "",
    directoryCategoryIds: [],
    seoTags: [],
    showInDirectory: true,
  },
  address: {
    hideStreetAddressInDirectories: false,
    streetAddress: "",
    city: "",
    provinceOrState: "",
    postalOrZipCode: "",
    country: "",
  },
  socialLinks: {
    facebookUrl: "",
    instagramUrl: "",
    threadsUrl: "",
    linkedinUrl: "",
    xTwitterUrl: "",
    tikTokUrl: "",
    youtubeUrl: "",
  },
  contacts: {
    primaryVoting: createEmptyContact(),
    administrator: createEmptyContact(),
    accountsPayable: createEmptyContact(),
    employeeAdoption: createEmptyContact(),
    marketingCommunication: createEmptyContact(),
    hrEmployeeEngagement: createEmptyContact(),
    sales: createEmptyContact(),
  },
  billingAddress: {
    streetAddress: "",
    city: "",
    provinceOrState: "",
    postalOrZipCode: "",
    country: "",
    sameAsMemberAddress: false,
  },
  profile: {
    internalNotes: "",
    customFields: [],
  },
  logo: {
    fileName: "",
    previewUrl: "",
  },
});

const mapProspectToCreateMemberFormState = (
  prospect: CmpProspectFieldsFragment,
): CreateMemberFormState => {
  const initialState = createInitialFormState();
  const provinceOrState = normalizeProvinceOrStateLabel(
    prospect.address?.province || "",
  );
  const sourceContact = mapPrefillContact({
    name: prospect.mainContactName,
    title: prospect.mainContactTitle,
    mobilePhone: prospect.mobilePhoneNumber,
    email: prospect.mainContactEmail,
    phone: prospect.mainPhoneNumber,
    ext: prospect.mainPhoneNumberExtension,
  });
  const sourceContactCanPopulateRequiredRoles = !!(
    sourceContact.name.trim() && hasEmailShape(sourceContact.email.trim())
  );
  const matchingHearAboutOption = hearAboutOptions.find(
    (option) =>
      option.toLowerCase() === prospect.leadCaptureLabel.trim().toLowerCase(),
  );

  return {
    ...initialState,
    companyDetails: {
      ...initialState.companyDetails,
      howDidYouHearAboutUs: [matchingHearAboutOption || "Other"],
      howDidYouHearAboutUsOther: matchingHearAboutOption
        ? ""
        : prospect.leadCaptureLabel,
    },
    directory: {
      ...initialState.directory,
      companyName: prospect.companyName,
      companyWebsite: prospect.companyWebsite || "",
      mainCompanyContactName: prospect.mainContactName || "",
      mainCompanyContactEmail: prospect.mainContactEmail || "",
      mainCompanyPhone: prospect.mainPhoneNumber || "",
      mainCompanyPhoneExt: prospect.mainPhoneNumberExtension || "",
    },
    address: {
      ...initialState.address,
      streetAddress: prospect.address?.streetAddress || "",
      city: prospect.address?.city || "",
      provinceOrState,
      postalOrZipCode: prospect.address?.postalCode || "",
      country: prospect.address?.country || "",
    },
    contacts: {
      ...initialState.contacts,
      primaryVoting: {
        ...sourceContact,
        sameAsMainCompanyContact: sourceContactCanPopulateRequiredRoles,
      },
      administrator: {
        ...sourceContact,
        sameAsCompanyPrimaryContact: sourceContactCanPopulateRequiredRoles,
      },
      accountsPayable: {
        ...sourceContact,
        sameAsCompanyPrimaryContact: sourceContactCanPopulateRequiredRoles,
      },
    },
    billingAddress: {
      streetAddress: prospect.address?.streetAddress || "",
      city: prospect.address?.city || "",
      provinceOrState,
      postalOrZipCode: prospect.address?.postalCode || "",
      country: prospect.address?.country || "",
    },
  };
};

const membershipPaymentScheduleOptions = [
  {
    value: ChamberMemberProPaymentScheduleEnum.Annual,
    label: "Yearly",
  },
  {
    value: ChamberMemberProPaymentScheduleEnum.Monthly,
    label: "Monthly",
  },
];

const currencyFormatter = new Intl.NumberFormat("en-CA", {
  style: "currency",
  currency: "CAD",
});

const formatCurrency = (amount: number) => currencyFormatter.format(amount);

const getConfiguredMembershipAddOns = (
  membershipModels: MockMembershipModel[],
): MembershipAdditionalCost[] => {
  const configuredAddOnsById = new Map<string, MembershipAdditionalCost>();

  membershipModels.forEach((membershipModel) => {
    membershipModel.additionalCosts.forEach((additionalCost) => {
      if (!configuredAddOnsById.has(additionalCost.id)) {
        configuredAddOnsById.set(additionalCost.id, additionalCost);
      }
    });
  });

  return Array.from(configuredAddOnsById.values()).filter(
    (additionalCost) => additionalCost.enabled,
  );
};

const getError = (errors: FormErrors, key: string) => errors[key] || "";

const toOptionalString = (value: string) => {
  const trimmedValue = value.trim();
  return trimmedValue ? trimmedValue : undefined;
};

// Year established is intentionally year-only. Older dashboard builds stored
// the value as yyyy-01-01, so extract the year while hydrating those records.
const toEstablishedYearValue = (value?: string | null) => {
  const trimmedValue = (value || "").trim();
  const yearMatch = trimmedValue.match(/\d{4}/);
  return yearMatch ? yearMatch[0] : "";
};

const isValidEstablishedYear = (value: string) => {
  if (!value) {
    return true;
  }

  const year = Number(value);
  return (
    /^\d{4}$/.test(value) && year >= 1000 && year <= new Date().getFullYear()
  );
};

// Display formatter for native date-input values ("yyyy-mm-dd"). Parse the
// parts locally instead of new Date(string) so the rendered day never shifts
// a day backwards in negative-UTC-offset timezones.
const formatDateInputValueLong = (value?: string) => {
  if (!value) {
    return "-";
  }

  const [year, month, day] = value.split("-").map(Number);

  if (!year || !month || !day) {
    return value;
  }

  return cmpLongDateFormatter.format(new Date(year, month - 1, day));
};

const mapPrefillContact = ({
  email,
  ext,
  name,
  phone,
  mobilePhone,
  sameAsCompanyPrimaryContact,
  sameAsMainCompanyContact,
  title,
}: {
  name?: string | null;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  mobilePhone?: string | null;
  ext?: string | null;
  sameAsCompanyPrimaryContact?: boolean | null;
  sameAsMainCompanyContact?: boolean | null;
}): ContactInfo => ({
  name: name || "",
  title: title || "",
  email: email || "",
  companyPhone: phone || "",
  mobilePhone: mobilePhone || "",
  ext: ext || "",
  sameAsCompanyPrimaryContact: !!sameAsCompanyPrimaryContact,
  sameAsMainCompanyContact: !!sameAsMainCompanyContact,
});

export const buildCreateMemberCompanyDetailsInput = (
  companyDetails: CreateMemberFormState["companyDetails"],
  { showCharityRegistrationNumber, legacyBoardOrCommitteeInterest }: {
    showCharityRegistrationNumber: boolean;
    legacyBoardOrCommitteeInterest: string;
  },
): CmpMemberCompanyDetailsInput => ({
  ...getMemberCharityRegistrationNumberInput({
    shown: showCharityRegistrationNumber,
    value: companyDetails.charityRegistrationNumber,
  }),
  numberedCompany: (companyDetails.numberedCompany || "").trim(),
  yearEstablished: toOptionalString(companyDetails.yearEstablished),
  // Monday 13362035864: the ticked Member Ownership Diversity boxes, or [].
  memberOwnershipDiversity: getMemberOwnershipDiversityInput(
    companyDetails.memberOwnershipDiversity,
  ),
  howDidYouHearAboutUs: companyDetails.howDidYouHearAboutUs,
  howDidYouHearAboutUsOther: toOptionalString(
    companyDetails.howDidYouHearAboutUsOther,
  ),
  primaryReasonsForJoining: companyDetails.primaryReasonsForJoining,
  primaryReasonForJoiningOther: toOptionalString(
    companyDetails.primaryReasonForJoiningOther,
  ),
  activeSocialPlatforms: companyDetails.activeSocialPlatforms,
  boardOrCommitteeInterest: toOptionalString(legacyBoardOrCommitteeInterest),
});

export const mapPrefilledMemberToFormState = (
  member: CmpMemberCoreFieldsFragment,
  thirdPartyBillingSnapshot?: CmpInvoiceBillingSnapshotReturn | null,
): CreateMemberFormState => {
  const extension = member.cmpExtension;
  const membership = extension?.membership;
  const applicationMetadata = extension?.applicationMetadata;
  const paymentChoice = membership?.paymentChoice;
  const membershipDiscount = membership?.membershipDiscount;
  const manualPaymentMethod = paymentChoice?.manualPaymentMethodId
    ? mapManualPaymentMethodIdToPaymentMethod(
        paymentChoice.manualPaymentMethodId,
      )
    : "";
  const billingResponsibility = getMemberBillingResponsibility({
    insuranceOptionAvailable: false,
    membership,
  });

  return {
    dashboardAccessRequestedContactIds: [],
    initialStaffNote: "",
    association: {
      isAssociateCompanyMember:
        !!extension?.association.isAssociateCompanyMember,
      primaryMemberCompanyId:
        extension?.association.primaryMemberCompanyId || "",
    },
    membership: {
      membershipTypeId: membership?.membershipTypeId || "",
      fullTimeEmployees: String(membership?.fullTimeEmployees ?? ""),
      partTimeEmployees: String(membership?.partTimeEmployees ?? ""),
      paymentSchedule: membership?.paymentSchedule || "",
      billingResponsibility,
      paymentResponsibility: membership?.paymentResponsibility || null,
      thirdPartyPayerName: membership?.thirdPartyPayerName || "",
      thirdPartyPaymentNotes: membership?.thirdPartyPaymentNotes || "",
      thirdPartyBillingSnapshot: mapRestartedMemberThirdPartyBillingSnapshot({
        paymentResponsibility: membership?.paymentResponsibility,
        thirdPartyBillingSnapshot,
      }),
      selectedAdditionalCostIds: membership?.selectedAdditionalCostIds || [],
      membershipDiscount: {
        enabled: !!membershipDiscount?.enabled,
        code: membershipDiscount?.code || "",
        description: membershipDiscount?.description || "",
        amount: membershipDiscount?.amountInCents
          ? String(membershipDiscount.amountInCents / 100)
          : "",
      },
      paymentMethod:
        paymentChoice?.type === CmpPaymentChoiceTypeEnum.CreditCard
          ? "creditCard"
          : paymentChoice?.type === CmpPaymentChoiceTypeEnum.Free
            ? "free"
            : paymentChoice?.type === CmpPaymentChoiceTypeEnum.ThirdParty
              ? ""
              : manualPaymentMethod,
      autoRenew: !!paymentChoice?.autoRenew,
      emailCommunicationConsentConfirmed:
        !!paymentChoice?.emailCommunicationConsentConfirmed,
    },
    companyDetails: {
      charityRegistrationNumber:
        extension?.companyDetails.charityRegistrationNumber || "",
      numberedCompany: extension?.companyDetails.numberedCompany || "",
      yearEstablished: toEstablishedYearValue(
        extension?.companyDetails.yearEstablished,
      ),
      memberOwnershipDiversity: getMemberOwnershipDiversityInput(
        extension?.companyDetails.memberOwnershipDiversity,
      ),
      howDidYouHearAboutUs:
        extension?.companyDetails.howDidYouHearAboutUs || [],
      howDidYouHearAboutUsOther:
        extension?.companyDetails.howDidYouHearAboutUsOther || "",
      primaryReasonsForJoining:
        extension?.companyDetails.primaryReasonsForJoining || [],
      primaryReasonForJoiningOther:
        extension?.companyDetails.primaryReasonForJoiningOther || "",
      activeSocialPlatforms:
        extension?.companyDetails.activeSocialPlatforms || [],
      boardOrCommitteeInterest:
        extension?.companyDetails.boardOrCommitteeInterest || "",
    },
    insights: {
      chamberPerksAppHeardAbout: extension?.chamberPerksAppHeardAbout || "",
      chamberPerksAppDownloaded: extension?.chamberPerksAppDownloaded || "",
      boardDirectorInterest:
        extension?.boardDirectorInterest ||
        extension?.companyDetails.boardOrCommitteeInterest ||
        "",
      committeeInterest: extension?.committeeInterest || "",
      governanceGroupIds: extension?.governanceGroupIds || [],
      communicationPreferences:
        extension?.applicationMetadata?.communicationPreferences || [],
    },
    referralTracking:
      extension?.referralTracking && extension.referralTracking.length > 0
        ? extension.referralTracking.map((referral) => ({
            id: referral.id,
            referralDate: referral.referralDate || "",
            becameMemberDate: referral.becameMemberDate || "",
            companyReferralName: referral.companyReferralName || "",
            contactName: referral.contactName || "",
            email: referral.email || "",
            contactNumber: referral.contactNumber || "",
            contactNumberExtension: referral.contactNumberExtension || "",
          }))
        : [createEmptyReferralTracking()],
    affiliatedChamberMember: {
      isAffiliatedChamberMember:
        !!extension?.affiliatedChamberMember?.isAffiliatedChamberMember,
      chamberName: extension?.affiliatedChamberMember?.chamberName || "",
    },
    affinityProgramEnrollments:
      extension?.affinityProgramEnrollments?.map((enrollment) => ({
        id: enrollment.id,
        affinityProgramPartnerId: enrollment.affinityProgramPartnerId || "",
        affinityProgramPartnerName: enrollment.affinityProgramPartnerName || "",
        idNumber: enrollment.idNumber || "",
        startDate: enrollment.startDate || "",
        billingOption: enrollment.billingOption || "",
        status: enrollment.status || "Active",
        notes: enrollment.notes || "",
      })) || [],
    directory: {
      companyName: member.name,
      companyDescription: member.description || "",
      companyWebsite: member.website || "",
      googleReviewUrl: member.googleReviewLink || "",
      mainCompanyContactName: member.contactName || "",
      mainCompanyContactEmail: member.email || "",
      mainCompanyPhone: member.phoneNumber || "",
      mainCompanyPhoneExt: member.phoneNumberExtension || "",
      tollFreePhone: formatStaffTollFreeInput(member.tollFreePhoneNumber),
      faxNumber: formatStaffFaxInput(member.faxNumber),
      directoryCategoryIds: member.categoryId,
      seoTags: member.seoTags || [],
      // IF11 MB9: the prefill is an application draft started from an old
      // record. The draft is saved hidden (showInApp false) and a deleted
      // source is hidden too, so the stored value says nothing about the
      // member's choice. A restarted membership starts shown; activation
      // shows an Active member anyway.
      showInDirectory: true,
    },
    address: {
      hideStreetAddressInDirectories: !!member.hideAddressFromDirectory,
      streetAddress: member.address.streetAddress,
      city: member.address.city,
      provinceOrState: member.address.province,
      postalOrZipCode: member.address.postalCode,
      country: extension?.applicationMetadata.country || "Canada",
    },
    socialLinks: {
      facebookUrl: member.facebook || "",
      instagramUrl: member.instagram || "",
      threadsUrl: member.threads || "",
      linkedinUrl: member.linkedin || "",
      xTwitterUrl: member.twitter || "",
      tikTokUrl: member.tiktok || "",
      youtubeUrl: member.youtube || "",
    },
    contacts: {
      primaryVoting: mapPrefillContact({
        name: member.primaryContactName,
        title: member.primaryContactTitle,
        email: member.primaryContactEmail,
        phone: member.primaryContactPhoneNumber,
        mobilePhone: member.primaryContactMobilePhoneNumber,
        ext: member.primaryContactPhoneNumberExtension,
        sameAsMainCompanyContact:
          applicationMetadata?.primaryVotingSameAsMainCompanyContact,
      }),
      administrator: mapPrefillContact({
        name: member.adminConsoleContactName,
        title: member.cpaAdminCompanyAdminContactTitle,
        email: member.adminConsoleLoginEmail,
        phone: member.cpaAdminCompanyAdminContactNumber,
        mobilePhone: member.cpaAdminCompanyAdminContactMobilePhoneNumber,
        ext: member.cpaAdminCompanyAdminContactNumberExtension,
        sameAsCompanyPrimaryContact:
          applicationMetadata?.administratorSameAsCompanyPrimaryContact ??
          applicationMetadata?.marketingSameAsCompanyPrimaryContact,
      }),
      accountsPayable: mapPrefillContact({
        name: extension?.accountsPayableContactName,
        title: extension?.accountsPayableContactTitle,
        email: extension?.accountsPayableContactEmail,
        phone: extension?.accountsPayableContactNumber,
        mobilePhone: extension?.accountsPayableContactMobilePhoneNumber,
        ext: extension?.accountsPayableContactNumberExtension,
        sameAsCompanyPrimaryContact:
          applicationMetadata?.accountsPayableSameAsCompanyPrimaryContact,
      }),
      employeeAdoption: mapPrefillContact({
        name: member.cpaEmployeeAdoptionContactName,
        title: member.cpaEmployeeAdoptionContactTitle,
        email: member.cpaEmployeeAdoptionContactEmail,
        phone: member.cpaEmployeeAdoptionContactNumber,
        mobilePhone: member.cpaEmployeeAdoptionContactMobilePhoneNumber,
        ext: member.cpaEmployeeAdoptionContactNumberExtension,
        sameAsCompanyPrimaryContact:
          applicationMetadata?.employeeAdoptionSameAsCompanyPrimaryContact ??
          applicationMetadata?.hrSameAsCompanyPrimaryContact,
      }),
      marketingCommunication: mapPrefillContact({
        name: extension?.marketingCommunicationContactName,
        title: extension?.marketingCommunicationContactTitle,
        email: extension?.marketingCommunicationContactEmail,
        phone: extension?.marketingCommunicationContactNumber,
        mobilePhone: extension?.marketingCommunicationContactMobilePhoneNumber,
        ext: extension?.marketingCommunicationContactNumberExtension,
        sameAsCompanyPrimaryContact:
          applicationMetadata?.marketingCommunicationSameAsCompanyPrimaryContact,
      }),
      hrEmployeeEngagement: mapPrefillContact({
        name: extension?.hrEmployeeEngagementContactName,
        title: extension?.hrEmployeeEngagementContactTitle,
        email: extension?.hrEmployeeEngagementContactEmail,
        phone: extension?.hrEmployeeEngagementContactNumber,
        mobilePhone: extension?.hrEmployeeEngagementContactMobilePhoneNumber,
        ext: extension?.hrEmployeeEngagementContactNumberExtension,
        sameAsCompanyPrimaryContact:
          applicationMetadata?.hrEmployeeEngagementSameAsCompanyPrimaryContact,
      }),
      sales: mapPrefillContact({
        name: extension?.salesBusinessDevelopmentContactName,
        title: extension?.salesBusinessDevelopmentContactTitle,
        email: extension?.salesBusinessDevelopmentContactEmail,
        phone: extension?.salesBusinessDevelopmentContactNumber,
        mobilePhone:
          extension?.salesBusinessDevelopmentContactMobilePhoneNumber,
        ext: extension?.salesBusinessDevelopmentContactNumberExtension,
        sameAsCompanyPrimaryContact:
          applicationMetadata?.salesSameAsCompanyPrimaryContact,
      }),
    },
    billingAddress: {
      streetAddress:
        extension?.accountsPayableBillingAddress?.streetAddress || "",
      city: extension?.accountsPayableBillingAddress?.city || "",
      provinceOrState: extension?.accountsPayableBillingAddress?.province || "",
      postalOrZipCode:
        extension?.accountsPayableBillingAddress?.postalCode || "",
      country: extension?.accountsPayableBillingAddress?.country || "Canada",
    },
    profile: {
      internalNotes: extension?.profile.internalNotes || "",
      customFields:
        extension?.profile.customFields.map((field) => ({
          id: field.id,
          fieldName: field.fieldName,
          value: field.value,
        })) || [],
    },
    logo: {
      fileName: member.logo || "",
      previewUrl: "",
    },
  };
};

const formatEnumLabel = (value?: string | null) => {
  if (!value) {
    return "Unknown";
  }

  return value
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const mapManualPaymentMethodIdToPaymentMethod = (
  methodId?: string | null,
): PaymentMethodType => {
  switch (methodId) {
    case "INVOICE_ME":
    case "invoice":
      return "cheque";
    case "CASH":
    case "cash":
      return "cash";
    case "CHEQUE":
    case "cheque":
      return "cheque";
    case "DIRECT_DEPOSIT":
    case "direct-deposit":
      return "bankTransfer";
    case "E_TRANSFER":
    case "e-transfer":
      return "eTransfer";
    default:
      return "bankTransfer";
  }
};

const mapPaymentMethodToManualMethodId = (
  paymentMethod: PaymentMethodType | "",
) => {
  switch (paymentMethod) {
    case "cash":
      return "CASH";
    case "cheque":
      return "CHEQUE";
    case "bankTransfer":
      return "DIRECT_DEPOSIT";
    case "eTransfer":
      return "E_TRANSFER";
    default:
      return undefined;
  }
};

const getManualPaymentMethodLabel = ({
  fallbackLabel,
  paymentMethod,
}: {
  fallbackLabel?: string | null;
  paymentMethod: PaymentMethodType;
}) => {
  switch (paymentMethod) {
    case "cheque":
      return "Company Cheque";
    case "bankTransfer":
      return fallbackLabel || "Bank Transfer";
    case "eTransfer":
      return fallbackLabel || "E-Transfer";
    case "cash":
      return fallbackLabel || "Cash";
    default:
      return fallbackLabel || "";
  }
};

const buildManualPaymentOptions = (
  methods: { id: string; label: string; enabled: boolean }[],
) => {
  const seen = new Set<PaymentMethodType>();

  return methods.reduce<ManualPaymentMethodOption[]>((options, method) => {
    if (!method.enabled) {
      return options;
    }

    const paymentMethod = mapManualPaymentMethodIdToPaymentMethod(method.id);

    if (seen.has(paymentMethod)) {
      return options;
    }

    seen.add(paymentMethod);
    options.push({
      id: paymentMethod,
      label: getManualPaymentMethodLabel({
        fallbackLabel: method.label,
        paymentMethod,
      }),
      enabled: true,
    });
    return options;
  }, []);
};

const mapDuplicateMatches = (
  matches: Pick<
    CmpDuplicateMatchReturn,
    "id" | "companyName" | "lifecycleStatus" | "email" | "matchReasons"
  >[],
): DuplicateDialogMatch[] =>
  matches.map((match) => ({
    id: match.id,
    name: match.companyName,
    status: formatEnumLabel(match.lifecycleStatus),
    email: match.email,
    matchReasons: match.matchReasons,
  }));

type RadioChoiceGroupProps = {
  label: string;
  options: string[];
  value: string;
  onChange: (value: string) => void;
  columns?: "compact" | "wide" | "single";
  renderOptionLabel?: (option: string) => React.ReactNode;
};

const RadioChoiceGroup = ({
  label,
  options,
  value,
  onChange,
  columns = "compact",
  renderOptionLabel,
}: RadioChoiceGroupProps) => (
  <div className="flex flex-col gap-2.5">
    <p className="mb-2 text-sm font-bold text-gray-950">{label}</p>
    <RadioGroup
      row={columns !== "single"}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={
        columns === "wide"
          ? "grid grid-cols-1 gap-x-8 sm:grid-cols-3"
          : columns === "single"
            ? "grid grid-cols-1 gap-y-2"
            : "flex flex-wrap gap-x-[60px] gap-y-3"
      }
    >
      {options.map((option) => (
        <FormControlLabel
          key={option}
          value={option}
          sx={choiceControlLabelSx}
          control={<Radio sx={radioControlSx} />}
          label={
            <span className="text-sm text-gray-950">
              {renderOptionLabel ? renderOptionLabel(option) : option}
            </span>
          }
        />
      ))}
    </RadioGroup>
  </div>
);

type AffinityEnrollmentStatusIndicatorProps = {
  status: string;
};

// Shared "status dot" rendering for affinity enrollments: used by the
// Enrolled Affinity Programs table and the edit dialog's Status select
// (MEM-CREATE-015) so both surfaces stay in sync.
const AffinityEnrollmentStatusIndicator = ({
  status,
}: AffinityEnrollmentStatusIndicatorProps) => {
  const isInactive = status === "Inactive";

  return (
    <span
      className={`inline-flex items-center gap-2 font-semibold ${
        isInactive ? "text-red-600" : "text-green-700"
      }`}
    >
      <span
        className={`h-2 w-2 rounded-full ${
          isInactive ? "bg-red-500" : "bg-green-500"
        }`}
      />
      {status || "Active"}
    </span>
  );
};

type InterestOptionMatrixRow = {
  id: string;
  label: string;
  affirmativeValue: string;
  value: string;
  onChange: (value: string) => void;
};

type InterestOptionMatrixProps = {
  label: string;
  rows: InterestOptionMatrixRow[];
};

const InterestOptionMatrix = ({ label, rows }: InterestOptionMatrixProps) => (
  <div className="flex flex-col gap-2.5">
    <p className="mb-2 text-sm font-bold text-gray-950">{label}</p>
    <div className="space-y-2">
      {rows.map((row) => (
        <RadioGroup
          key={row.id}
          row
          value={row.value}
          onChange={(event) => row.onChange(event.target.value)}
          className="grid max-w-[760px] grid-cols-1 gap-[14px] sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)]"
        >
          <FormControlLabel
            value={row.affirmativeValue}
            sx={choiceControlLabelSx}
            control={<Radio sx={radioControlSx} />}
            label={<span className="text-sm text-gray-950">{row.label}</span>}
          />
          {["No", "Maybe"].map((option) => (
            <FormControlLabel
              key={option}
              value={option}
              sx={choiceControlLabelSx}
              control={<Radio sx={radioControlSx} />}
              label={<span className="text-sm text-gray-950">{option}</span>}
            />
          ))}
        </RadioGroup>
      ))}
    </div>
  </div>
);

type ContactCardRequiredField = "name" | "email";

type ContactSectionConfig = {
  role: ContactRole;
  title: string;
  description: string;
  sameAsLabel: string;
  nameLabel: string;
  phoneLabel: string;
  requiredFields: ContactCardRequiredField[];
  emailPlaceholder?: string;
  namePlaceholder?: string;
  phonePlaceholder?: string;
  includesBillingAddress?: boolean;
};

// CMP Dashboard (19) L11400-11409. The design has no final period.
export const createMemberContactSameAsPrimaryLabel = "If you do not have the contact name and email below, check to add the primary contact";

const internalContactNotice =
  "This contact information will NOT appear on our website and the Chamber Perks App™ directories, unless it is entered as the main company contact.";

const createMemberContactSections: ContactSectionConfig[] = [
  {
    role: "primaryVoting",
    title: "Member Primary Contact (Voting Member)",
    description: "Enter the Primary Contact/Voting Member details below.\nOnce the member is created and their status is active, the Primary Contact will automatically receive an email with credentials for their company Member Dashboard. If the Primary Contact has instructed you to grant access to any other contacts, select the checkbox beside those contacts, and their login credentials will be emailed automatically.\nAfter the member is created, you can view each contact’s Member Dashboard access details under Manage Members.",
    sameAsLabel: "Check here if same as Main Contact",
    nameLabel: "Primary Contact Name",
    phoneLabel: "Contact Number",
    emailPlaceholder: "Enter Primary Contact Email",
    phonePlaceholder: "Enter Contact Number",
    requiredFields: ["name", "email"],
  },
  {
    role: "accountsPayable",
    title: "Accounts Payable Contact and Billing/Mailing Address",
    description:
      "Enter the contact responsible for managing payments for this member, and the billing/mailing address",
    sameAsLabel: createMemberContactSameAsPrimaryLabel,
    nameLabel: "Billing Contact Name",
    phoneLabel: "Contact Number",
    namePlaceholder: "Enter Contact Name",
    emailPlaceholder: "Enter Billing Contact Email",
    requiredFields: ["name", "email"],
    includesBillingAddress: true,
  },
  {
    role: "administrator",
    title: "Member Administrator Contact",
    description: "Enter the Administrator Contact responsible for an additional point of contact for this member.",
    sameAsLabel: createMemberContactSameAsPrimaryLabel,
    nameLabel: "Administrator Contact Name",
    phoneLabel: "Contact Number",
    namePlaceholder: "Enter Primary Contact Name",
    emailPlaceholder: "Enter Primary Contact Email",
    // Optional (Monday 13304072079); name and email are needed only to grant access.
    requiredFields: [],
  },
  {
    role: "marketingCommunication",
    title: "Marketing and Communication Contact",
    description:
      "Enter the contact responsible for marketing and communications. This person will receive promotions, sponsorship opportunities, and other Chamber communications to help the member maximize their membership value.",
    sameAsLabel: createMemberContactSameAsPrimaryLabel,
    nameLabel: "Contact Name",
    phoneLabel: "Company Contact Number",
    requiredFields: [],
  },
  {
    role: "hrEmployeeEngagement",
    title: "HR/Employee Engagement Contact",
    description:
      "Enter the contact responsible for sharing membership benefits and opportunities with employees. This person will help promote cost-saving programs, events, resources, and the Chamber Perks App™ for access to local and national discounts.",
    sameAsLabel: createMemberContactSameAsPrimaryLabel,
    nameLabel: "Contact Name",
    phoneLabel: "Contact Number",
    requiredFields: [],
  },
  {
    role: "sales",
    title: "Sales/Business Development Contact",
    description:
      "Enter the contact responsible for business development, growth, and sales opportunities. This person will receive information about networking, partnerships, events, and the initiatives designed to increase visibility, build relationships, and support the member’s growth.",
    sameAsLabel: createMemberContactSameAsPrimaryLabel,
    nameLabel: "Contact Name",
    phoneLabel: "Contact Number",
    requiredFields: [],
  },
];

type ContactCardProps = {
  title: string;
  description: string;
  notice?: string;
  contact: ContactInfo;
  errors: FormErrors;
  errorPrefix: ContactRole;
  sameAsLabel: string;
  onSameAsChange: () => void;
  onChange: (field: keyof ContactInfo, value: string) => void;
  nameLabel: string;
  phoneLabel?: string;
  emailPlaceholder?: string;
  namePlaceholder?: string;
  phonePlaceholder?: string;
  requiredFields?: ContactCardRequiredField[];
  accessControl?: React.ReactNode;
  /** Design red border after Next Step with access ticked and no name or valid email. */
  accessNameMissing?: boolean;
  accessEmailMissing?: boolean;
  children?: React.ReactNode;
};

const ContactCard = ({
  title,
  description,
  notice,
  contact,
  errors,
  errorPrefix,
  sameAsLabel,
  onSameAsChange,
  onChange,
  nameLabel,
  phoneLabel = "Contact Number",
  emailPlaceholder = "Enter Contact Email",
  namePlaceholder,
  phonePlaceholder,
  requiredFields = [],
  accessControl,
  accessNameMissing = false,
  accessEmailMissing = false,
  children,
}: ContactCardProps) => {
  const sameAsChecked =
    contact.sameAsMainCompanyContact ||
    contact.sameAsCompanyPrimaryContact ||
    false;
  const isRequired = (field: ContactCardRequiredField) =>
    requiredFields.includes(field);

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-[#E0E0E0] bg-white px-[20px] py-[26px] md:px-[28px]">
      <div>
        <h3 className="text-[17px] font-bold leading-6 text-[#0A0A0A]">
          {title}
        </h3>
        <div className="mt-1.5 space-y-2 text-sm leading-[1.55] text-[#0A0A0A]">
          {description.split("\n").map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
        </div>
      </div>
      {notice && (
        <p className="text-sm font-bold leading-[1.55] text-black">{notice}</p>
      )}
      <FormControlLabel
        sx={{ margin: 0 }}
        control={
          <Checkbox
            checked={sameAsChecked}
            onChange={onSameAsChange}
          />
        }
        label={
          <span className="text-sm leading-6 text-black">{sameAsLabel}</span>
        }
      />
      {accessControl}
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <FormTextField
          id={`${errorPrefix}-name`}
          label={nameLabel}
          value={contact.name}
          onChange={(value) => onChange("name", value)}
          placeholder={namePlaceholder || `Enter ${nameLabel}`}
          error={getError(errors, `${errorPrefix}.name`)}
          required={isRequired("name")}
          containerClassName={accessNameMissing ? "mb-5 min-w-0 member-field-access-missing" : undefined}
        />
        <FormTextField
          id={`${errorPrefix}-title`}
          label="Title or Designation"
          value={contact.title}
          onChange={(value) => onChange("title", value)}
          placeholder="Enter Title or Designation"
        />
      </div>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_76px_minmax(0,1fr)]">
        <FormTextField
          id={`${errorPrefix}-email`}
          label="Contact Email"
          value={contact.email}
          onChange={(value) => onChange("email", value)}
          placeholder={emailPlaceholder}
          error={getError(errors, `${errorPrefix}.email`)}
          required={isRequired("email")}
          containerClassName={accessEmailMissing ? "mb-5 min-w-0 member-field-access-missing" : undefined}
        />
        <FormPhoneField
          id={`${errorPrefix}-company-phone`}
          label={phoneLabel}
          value={contact.companyPhone}
          onChange={(value) => onChange("companyPhone", value)}
          placeholder={phonePlaceholder || "Enter Contact Number"}
        />
        <FormTextField
          id={`${errorPrefix}-ext`}
          label="Ext."
          value={contact.ext}
          onChange={(value) => onChange("ext", value)}
          placeholder="0000"
        />
        <FormPhoneField
          id={`${errorPrefix}-mobile-phone`}
          label="Mobile Phone Number"
          value={contact.mobilePhone}
          onChange={(value) => onChange("mobilePhone", value)}
          placeholder="Enter Mobile Phone Number"
        />
      </div>
      {children}
    </div>
  );
};

const hasEmailShape = (value: string) => value.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const canRequestContactDashboardAccess = (contact: ContactInfo) =>
  !!contact.name.trim() && hasEmailShape(contact.email.trim()) && !contact.sameAsCompanyPrimaryContact;

// CMP Dashboard (19) sameAsPrimary: the box is ticked or the email matches the
// Primary Contact. That contact already has access, so its box is locked.
const isCreateContactSameAsPrimary = (
  form: Pick<CreateMemberFormState, "contacts">,
  role: ContactRole,
) => {
  if (role === "primaryVoting") return false;
  const contact = form.contacts[role];
  const email = contact.email.trim().toLowerCase();
  return !!contact.sameAsCompanyPrimaryContact ||
    (!!email && email === form.contacts.primaryVoting.email.trim().toLowerCase());
};

const getCreateContactValidationInput = (
  form: Pick<CreateMemberFormState, "contacts" | "dashboardAccessRequestedContactIds">,
  section: ContactSectionConfig,
): MemberContactValidationInput => ({
  section: section.title,
  nameLabel: section.nameLabel,
  name: form.contacts[section.role].name,
  email: form.contacts[section.role].email,
  required: section.requiredFields.length > 0,
  accessRequested: section.role !== "primaryVoting" && !!form.dashboardAccessRequestedContactIds?.includes(section.role),
  sameAsPrimary: isCreateContactSameAsPrimary(form, section.role),
});

/** Rows of the Step 2 MISSING REQUIRED FIELDS pop-up (CMP Dashboard (19) L16580-16595). */
export const getCreateMemberContactDialogRows = (
  form: Pick<CreateMemberFormState, "contacts" | "dashboardAccessRequestedContactIds" | "billingAddress">,
): MissingRequiredFieldsRow[] => createMemberContactSections.flatMap((section) => [
  ...getMemberContactRows(getCreateContactValidationInput(form, section)),
  ...(section.includesBillingAddress ? getMemberBillingAddressRows(section.title, form.billingAddress) : []),
]);

// The Primary Contact is owned by trusted activation. Hidden legacy contacts
// and invalid or copied contact details must never become new access requests.
export const getCreateMemberDashboardAccessRequests = (
  form: Pick<CreateMemberFormState, "contacts" | "dashboardAccessRequestedContactIds">,
): string[] => createMemberContactSections
  .filter(({ role }) => role !== "primaryVoting" &&
    form.dashboardAccessRequestedContactIds?.includes(role) &&
    canRequestContactDashboardAccess(form.contacts[role]))
  .map(({ role }) => role);


const getScrollableParent = (element: HTMLElement | null) => {
  let currentElement = element?.parentElement || null;

  while (currentElement) {
    const overflowY = window.getComputedStyle(currentElement).overflowY;
    const hasScrollableContent =
      currentElement.scrollHeight > currentElement.clientHeight;
    const allowsVerticalScroll =
      overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay";

    if (hasScrollableContent && allowsVerticalScroll) {
      return currentElement;
    }

    currentElement = currentElement.parentElement;
  }

  return null;
};

const CreateMemberPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const noun = useCmpOrganizationNoun();
  const setPageHeaderOverride = usePageHeaderOverride();
  const { user: authenticatedUser } = useGetUserFromUserIdInAuthState();
  const canManageMembers = canManageCmpMembers(
    authenticatedUser?.usagePermissions?.chamberMemberPro_EnabledPages,
  );
  const locationState = location.state as CreateMemberLocationState | null;
  const prospectIdFromQuery = useMemo(
    () => new URLSearchParams(location.search).get("prospectId")?.trim() || "",
    [location.search],
  );
  const prefilledMember = prospectIdFromQuery
    ? undefined
    : locationState?.cmpPrefillMember;
  const prefilledThirdPartyBillingSnapshot = prospectIdFromQuery
    ? undefined
    : locationState?.cmpPrefillThirdPartyBillingSnapshot;
  const [formStateWithBaseline, setFormStateWithBaseline] = useState(() => {
    const initialFormData = prefilledMember
      ? mapPrefilledMemberToFormState(
          prefilledMember,
          prefilledThirdPartyBillingSnapshot,
        )
      : createInitialFormState();

    return {
      current: initialFormData,
      savedBaseline: initialFormData,
    };
  });
  const formData = formStateWithBaseline.current;
  const savedFormBaseline = formStateWithBaseline.savedBaseline;
  const setFormData = (action: React.SetStateAction<CreateMemberFormState>) => {
    setFormStateWithBaseline((currentState) => {
      const nextFormData =
        typeof action === "function" ? action(currentState.current) : action;

      return nextFormData === currentState.current
        ? currentState
        : { ...currentState, current: nextFormData };
    });
  };
  const [prefillMemberId] = useState(prefilledMember?.id || "");
  const [errors, setErrors] = useState<FormErrors>({});
  // Step 2 Additional Contacts (IF11 MB6), saved with the member.
  const [additionalContacts, setAdditionalContacts] = useState<CustomContactInput[]>([]);
  // CMP Dashboard (19) Step 2 MISSING REQUIRED FIELDS pop-up and the access
  // red borders it leaves after Next Step.
  const [missingFieldRows, setMissingFieldRows] = useState<MissingRequiredFieldsRow[]>([]);
  const [contactAccessTried, setContactAccessTried] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{
    severity: "success" | "info" | "warning" | "error";
    message: string;
  } | null>(null);
  const [footerStatusMessage, setFooterStatusMessage] = useState<{
    severity: "success" | "info" | "warning" | "error";
    message: string;
  } | null>(null);
  const [applicationSubmissionInFlight, setApplicationSubmissionInFlight] =
    useState(false);
  const [memberLoginEmailConflictDialog, setMemberLoginEmailConflictDialog] =
    useState<{
      requestId: number;
      checkedLoginEmail: string;
      suggestedLoginEmail: string;
    } | null>(null);
  const [memberLoginEmailRetryInFlight, setMemberLoginEmailRetryInFlight] =
    useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateMatchesToShow, setDuplicateMatchesToShow] = useState<
    DuplicateDialogMatch[]
  >([]);
  const duplicateDialogMatches = duplicateMatchesToShow;
  const duplicateRecordId = duplicateMatchesToShow[0]?.id || "";
  const [logoError, setLogoError] = useState("");
  const [staffNoteDraft, setStaffNoteDraft] = useState("");
  const [draftNotice, setDraftNotice] = useState("");
  const [draftSaveError, setDraftSaveError] = useState("");
  const savedDraftFingerprintRef = useRef("");
  const loadedDraftKeyRef = useRef("");
  const [initialStaffNoteCreatedAt, setInitialStaffNoteCreatedAt] = useState(0);
  const [createdSuccess, setCreatedSuccess] = useState<{
    name: string;
    amount: string;
    hasInvoice: boolean;
  } | null>(null);
  const [activeCreateMemberStep, setActiveCreateMemberStep] = useState(1);
  const [affinityEnrollmentDraft, setAffinityEnrollmentDraft] =
    useState<AffinityProgramEnrollmentDraft>(
      createEmptyAffinityProgramEnrollmentDraft(),
    );
  const [affinityEnrollmentError, setAffinityEnrollmentError] = useState("");
  const [pendingAffinityEnrollment, setPendingAffinityEnrollment] =
    useState<AffinityProgramEnrollment | null>(null);
  const [editingAffinityEnrollment, setEditingAffinityEnrollment] =
    useState<AffinityProgramEnrollment | null>(null);
  const affinityDialogContentRef = useRef<HTMLDivElement>(null);
  const [invoicePreview, setInvoicePreview] =
    useState<CmpInvoiceRecordFieldsFragment | null>(null);
  // BILL-003: monthly + add-ons produces a SECOND separate full-amount
  // invoice for the add-on benefits. The backend returns it as
  // `addOnInvoice`; kept separately so the preview can show both invoices.
  const [addOnInvoicePreview, setAddOnInvoicePreview] =
    useState<CmpInvoiceRecordFieldsFragment | null>(null);
  const [invoicePreviewOpen, setInvoicePreviewOpen] = useState(false);
  const [invoiceZoom, setInvoiceZoom] = useState<InvoicePreviewZoom>("fit");
  const invoiceViewportRef = useRef<HTMLDivElement>(null);
  const invoicePageRef = useRef<HTMLDivElement>(null);
  const invoiceFitDependencies = useMemo(
    () => [invoicePreview, addOnInvoicePreview],
    [invoicePreview, addOnInvoicePreview],
  );
  const { fitScale: invoiceFitScale, pageHeight: invoicePageHeight } =
    useInvoicePreviewFitScale({
      open: invoicePreviewOpen,
      pageWidth: 1070,
      viewportRef: invoiceViewportRef,
      pageRef: invoicePageRef,
      deps: invoiceFitDependencies,
      fitHeight: false,
    });
  const invoiceScale =
    invoiceZoom === "fit" ? invoiceFitScale : invoiceZoom / 100;
  const [
    createdInvoicePaymentRequestCapabilities,
    setCreatedInvoicePaymentRequestCapabilities,
  ] = useState<CmpMemberApplicationPaymentRequestCapability[]>([]);
  const [previewStripeCheckoutUrl, setPreviewStripeCheckoutUrl] = useState("");
  const [invoiceDeliveryFeedback, setInvoiceDeliveryFeedback] =
    useState<MemberInvoiceDeliveryFeedbackState | null>(null);
  const [invoiceSuccessMessage, setInvoiceSuccessMessage] = useState("");
  // Blocks the "same as ..." contact copy when the source contact is still
  // blank (MEM-CREATE-006); holds the explanation shown in a small dialog.
  const [contactCopyBlockedMessage, setContactCopyBlockedMessage] =
    useState("");
  const createMemberRootRef = useRef<HTMLDivElement>(null);
  const appliedProspectPrefillIdRef = useRef("");
  const memberApplicationSubmissionInFlightRef = useRef(false);
  const nextMemberLoginEmailConflictRequestIdRef = useRef(0);
  const pendingMemberLoginEmailConflictDecisionRef = useRef<{
    requestId: number;
    resolve: (decision: MemberLoginEmailConflictDecision) => void;
  } | null>(null);

  useEffect(
    () => () => {
      const pendingDecision =
        pendingMemberLoginEmailConflictDecisionRef.current;
      pendingMemberLoginEmailConflictDecisionRef.current = null;
      pendingDecision?.resolve("cancel");
    },
    [],
  );
  const hasUnsavedFormDataChanges = hasUnsavedMemberFormChanges({
    baseline: savedFormBaseline,
    current: formData,
  });
  const affinityEnrollmentDraftHasChanges = Object.values(
    affinityEnrollmentDraft,
  ).some((value) => value.trim().length > 0);
  const editingAffinityEnrollmentBaseline = editingAffinityEnrollment
    ? formData.affinityProgramEnrollments.find(
        (enrollment) => enrollment.id === editingAffinityEnrollment.id,
      )
    : undefined;
  const editingAffinityEnrollmentHasChanges = editingAffinityEnrollment
    ? !editingAffinityEnrollmentBaseline ||
      hasUnsavedFormChanges({
        baseline: editingAffinityEnrollmentBaseline,
        current: editingAffinityEnrollment,
      })
    : false;
  const isDirty =
    hasUnsavedFormDataChanges ||
    affinityEnrollmentDraftHasChanges ||
    editingAffinityEnrollmentHasChanges ||
    Boolean(staffNoteDraft.trim());
  const chamber = useGetChamberFromUserIdInAuth();
  const chamberId = chamber?.id || "";
  const browserDraftKey = authenticatedUser?.id && chamberId
    ? createMemberDraftKey(authenticatedUser.id, chamberId, prospectIdFromQuery || prefillMemberId) : "";
  const browserDraft = { form: formData, step: activeCreateMemberStep, staffNote: staffNoteDraft,
    staffNoteCreatedAt: initialStaffNoteCreatedAt, affinityDraft: affinityEnrollmentDraft, editingAffinity: editingAffinityEnrollment };
  const browserDraftFingerprint = JSON.stringify(browserDraft);
  const saveBrowserDraft = async (): Promise<boolean> => {
    setDraftSaveError("");
    if (!browserDraftKey) { setDraftSaveError(`Wait for your ${noun.singular} account to load before saving.`); return false; }
    try {
      saveCreateMemberDraft(browserDraftKey, { ...browserDraft, savedAt: Date.now() });
      savedDraftFingerprintRef.current = browserDraftFingerprint;
      return true;
    } catch {
      setDraftSaveError("We could not save a copy of this form in this browser. Keep this page open and try again. A large logo can stop the save. If it still does not save, remove the logo and try again.");
      return false;
    }
  };

  const {
    data: prospectDetailData,
    loading: prospectDetailLoading,
    error: prospectDetailError,
  } = useGetCmpProspectDetailQuery({
    variables: {
      input: {
        chamberId,
        prospectId: prospectIdFromQuery,
      },
    },
    skip: !chamberId || !prospectIdFromQuery,
    fetchPolicy: "cache-and-network",
    context: memberRequestApolloContext,
  });
  const queriedProspect = prospectDetailData?.getCmpProspectDetail;
  const sourceProspect =
    queriedProspect?.id === prospectIdFromQuery ? queriedProspect : undefined;
  const convertibleSourceProspect =
    sourceProspect?.status === CmpProspectStatusEnum.Active
      ? sourceProspect
      : null;

  useEffect(() => {
    if (
      !convertibleSourceProspect ||
      appliedProspectPrefillIdRef.current === convertibleSourceProspect.id ||
      hasUnsavedFormDataChanges
    ) {
      return;
    }

    const nextFormData = mapProspectToCreateMemberFormState(
      convertibleSourceProspect,
    );

    setFormStateWithBaseline({
      current: nextFormData,
      savedBaseline: nextFormData,
    });
    setErrors({});
    appliedProspectPrefillIdRef.current = convertibleSourceProspect.id;
  }, [convertibleSourceProspect, hasUnsavedFormDataChanges]);
  useEffect(() => {
    if (!browserDraftKey || loadedDraftKeyRef.current === browserDraftKey) return;
    loadedDraftKeyRef.current = browserDraftKey;
    try {
      const draft = readCreateMemberDraft(browserDraftKey);
      if (!draft || hasUnsavedFormDataChanges) return;
      setFormStateWithBaseline({ current: draft.form, savedBaseline: draft.form });
      setActiveCreateMemberStep(draft.step); setStaffNoteDraft(draft.staffNote);
      setInitialStaffNoteCreatedAt(draft.staffNoteCreatedAt); setAffinityEnrollmentDraft(draft.affinityDraft);
      setEditingAffinityEnrollment(draft.editingAffinity);
      savedDraftFingerprintRef.current = JSON.stringify({ form: draft.form, step: draft.step, staffNote: draft.staffNote,
        staffNoteCreatedAt: draft.staffNoteCreatedAt, affinityDraft: draft.affinityDraft, editingAffinity: draft.editingAffinity });
      appliedProspectPrefillIdRef.current = prospectIdFromQuery;
      setDraftNotice("Your saved draft was restored. It stays in this browser tab for 24 hours or until you sign out. No member has been created from this draft.");
    } catch {
      setDraftNotice("The saved browser draft could not be restored. You can start a new form here.");
    }
  }, [browserDraftKey, hasUnsavedFormDataChanges, prospectIdFromQuery]);
  const {
    data: categoriesData,
    loading: categoriesLoading,
    error: categoriesError,
  } = useGetAllCategoriesQuery({ context: memberRequestApolloContext });
  const {
    data: affinityProgramsData,
    loading: affinityProgramsLoading,
    error: affinityProgramsError,
  } = useGetAllAffinityProgramPartnersForChamberQuery({
    variables: { chamberId },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
    context: memberRequestApolloContext,
  });
  const {
    data: globalSettingsData,
    loading: globalSettingsLoading,
    error: globalSettingsError,
  } = useCmpGlobalSettings(chamberId, undefined, memberRequestApolloContext);
  const {
    data: membershipSettingsData,
    loading: membershipSettingsLoading,
    error: membershipSettingsError,
  } = useCmpMembershipSettings(chamberId, undefined, memberRequestApolloContext);
  const {
    data: governanceSettingsData,
    loading: governanceSettingsLoading,
    error: governanceSettingsError,
  } = useCmpGovernanceSettings(chamberId, "cache-and-network", memberRequestApolloContext);
  const chamberSettingsLoading =
    globalSettingsLoading || membershipSettingsLoading;
  const chamberSettingsError = globalSettingsError || membershipSettingsError;
  // Primary Member companies for the Associate Member Company dropdown come
  // from the chamber's real (Active) member list, per the step 3 spec's
  // "Select the Primary Member Company" drop-down (mirrors Update).
  const { data: primaryCompaniesData, loading: primaryCompaniesLoading } =
    useGetCmpManageMembersQuery({
      variables: {
        input: {
          chamberId,
          // The primary company picker needs member rows only, which skips
          // the chamber invoice read (large chambers: seconds faster).
          includeInvoiceSummary: false,
          lifecycleStatuses: [CmpMemberLifecycleStatusEnum.Active],
        },
      },
      skip: !chamberId,
      fetchPolicy: "cache-and-network",
    });
  const [submitCmpMemberApplication, { loading: submittingApplication }] =
    useSubmitCmpMemberApplicationMutation({ context: memberRequestApolloContext });
  const applicationSubmissionBusy =
    applicationSubmissionInFlight || submittingApplication;
  const [downloadCmpInvoicePdf, { loading: downloadingInvoicePdf }] =
    useDownloadCmpInvoicePdfMutation({ context: memberRequestApolloContext });
  const [sendCmpInvoicePaymentRequest, { loading: sendingInvoiceRequest }] =
    useSendCmpInvoicePaymentRequestMutation({ context: memberRequestApolloContext });
  const globalSettings = globalSettingsData?.getChamberMemberProSettings;
  const membershipSettings =
    membershipSettingsData?.getChamberMemberProSettings;
  const chamberSettings = useMemo(
    () =>
      globalSettings && membershipSettings
        ? {
            ...globalSettings,
            membership: membershipSettings.membership,
          }
        : undefined,
    [globalSettings, membershipSettings],
  );

  const membershipModels = useMemo<MockMembershipModel[]>(() => {
    // Membership options are settings-driven (MEM-CREATE-022). There is no
    // mock fallback: until Chamber Set Up loads, the billing step renders a
    // loading state instead of options.
    if (!chamberSettings) {
      return [];
    }

    return chamberSettings.membership.membershipModels
      .filter((model) => model.enabled)
      .map((model) => ({
        id: model.id,
        code: model.code,
        name: model.name,
        description: model.description,
        pricingModel:
          model.pricingModel ===
          ChamberMemberProMembershipPricingModelEnum.EmployeeRange
            ? ("employeeRange" as const)
            : ("fixed" as const),
        annualPrice: model.annualPriceInDollars,
        monthlyPrice: model.monthlyPriceInDollars,
        paymentResponsibility: model.paymentResponsibility,
        thirdPartyPayerName: model.thirdPartyPayerName,
        thirdPartyPaymentNotes: model.thirdPartyPaymentNotes,
        excludeOneTimeOnboardingFee: model.excludeOneTimeOnboardingFee === true,
        excludeProvincialChamberFee: model.excludeProvincialChamberFee === true,
        collectsCharityRegistrationNumber:
          model.collectsCharityRegistrationNumber === true,
        additionalCosts: model.additionalCosts.map((cost) => ({
          id: cost.id,
          code: cost.code,
          name: cost.name,
          description: cost.description,
          annualAmount: cost.annualAmountInDollars,
          enabled: cost.enabled,
          taxExempt: cost.taxExempt === true,
        })),
        ranges: model.ranges.map((range) => ({
          id: range.id,
          code: range.code,
          from: range.from,
          to: range.to ?? undefined,
          annualPrice: range.annualPriceInDollars,
          monthlyPrice: range.monthlyPriceInDollars,
        })),
      }));
  }, [chamberSettings]);
  const hasNoConfiguredMembershipModels =
    !!chamberSettings && membershipModels.length === 0;
  // Design Step 4: a chamber with membership tiers selects a "Membership
  // Tier" instead of a "Membership Type".
  const membershipTierMode = membershipModels.some(isTierMembershipModel);

  const selectedMembershipModel = useMemo(
    () =>
      membershipModels.find(
        (model) => model.id === formData.membership.membershipTypeId,
      ),
    [formData.membership.membershipTypeId, membershipModels],
  );
  const showCharityRegistrationNumber =
    shouldShowMemberCharityRegistrationNumber({
      membershipModels: [selectedMembershipModel],
    });
  const creditCardPaymentsReady = getCmpCreditCardPaymentsReady(
    chamberSettings?.paymentsAndTaxes.stripeConnect,
  );
  const usesThirdPartyMembershipBilling =
    formData.membership.billingResponsibility === "affiliatedPrimaryCompany" ||
    formData.membership.billingResponsibility === "otherThirdParty" ||
    formData.membership.billingResponsibility === "chamberGroupInsurance";
  const enabledPaymentSchedules = useMemo(
    () =>
      (chamberSettings?.membership.enabledPaymentSchedules ?? []).filter(
        (schedule) =>
          schedule !== ChamberMemberProPaymentScheduleEnum.Monthly ||
          creditCardPaymentsReady ||
          usesThirdPartyMembershipBilling,
      ),
    [chamberSettings, creditCardPaymentsReady, usesThirdPartyMembershipBilling],
  );
  // Payment schedules are settings-driven only (MEM-CREATE-022): nothing is
  // offered until Chamber Set Up loads (the billing step is gated on it).
  const availablePaymentScheduleOptions = useMemo(
    () =>
      membershipPaymentScheduleOptions.filter((option) =>
        enabledPaymentSchedules.includes(option.value),
      ),
    [enabledPaymentSchedules],
  );
  const availablePaymentScheduleValues = useMemo(
    () => availablePaymentScheduleOptions.map((option) => option.value),
    [availablePaymentScheduleOptions],
  );
  const createPaymentScheduleMutationGate = useMemo(
    () =>
      gateCreateMemberPaymentScheduleMutation({
        enabledPaymentSchedules: availablePaymentScheduleValues,
        storedPaymentSchedule: formData.membership.paymentSchedule,
      }),
    [availablePaymentScheduleValues, formData.membership.paymentSchedule],
  );
  const paymentScheduleResolution =
    createPaymentScheduleMutationGate.resolution;
  const selectedPaymentSchedule = paymentScheduleResolution.paymentSchedule;
  const shouldShowMembershipPaymentOption =
    shouldShowMemberPaymentScheduleSelection(paymentScheduleResolution);
  const membershipDiscountSettingEnabled =
    !!chamberSettings?.paymentsAndTaxes.membershipDiscount.enabled;
  const configuredOneTimeOnboardingFee =
    chamberSettings?.paymentsAndTaxes.oneTimeOnboardingFee;
  const oneTimeOnboardingFeeInDollars =
    configuredOneTimeOnboardingFee?.enabled === true
      ? Math.max(0, configuredOneTimeOnboardingFee.amountInDollars || 0)
      : 0;
  const hasPayableOneTimeOnboardingFee = oneTimeOnboardingFeeInDollars > 0;
  const configuredProvincialChamberFee =
    chamberSettings?.paymentsAndTaxes.provincialChamberFee;
  const provincialChamberFeeInDollars =
    configuredProvincialChamberFee?.enabled === true
      ? Math.max(0, configuredProvincialChamberFee.amountInDollars || 0)
      : 0;
  // Primary Member companies come from the chamber's real (Active) member
  // list (MEM-CREATE-022 / DI-017 — mirrors Update Member).
  const primaryMemberCompanyOptions: PrimaryMemberCompanyOption[] = (
    primaryCompaniesData?.getCmpManageMembers.rows || []
  )
    .map((row) => ({ id: row.id, name: row.companyName }))
    .sort((firstCompany, secondCompany) =>
      firstCompany.name.localeCompare(secondCompany.name),
    );
  const selectedPrimaryMemberCompany = primaryMemberCompanyOptions.find(
    (company) => company.id === formData.association.primaryMemberCompanyId,
  );
  // The affiliated Primary Member's own record gives the invoice payer
  // (Accounts Payable contact and billing address), the same as Update
  // Member, so staff do not retype the parent's billing email.
  const { data: affiliatedPrimaryMemberData } = useGetCmpMemberDetailQuery({
    variables: {
      input: {
        chamberId: chamberId || "",
        memberId: selectedPrimaryMemberCompany?.id || "",
      },
    },
    skip: !chamberId || !selectedPrimaryMemberCompany,
    fetchPolicy: "cache-first",
  });
  const affiliatedPrimaryMember =
    affiliatedPrimaryMemberData?.getCmpMemberDetail.member;
  const affiliatedPrimaryCompanyBillingSnapshot = useMemo(
    () => getAffiliatedPrimaryCompanyBillingSnapshot(affiliatedPrimaryMember),
    [affiliatedPrimaryMember],
  );

  // Fill-only: copy the parent's billing details into empty payer fields
  // once they load. Values staff typed are never overwritten.
  useEffect(() => {
    if (
      formData.membership.billingResponsibility !==
        "affiliatedPrimaryCompany" ||
      !affiliatedPrimaryCompanyBillingSnapshot
    ) {
      return;
    }

    const nextSnapshot = fillEmptyMemberThirdPartyBillingSnapshotFields(
      formData.membership.thirdPartyBillingSnapshot,
      affiliatedPrimaryCompanyBillingSnapshot,
    );

    if (nextSnapshot) {
      updateSection("membership", {
        thirdPartyBillingSnapshot: nextSnapshot,
        thirdPartyPayerName: nextSnapshot.customerName,
      });
    }
    // updateSection is a stable form helper; the snapshot drives the fill.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    affiliatedPrimaryCompanyBillingSnapshot,
    formData.membership.billingResponsibility,
    formData.membership.thirdPartyBillingSnapshot,
  ]);
  const chamberGroupInsuranceBillingDetails =
    chamberSettings?.paymentsAndTaxes.chamberGroupInsurancePayment
      .billingDetails;
  const chamberGroupInsuranceBillingSnapshot = {
    customerName: chamberGroupInsuranceBillingDetails?.companyName || "",
    contactName: chamberGroupInsuranceBillingDetails?.contactName || "",
    contactEmail: chamberGroupInsuranceBillingDetails?.contactEmail || "",
    contactPhone: chamberGroupInsuranceBillingDetails?.phoneNumber || "",
    addressLine1: chamberGroupInsuranceBillingDetails?.streetAddress || "",
    addressLine2: chamberGroupInsuranceBillingDetails?.addressLine2 || "",
    city: chamberGroupInsuranceBillingDetails?.city || "",
    province: chamberGroupInsuranceBillingDetails?.province || "",
    postalCode: chamberGroupInsuranceBillingDetails?.postalCode || "",
    country: chamberGroupInsuranceBillingDetails?.country || "Canada",
  };
  const chamberGroupInsuranceBillingIsComplete =
    Object.keys(
      getMemberThirdPartyBillingSnapshotErrors(
        chamberGroupInsuranceBillingSnapshot,
      ),
    ).length === 0;
  const chamberGroupInsuranceOptionAvailable = Boolean(
    chamberSettings?.paymentsAndTaxes.chamberGroupInsurancePayment.enabled &&
    chamberGroupInsuranceBillingIsComplete,
  );
  const visibleMembershipBillingOptions = useMemo(
    () =>
      membershipBillingOptions.filter(
        (option) =>
          option.value !== "otherThirdParty" ||
          formData.membership.billingResponsibility === "otherThirdParty",
      ),
    [formData.membership.billingResponsibility],
  );
  const selectedPaymentResponsibility =
    formData.membership.billingResponsibility === "complimentary" ||
    formData.membership.billingResponsibility === "freeLifetime"
      ? ChamberMemberProMembershipModelPaymentResponsibilityEnum.NoCharge
      : formData.membership.billingResponsibility === "chamberGroupInsurance" ||
          formData.membership.billingResponsibility === "otherThirdParty" ||
          formData.membership.billingResponsibility ===
            "affiliatedPrimaryCompany"
        ? ChamberMemberProMembershipModelPaymentResponsibilityEnum.ThirdPartyPays
        : ChamberMemberProMembershipModelPaymentResponsibilityEnum.MemberPays;
  const selectedMembershipIsThirdPartyPaid =
    selectedPaymentResponsibility ===
    ChamberMemberProMembershipModelPaymentResponsibilityEnum.ThirdPartyPays;
  const selectedMembershipIsNoCharge =
    selectedPaymentResponsibility ===
    ChamberMemberProMembershipModelPaymentResponsibilityEnum.NoCharge;
  const createdInvoicePaymentRequestResolution = useMemo(
    () =>
      resolveMemberApplicationPaymentRequestCapabilities({
        capabilities: createdInvoicePaymentRequestCapabilities,
        invoices: [
          ...(invoicePreview
            ? [{ kind: "membership" as const, invoice: invoicePreview }]
            : []),
          ...(addOnInvoicePreview
            ? [{ kind: "add-on" as const, invoice: addOnInvoicePreview }]
            : []),
        ],
      }),
    [
      addOnInvoicePreview,
      createdInvoicePaymentRequestCapabilities,
      invoicePreview,
    ],
  );
  const pendingCreatedInvoiceDeliveryTargets =
    createdInvoicePaymentRequestResolution.sendable;
  const blockedCreatedInvoiceDeliveryTargets =
    createdInvoicePaymentRequestResolution.blocked;
  const unverifiedCreatedInvoiceDeliveryTargets =
    createdInvoicePaymentRequestResolution.unverified;
  const canSendCreatedInvoicePaymentRequest =
    pendingCreatedInvoiceDeliveryTargets.length > 0;

  useEffect(() => {
    const prefilledMembership = prefilledMember?.cmpExtension?.membership;

    if (!prefilledMembership || !chamberSettings) {
      return;
    }

    const billingResponsibility = getMemberBillingResponsibility({
      configuredInsurancePayerName:
        chamberGroupInsuranceBillingDetails?.companyName,
      insuranceOptionAvailable: chamberGroupInsuranceOptionAvailable,
      membership: prefilledMembership,
    });

    setFormStateWithBaseline((currentState) => {
      const savedBillingResponsibility =
        currentState.savedBaseline.membership.billingResponsibility;
      const currentBillingResponsibility =
        currentState.current.membership.billingResponsibility;

      if (
        !shouldRebaseUntouchedMemberSystemValue({
          baseline: savedBillingResponsibility,
          current: currentBillingResponsibility,
          next: billingResponsibility,
        })
      ) {
        return currentState;
      }

      const nextFormData = {
        ...currentState.current,
        membership: {
          ...currentState.current.membership,
          billingResponsibility,
        },
      };

      return {
        current: nextFormData,
        savedBaseline: {
          ...currentState.savedBaseline,
          membership: {
            ...currentState.savedBaseline.membership,
            billingResponsibility,
          },
        },
      };
    });
  }, [
    chamberGroupInsuranceBillingDetails?.companyName,
    chamberGroupInsuranceOptionAvailable,
    chamberSettings,
    prefilledMember,
  ]);
  const createdInvoiceSendButtonLabel = getCreatedInvoiceSendButtonLabel(
    pendingCreatedInvoiceDeliveryTargets,
  );
  const configuredMembershipAddOns = useMemo(
    () => getConfiguredMembershipAddOns(membershipModels),
    [membershipModels],
  );
  const selectedMembershipAddOns = configuredMembershipAddOns;
  const selectedMembershipAddOnIds = useMemo(
    () => new Set(selectedMembershipAddOns.map((addOn) => addOn.id)),
    [selectedMembershipAddOns],
  );
  const effectiveMembershipDiscountAmount =
    membershipDiscountSettingEnabled &&
    formData.membership.membershipDiscount.enabled
      ? formData.membership.membershipDiscount.amount
      : "";
  const membershipInvoiceTaxRatePercentage =
    getMembershipInvoiceTaxRatePercentage({
      invoiceTaxSettings:
        chamberSettings?.paymentsAndTaxes.invoiceTaxSettings.membershipInvoices,
    });

  useEffect(() => {
    if (!chamberSettings) {
      return;
    }

    setFormStateWithBaseline((currentState) => {
      const current = currentState.current;
      const currentPaymentSchedule = current.membership.paymentSchedule;
      const nextPaymentSchedule = normalizeMemberPaymentSchedule({
        enabledPaymentSchedules: availablePaymentScheduleValues,
        storedPaymentSchedule: currentPaymentSchedule,
      }).paymentSchedule;
      const nextSelectedAdditionalCostIds =
        current.membership.selectedAdditionalCostIds.filter((id) =>
          selectedMembershipAddOnIds.has(id),
        );
      const nextMembershipDiscount = membershipDiscountSettingEnabled
        ? current.membership.membershipDiscount
        : {
            enabled: false,
            code: "",
            description: "",
            amount: "",
          };
      const paymentScheduleChanged =
        current.membership.paymentSchedule !== nextPaymentSchedule;
      const selectedAdditionalCostsChanged =
        nextSelectedAdditionalCostIds.length !==
          current.membership.selectedAdditionalCostIds.length ||
        nextSelectedAdditionalCostIds.some(
          (id, index) =>
            id !== current.membership.selectedAdditionalCostIds[index],
        );
      const membershipDiscountChanged =
        !membershipDiscountSettingEnabled &&
        (current.membership.membershipDiscount.enabled ||
          !!current.membership.membershipDiscount.code ||
          !!current.membership.membershipDiscount.description ||
          !!current.membership.membershipDiscount.amount);
      const savedMembership = currentState.savedBaseline.membership;
      const paymentScheduleWasUnchanged =
        current.membership.paymentSchedule === savedMembership.paymentSchedule;
      const selectedAdditionalCostsWereUnchanged =
        haveSameMemberSelectionValues(
          current.membership.selectedAdditionalCostIds,
          savedMembership.selectedAdditionalCostIds,
        );
      const membershipDiscountWasUnchanged = !hasUnsavedFormChanges({
        baseline: savedMembership.membershipDiscount,
        current: current.membership.membershipDiscount,
      });

      if (
        !paymentScheduleChanged &&
        !selectedAdditionalCostsChanged &&
        !membershipDiscountChanged
      ) {
        return currentState;
      }

      const nextFormData = {
        ...current,
        membership: {
          ...current.membership,
          paymentSchedule: nextPaymentSchedule,
          selectedAdditionalCostIds: nextSelectedAdditionalCostIds,
          membershipDiscount: nextMembershipDiscount,
        },
      };

      return {
        current: nextFormData,
        savedBaseline: {
          ...currentState.savedBaseline,
          membership: {
            ...savedMembership,
            paymentSchedule: paymentScheduleWasUnchanged
              ? nextPaymentSchedule
              : savedMembership.paymentSchedule,
            selectedAdditionalCostIds: selectedAdditionalCostsWereUnchanged
              ? [...nextSelectedAdditionalCostIds]
              : [...savedMembership.selectedAdditionalCostIds],
            membershipDiscount: membershipDiscountWasUnchanged
              ? { ...nextMembershipDiscount }
              : savedMembership.membershipDiscount,
          },
        },
      };
    });
  }, [
    availablePaymentScheduleValues,
    chamberSettings,
    membershipDiscountSettingEnabled,
    selectedMembershipAddOnIds,
  ]);

  const feeBreakdown = useMemo(
    () =>
      calculateFeeBreakdown(
        formData.membership.membershipTypeId,
        formData.membership.fullTimeEmployees,
        formData.membership.partTimeEmployees,
        formData.membership.selectedAdditionalCostIds,
        selectedPaymentSchedule,
        effectiveMembershipDiscountAmount,
        membershipInvoiceTaxRatePercentage,
        membershipModels,
        chamberSettings?.membership.partTimeEmployeesPerFullTime,
        // Proration covers the term up to the next renewal billing date, so
        // it applies only with the Fix Date or Multi-Billing Dates (R5).
        !!chamberSettings?.membership.proratedSignupPricingEnabled &&
          (!!chamberSettings?.setup.membershipRenewalFixedDateEnabled ||
            !!chamberSettings?.setup.multipleBillingDates?.enabled),
        chamberSettings?.membership.proratedSignupPrices,
        oneTimeOnboardingFeeInDollars,
        selectedMembershipIsNoCharge,
        provincialChamberFeeInDollars,
      ),
    [
      chamberSettings?.membership.partTimeEmployeesPerFullTime,
      chamberSettings?.membership.proratedSignupPricingEnabled,
      chamberSettings?.setup.membershipRenewalFixedDateEnabled,
      chamberSettings?.setup.multipleBillingDates?.enabled,
      chamberSettings?.membership.proratedSignupPrices,
      formData.membership.membershipTypeId,
      formData.membership.fullTimeEmployees,
      formData.membership.partTimeEmployees,
      formData.membership.selectedAdditionalCostIds,
      effectiveMembershipDiscountAmount,
      membershipInvoiceTaxRatePercentage,
      membershipModels,
      oneTimeOnboardingFeeInDollars,
      provincialChamberFeeInDollars,
      selectedPaymentSchedule,
      selectedMembershipIsNoCharge,
    ],
  );
  const hasPayableMemberFacingReceivable = getHasPayableMemberFacingReceivable({
    onboardingFee: feeBreakdown.onboardingFee,
    addOnInvoiceTotal: feeBreakdown.addOnInvoiceTotal,
  });

  const directoryCategories = useMemo(
    () => sortMemberCategoryOptions(categoriesData?.getAllCategories ?? []),
    [categoriesData?.getAllCategories],
  );
  const availableAffinityProgramPartners = useMemo(
    () =>
      (affinityProgramsData?.getAllAffinityProgramPartnersForChamber || [])
        .filter(
          (program) =>
            program.activationStatus !==
            AffinityProgramPartnerActivationStatusEnum.Deactivated,
        )
        .sort((firstProgram, secondProgram) =>
          firstProgram.companyName.localeCompare(secondProgram.companyName),
        ),
    [affinityProgramsData?.getAllAffinityProgramPartnersForChamber],
  );
  const affinityProgramOptions = useMemo(
    () =>
      availableAffinityProgramPartners.map((program) => ({
        value: program.id,
        label: program.companyName,
      })),
    [availableAffinityProgramPartners],
  );

  const enabledPaymentMethods = useMemo(() => {
    // Payment methods come from Chamber Set Up only (MEM-CREATE-022); no
    // mock fallback while settings are loading.
    if (!chamberSettings) {
      return [];
    }

    if (selectedMembershipIsThirdPartyPaid) {
      return [];
    }

    if (selectedMembershipIsNoCharge && !hasPayableMemberFacingReceivable) {
      return [{ id: "free" as PaymentMethodType, label: "Free" }];
    }

    const manualMethods = buildManualPaymentOptions(
      chamberSettings.paymentsAndTaxes.manualPaymentMethods,
    );

    return [
      ...(creditCardPaymentsReady
        ? [{ id: "creditCard" as PaymentMethodType, label: "Credit Card" }]
        : []),
      ...manualMethods,
      ...(hasPayableMemberFacingReceivable
        ? []
        : [{ id: "free" as PaymentMethodType, label: "Free" }]),
    ];
  }, [
    chamberSettings,
    creditCardPaymentsReady,
    hasPayableMemberFacingReceivable,
    selectedMembershipIsNoCharge,
    selectedMembershipIsThirdPartyPaid,
  ]);
  const onboardingFeePaymentMethods = useMemo(
    () =>
      enabledPaymentMethods.filter(
        (method) =>
          method.id !== "free" &&
          (selectedPaymentSchedule !==
            ChamberMemberProPaymentScheduleEnum.Monthly ||
            method.id === "creditCard"),
      ),
    [enabledPaymentMethods, selectedPaymentSchedule],
  );

  useEffect(() => {
    if (
      selectedPaymentSchedule !== ChamberMemberProPaymentScheduleEnum.Monthly ||
      selectedMembershipIsThirdPartyPaid ||
      (selectedMembershipIsNoCharge && !hasPayableMemberFacingReceivable)
    ) {
      return;
    }

    setFormStateWithBaseline((currentState) => {
      const current = currentState.current;
      const nextPaymentMethod = resolveCreateMemberPaymentMethod({
        enabledPaymentMethods: enabledPaymentMethods.map(({ id }) => id),
        paymentSchedule: selectedPaymentSchedule,
        storedPaymentMethod: current.membership.paymentMethod,
      });

      if (nextPaymentMethod === current.membership.paymentMethod) {
        return currentState;
      }

      const nextFormData = {
        ...current,
        membership: {
          ...current.membership,
          paymentMethod: nextPaymentMethod,
        },
      };
      const savedPaymentMethod =
        currentState.savedBaseline.membership.paymentMethod;

      return {
        current: nextFormData,
        savedBaseline: {
          ...currentState.savedBaseline,
          membership: {
            ...currentState.savedBaseline.membership,
            paymentMethod:
              current.membership.paymentMethod === savedPaymentMethod
                ? nextPaymentMethod
                : savedPaymentMethod,
          },
        },
      };
    });
  }, [
    enabledPaymentMethods,
    hasPayableMemberFacingReceivable,
    selectedMembershipIsNoCharge,
    selectedMembershipIsThirdPartyPaid,
    selectedPaymentSchedule,
  ]);

  useEffect(() => {
    setPageHeaderOverride({
      ...(createMemberPageHeaders[activeCreateMemberStep] ||
        createMemberPageHeaders[1]),
      titleClassName: "text-[28px] font-bold leading-[1.25] text-[#0A0A0A]",
      descriptionClassName: "mt-[6px] text-[14px] leading-[normal] text-[#0A0A0A]",
      horizontalPaddingClassName: "px-[16px] md:px-[40px]",
      headerSpacingClassName: "pt-[32px] pb-[16px] md:pt-[calc(36px-1rem)]",
      containerClassName: "font-['DM_Sans',sans-serif] [&>div:last-child]:pt-[8px] [&>div:last-child]:pb-[16px]",
    });

    return () => setPageHeaderOverride(null);
  }, [activeCreateMemberStep, setPageHeaderOverride]);

  const markDirty = () => {
    setStatusMessage(null);
    setFooterStatusMessage(null);
  };

  const updateSection = <
    SectionName extends Exclude<
      keyof CreateMemberFormState,
      "initialStaffNote"
    >,
  >(
    sectionName: SectionName,
    value: Partial<CreateMemberFormState[SectionName]>,
  ) => {
    markDirty();
    setFormData((current) => ({
      ...current,
      [sectionName]: {
        ...current[sectionName],
        ...value,
      },
    }));
  };

  const updateMemberAddress = (
    value: Partial<CreateMemberFormState["address"]>,
  ) => {
    markDirty();
    setFormData((current) => {
      const address = { ...current.address, ...value };

      return {
        ...current,
        address,
        billingAddress: syncBillingWithMemberAddress(
          current.billingAddress,
          address,
        ),
      };
    });
  };

  const updateBillingAddress = (value: Partial<BillingAddressInfo>) => {
    markDirty();
    setFormData((current) => ({
      ...current,
      billingAddress: editBillingAddress(current.billingAddress, value),
    }));
  };

  const toggleBillingSameAsMemberAddress = (checked: boolean) => {
    markDirty();
    setFormData((current) => ({
      ...current,
      billingAddress: setBillingSameAsMemberAddress(
        current.billingAddress,
        current.address,
        checked,
      ),
    }));
    if (checked) {
      setErrors((current) =>
        Object.fromEntries(
          Object.entries(current).filter(
            ([key]) => !key.startsWith("billingAddress."),
          ),
        ),
      );
    }
  };

  const updateReferralTracking = (
    referralId: string,
    field: keyof ReferralTrackingInfo,
    value: string,
  ) => {
    markDirty();
    setFormData((current) => ({
      ...current,
      referralTracking: current.referralTracking.map((referral) =>
        referral.id === referralId ? { ...referral, [field]: value } : referral,
      ),
    }));
  };

  const addReferralTracking = () => {
    markDirty();
    setFormData((current) => ({
      ...current,
      referralTracking: [
        ...current.referralTracking,
        createEmptyReferralTracking(),
      ],
    }));
  };

  const updateAffinityEnrollment = (enrollment: AffinityProgramEnrollment) => {
    markDirty();
    setFormData((current) => ({
      ...current,
      affinityProgramEnrollments: current.affinityProgramEnrollments.map(
        (currentEnrollment) =>
          currentEnrollment.id === enrollment.id
            ? enrollment
            : currentEnrollment,
      ),
    }));
  };

  const updateContact = (
    role: ContactRole,
    field: keyof ContactInfo,
    value: string | boolean,
  ) => {
    markDirty();
    // CMP Dashboard (19): the access box stays ticked while the name and email
    // are entered; Next Step checks them.
    setFormData((current) => ({
      ...current,
      contacts: {
        ...current.contacts,
        [role]: {
          ...current.contacts[role],
          [field]: value,
        },
      },
    }));
  };

  const setContactDashboardAccessRequest = (role: ContactRole, enabled: boolean) => {
    if (role === "primaryVoting" || (enabled && isCreateContactSameAsPrimary(formData, role))) return;
    markDirty();
    setFormData((current) => ({
      ...current,
      dashboardAccessRequestedContactIds: enabled
        ? Array.from(new Set([...(current.dashboardAccessRequestedContactIds || []), role]))
        : (current.dashboardAccessRequestedContactIds || []).filter((contactId) => contactId !== role),
    }));
  };

  const copyMainContactToPrimary = () => {
    const willCheck = !formData.contacts.primaryVoting.sameAsMainCompanyContact;

    // Copy only a valid source: staff must fill the Step 1 Main Contact before
    // "same as Main Contact" can populate this card (MEM-CREATE-006).
    if (
      willCheck &&
      (!formData.directory.mainCompanyContactName.trim() ||
        !hasEmailShape(formData.directory.mainCompanyContactEmail.trim()))
    ) {
      setContactCopyBlockedMessage(
        "Enter the Main Contact name and a valid email in Step 1 first.",
      );
      return;
    }

    markDirty();
    setFormData((current) => ({
      ...current,
      contacts: {
        ...current.contacts,
        primaryVoting: {
          ...current.contacts.primaryVoting,
          sameAsMainCompanyContact:
            !current.contacts.primaryVoting.sameAsMainCompanyContact,
          ...(!current.contacts.primaryVoting.sameAsMainCompanyContact
            ? {
                name: current.directory.mainCompanyContactName,
                email: current.directory.mainCompanyContactEmail,
                companyPhone: current.directory.mainCompanyPhone,
                ext: current.directory.mainCompanyPhoneExt,
              }
            : {}),
        },
      },
    }));
  };

  const copyPrimaryContactToRole = (
    role: Exclude<ContactRole, "primaryVoting">,
  ) => {
    const willCheck = !formData.contacts[role].sameAsCompanyPrimaryContact;

    // Same guard as copyMainContactToPrimary: block the copy while the source
    // Primary Contact is incomplete or invalid (MEM-CREATE-006).
    if (
      willCheck &&
      (!formData.contacts.primaryVoting.name.trim() ||
        !hasEmailShape(formData.contacts.primaryVoting.email.trim()))
    ) {
      setContactCopyBlockedMessage(
        "Enter the Member Primary Contact name and a valid email first.",
      );
      return;
    }

    markDirty();
    setFormData((current) => ({
      ...current,
      dashboardAccessRequestedContactIds: willCheck
        ? current.dashboardAccessRequestedContactIds?.filter((contactId) => contactId !== role)
        : current.dashboardAccessRequestedContactIds,
      contacts: {
        ...current.contacts,
        [role]: {
          ...current.contacts[role],
          sameAsCompanyPrimaryContact:
            !current.contacts[role].sameAsCompanyPrimaryContact,
          ...(!current.contacts[role].sameAsCompanyPrimaryContact
            ? {
                name: current.contacts.primaryVoting.name,
                title: current.contacts.primaryVoting.title,
                email: current.contacts.primaryVoting.email,
                companyPhone: current.contacts.primaryVoting.companyPhone,
                mobilePhone: current.contacts.primaryVoting.mobilePhone,
                ext: current.contacts.primaryVoting.ext,
              }
            : {}),
        },
      },
    }));
  };

  const memberIda = useMemberIdaGeneration({
    name: formData.directory.companyName,
    description: formData.directory.companyDescription,
    websiteUrl: formData.directory.companyWebsite,
    disabled: applicationSubmissionBusy,
    onCategories: (directoryCategoryIds) => {
      updateSection("directory", { directoryCategoryIds });
      setErrors((current) => ({ ...current, directoryCategoryIds: "" }));
    },
  });

  const getDirectoryStepErrors = () => {
    const nextErrors: FormErrors = getMemberUrlFieldErrors({
      ...formData.directory,
      ...formData.socialLinks,
    });

    if (!formData.directory.companyName.trim()) {
      nextErrors.companyName = enterFieldMessage("Member Name");
    }

    if (
      formData.directory.mainCompanyContactEmail &&
      !hasEmailShape(formData.directory.mainCompanyContactEmail.trim())
    ) {
      nextErrors.mainCompanyContactEmail = EMAIL_FORMAT_MESSAGE;
    }
    Object.assign(nextErrors, getMemberTollFreeFaxErrors({
      tollFree: formData.directory.tollFreePhone || "",
      fax: formData.directory.faxNumber || "",
      ids: { tollFree: "tollFreePhone", fax: "faxNumber" },
    }));

    // A hidden address needs only city and province (the directories show no more).
    const addressHidden = formData.address.hideStreetAddressInDirectories;

    if (!addressHidden && !formData.address.streetAddress.trim()) {
      nextErrors.streetAddress = enterFieldMessage("Street Address");
    }

    if (!formData.address.city.trim()) {
      nextErrors.city = enterFieldMessage("City");
    }

    if (!formData.address.provinceOrState) {
      nextErrors.provinceOrState = selectFieldMessage("Province/State");
    }

    if (!addressHidden && !formData.address.postalOrZipCode.trim()) {
      nextErrors.postalOrZipCode = enterFieldMessage("Postal/Zip Code");
    }

    if (!formData.address.country.trim()) {
      nextErrors.country = selectFieldMessage("Country");
    }

    if (formData.directory.directoryCategoryIds.length === 0) {
      nextErrors.directoryCategoryIds = "Select 1 to 2 directory categories.";
    }

    return nextErrors;
  };

  const getContactStepErrors = () => {
    const nextErrors: FormErrors = {};

    createMemberContactSections.forEach((section) => {
      const contact = formData.contacts[section.role];

      // Access ticked without a name and valid email: the design shows the
      // red border and the pop-up, not a field message.
      if (isContactAccessDetailsMissing(getCreateContactValidationInput(formData, section))) {
        nextErrors[`${section.role}.access`] = contactAccessMissingDetailsMessage;
        return;
      }

      if (section.requiredFields.includes("name") && !contact.name.trim()) {
        nextErrors[`${section.role}.name`] = enterFieldMessage(section.nameLabel);
      }

      if (section.requiredFields.includes("email") && !contact.email.trim()) {
        nextErrors[`${section.role}.email`] = enterFieldMessage("Contact Email");
      } else if (contact.email.trim() && !hasEmailShape(contact.email.trim())) {
        nextErrors[`${section.role}.email`] = EMAIL_FORMAT_MESSAGE;
      }
    });

    if (!formData.billingAddress.streetAddress.trim()) {
      nextErrors["billingAddress.streetAddress"] =
        enterFieldMessage("Street Address");
    }

    if (!formData.billingAddress.city.trim()) {
      nextErrors["billingAddress.city"] = enterFieldMessage("City");
    }

    if (!formData.billingAddress.provinceOrState) {
      nextErrors["billingAddress.provinceOrState"] =
        selectFieldMessage("Province/State");
    }

    if (!formData.billingAddress.postalOrZipCode.trim()) {
      nextErrors["billingAddress.postalOrZipCode"] =
        enterFieldMessage("Postal/Zip Code");
    }

    if (!formData.billingAddress.country.trim()) {
      nextErrors["billingAddress.country"] = selectFieldMessage("Country");
    }

    return nextErrors;
  };

  const getInsightsStepErrors = () => {
    const nextErrors: FormErrors = {};

    if (!isValidEstablishedYear(formData.companyDetails.yearEstablished)) {
      nextErrors.yearEstablished =
        "Select the year the member was established. It cannot be a future year.";
    }

    formData.referralTracking.forEach((referral, index) => {
      const hasReferralValue =
        referral.referralDate ||
        referral.becameMemberDate ||
        referral.companyReferralName ||
        referral.contactName ||
        referral.email ||
        referral.contactNumber ||
        referral.contactNumberExtension;

      if (hasReferralValue && !referral.companyReferralName.trim()) {
        nextErrors[`referralTracking.${referral.id}.companyReferralName`] =
          "Enter the Company Referral Name, or clear the other referral fields.";
      }

      if (referral.email.trim() && !hasEmailShape(referral.email.trim())) {
        nextErrors[`referralTracking.${referral.id}.email`] =
          EMAIL_FORMAT_MESSAGE;
      }

      if (index === 0 && !hasReferralValue) {
        return;
      }
    });

    if (formData.insights.communicationPreferences.length === 0) {
      nextErrors.communicationPreferences =
        "Select at least one communication method.";
    }

    return nextErrors;
  };

  const validateDirectoryStep = () => {
    const nextErrors = getDirectoryStepErrors();

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateContactStep = () => {
    const nextErrors = getContactStepErrors();

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateInsightsStep = () => {
    const nextErrors = getInsightsStepErrors();

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateApplication = () => {
    const nextErrors: FormErrors = {
      ...getDirectoryStepErrors(),
      ...getContactStepErrors(),
      ...getInsightsStepErrors(),
    };

    if (
      formData.association.isAssociateCompanyMember &&
      !formData.association.primaryMemberCompanyId
    ) {
      nextErrors.primaryMemberCompanyId =
        "Select the affiliated primary member company.";
    }

    if (
      formData.membership.billingResponsibility ===
        "affiliatedPrimaryCompany" &&
      !formData.association.primaryMemberCompanyId
    ) {
      nextErrors.billingResponsibility =
        memberBillingPrimaryCompanyNotSelectedMessage;
    }

    if (
      formData.membership.billingResponsibility === "chamberGroupInsurance" &&
      (!chamberSettings?.paymentsAndTaxes.chamberGroupInsurancePayment
        .enabled ||
        !chamberGroupInsuranceBillingDetails?.companyName)
    ) {
      nextErrors.billingResponsibility =
        getMemberBillingGroupInsuranceNotSetUpMessage(`${noun.Singular} Set Up`);
    }

    if (
      formData.membership.billingResponsibility === "otherThirdParty" &&
      !formData.membership.thirdPartyPayerName.trim()
    ) {
      nextErrors.billingResponsibility =
        memberBillingThirdPartyPayerMissingMessage;
    }

    if (
      formData.membership.billingResponsibility === "otherThirdParty" ||
      formData.membership.billingResponsibility === "affiliatedPrimaryCompany"
    ) {
      Object.entries(
        getMemberThirdPartyBillingSnapshotErrors(
          formData.membership.thirdPartyBillingSnapshot,
        ),
      ).forEach(([field, message]) => {
        if (message) {
          nextErrors[`thirdPartyBillingSnapshot.${field}`] = message;
        }
      });
    }

    if (!formData.membership.membershipTypeId) {
      nextErrors.membershipTypeId = membershipTierMode
        ? "Membership Tier is required"
        : "Select a membership type.";
    }

    if (!formData.membership.fullTimeEmployees.trim()) {
      nextErrors.fullTimeEmployees = "Full-Time Employees is required";
    }

    if (!formData.membership.partTimeEmployees.trim()) {
      nextErrors.partTimeEmployees = "Part-Time Employees is required";
    }

    if (createPaymentScheduleMutationGate.blocked) {
      nextErrors.paymentSchedule = createPaymentScheduleMutationGate.error;
    }

    if (
      formData.membership.billingResponsibility === "memberPays" &&
      selectedPaymentSchedule === ChamberMemberProPaymentScheduleEnum.Monthly &&
      getEffectivePaymentMethod() !== "creditCard"
    ) {
      nextErrors.paymentMethod =
        `Monthly payments need credit card payments, which are turned off for your ${noun.singular}. Select Yearly, or turn on Credit Card in ${noun.Singular} Set Up > Global Settings > Payment Options.`;
    }

    if (
      selectedMembershipModel?.pricingModel === "employeeRange" &&
      !getNumber(formData.membership.fullTimeEmployees) &&
      !getNumber(formData.membership.partTimeEmployees)
    ) {
      nextErrors.employeeCount =
        "This membership type is priced by the number of employees. Enter the number of full-time or part-time employees.";
    }

    if (
      membershipDiscountSettingEnabled &&
      formData.membership.membershipDiscount.enabled
    ) {
      if (!formData.membership.membershipDiscount.code.trim()) {
        nextErrors.membershipDiscountCode = "Enter a discount code.";
      }

      if (!formData.membership.membershipDiscount.description.trim()) {
        nextErrors.membershipDiscountDescription =
          "Enter a discount description.";
      }

      if (!getNumber(formData.membership.membershipDiscount.amount)) {
        nextErrors.membershipDiscountAmount = "Enter a discount amount.";
      }
    }

    if (
      hasPayableMemberFacingReceivable &&
      !selectedMembershipIsThirdPartyPaid
    ) {
      if (onboardingFeePaymentMethods.length === 0) {
        nextErrors.billingResponsibility =
          `No payment method is turned on for the onboarding fee or add-ons. Turn one on in ${noun.Singular} Set Up > Global Settings > Payment Options, then try again.`;
      } else if (
        !onboardingFeePaymentMethods.some(
          (method) => method.id === formData.membership.paymentMethod,
        )
      ) {
        nextErrors.paymentMethod =
          "Select how the member will pay the onboarding fee or add-on invoice.";
      }
    }

    if (!formData.membership.emailCommunicationConsentConfirmed) {
      nextErrors.emailCommunicationConsentConfirmed =
        "This confirmation is required before creating the member";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const getEffectivePaymentMethod = (): PaymentMethodType | "" => {
    const normalizedPaymentMethod = resolveCreateMemberPaymentMethod({
      enabledPaymentMethods: enabledPaymentMethods.map(({ id }) => id),
      paymentSchedule: selectedPaymentSchedule,
      storedPaymentMethod: formData.membership.paymentMethod,
    });

    // Preserve the legacy annual fallback while keeping Monthly fail-closed.
    // The backend remains authoritative and validates/re-derives the choice.
    return (
      normalizedPaymentMethod ||
      (selectedPaymentSchedule === ChamberMemberProPaymentScheduleEnum.Monthly
        ? ""
        : "cheque")
    );
  };

  const buildPaymentChoice = () => {
    if (selectedMembershipIsThirdPartyPaid) {
      return {
        type: CmpPaymentChoiceTypeEnum.ThirdParty,
        autoRenew: false,
        emailCommunicationConsentConfirmed:
          formData.membership.emailCommunicationConsentConfirmed,
      };
    }

    if (selectedMembershipIsNoCharge && !hasPayableMemberFacingReceivable) {
      return {
        type: CmpPaymentChoiceTypeEnum.Free,
        autoRenew: false,
        emailCommunicationConsentConfirmed:
          formData.membership.emailCommunicationConsentConfirmed,
      };
    }

    const effectivePaymentMethod = getEffectivePaymentMethod();
    const paymentChoiceAutoRenew = selectedMembershipIsNoCharge
      ? false
      : formData.membership.autoRenew;

    if (effectivePaymentMethod === "creditCard") {
      return {
        type: CmpPaymentChoiceTypeEnum.CreditCard,
        autoRenew: paymentChoiceAutoRenew,
        emailCommunicationConsentConfirmed:
          formData.membership.emailCommunicationConsentConfirmed,
      };
    }

    if (effectivePaymentMethod === "free") {
      return {
        type: CmpPaymentChoiceTypeEnum.Free,
        autoRenew: paymentChoiceAutoRenew,
        emailCommunicationConsentConfirmed:
          formData.membership.emailCommunicationConsentConfirmed,
      };
    }

    return {
      type: CmpPaymentChoiceTypeEnum.Manual,
      manualPaymentMethodId: mapPaymentMethodToManualMethodId(
        effectivePaymentMethod,
      ),
      autoRenew: paymentChoiceAutoRenew,
      emailCommunicationConsentConfirmed:
        formData.membership.emailCommunicationConsentConfirmed,
    };
  };

  const buildMemberInput = (
    lifecycleStatus: CmpMemberLifecycleStatusEnum,
    paymentSchedule: ChamberMemberProPaymentScheduleEnum,
  ): ChamberMemberInput => {
    const primaryVoting = formData.contacts.primaryVoting;
    const administrator = formData.contacts.administrator;
    const accountsPayable = formData.contacts.accountsPayable;
    const employeeAdoption = formData.contacts.employeeAdoption;
    const marketingCommunication = formData.contacts.marketingCommunication;
    const hrEmployeeEngagement = formData.contacts.hrEmployeeEngagement;
    const sales = formData.contacts.sales;
    const categoryId = formData.directory.directoryCategoryIds;
    const referralTracking = formData.referralTracking.filter(
      (referral) =>
        referral.referralDate ||
        referral.becameMemberDate ||
        referral.companyReferralName ||
        referral.contactName ||
        referral.email ||
        referral.contactNumber ||
        referral.contactNumberExtension,
    );
    const legacyBoardOrCommitteeInterest =
      [
        formData.insights.boardDirectorInterest,
        formData.insights.committeeInterest,
      ]
        .filter((value) => value && value !== "No")
        .join("; ") || formData.companyDetails.boardOrCommitteeInterest;
    const selectedThirdPartyBilling = getMemberThirdPartyBillingFields({
      affiliatedPrimaryCompanyName:
        selectedPrimaryMemberCompany?.name ||
        formData.membership.thirdPartyPayerName,
      billingResponsibility: formData.membership.billingResponsibility,
      configuredInsurancePayerName:
        chamberGroupInsuranceBillingDetails?.companyName,
      savedThirdPartyPayerName:
        formData.membership.thirdPartyPayerName ||
        selectedMembershipModel?.thirdPartyPayerName,
      savedThirdPartyPaymentNotes:
        formData.membership.thirdPartyPaymentNotes ||
        selectedMembershipModel?.thirdPartyPaymentNotes,
    });
    const membershipDiscount = formData.membership.membershipDiscount;
    const membershipDiscountEnabled =
      membershipDiscountSettingEnabled && membershipDiscount.enabled;
    const usesExplicitThirdPartyBillingSnapshot =
      formData.membership.billingResponsibility === "otherThirdParty" ||
      formData.membership.billingResponsibility === "affiliatedPrimaryCompany";
    const explicitThirdPartyBillingSnapshot =
      formData.membership.thirdPartyBillingSnapshot;

    return {
      id: prefillMemberId,
      chamberId: chamberId || "",
      categoryId,
      name: formData.directory.companyName.trim(),
      description: toOptionalString(formData.directory.companyDescription),
      seoTags: formData.directory.seoTags,
      website: toOptionalString(formData.directory.companyWebsite),
      facebook: toOptionalString(formData.socialLinks.facebookUrl),
      instagram: toOptionalString(formData.socialLinks.instagramUrl),
      threads: toOptionalString(formData.socialLinks.threadsUrl),
      linkedin: toOptionalString(formData.socialLinks.linkedinUrl),
      twitter: toOptionalString(formData.socialLinks.xTwitterUrl),
      tiktok: toOptionalString(formData.socialLinks.tikTokUrl),
      youtube: toOptionalString(formData.socialLinks.youtubeUrl),
      googleReviewLink: toOptionalString(formData.directory.googleReviewUrl),
      address: {
        streetAddress: formData.address.streetAddress.trim(),
        city: formData.address.city.trim(),
        province: formData.address.provinceOrState.trim(),
        postalCode: formData.address.postalOrZipCode.trim(),
      },
      hideAddressFromDirectory: formData.address.hideStreetAddressInDirectories,
      phoneNumber: formData.directory.mainCompanyPhone.trim(),
      phoneNumberExtension: toOptionalString(
        formData.directory.mainCompanyPhoneExt,
      ),
      // IF11 MB4: digits only; "" clears.
      tollFreePhoneNumber: getMemberPhoneDigits(formData.directory.tollFreePhone),
      faxNumber: getMemberPhoneDigits(formData.directory.faxNumber),
      contactName: toOptionalString(formData.directory.mainCompanyContactName),
      email: formData.directory.mainCompanyContactEmail.trim(),
      logo: toOptionalString(getLogoInputValue(formData.logo)),
      showInApp: formData.directory.showInDirectory,
      adminConsoleLoginEmail: toOptionalString(administrator.email),
      adminConsoleContactName: toOptionalString(administrator.name),
      customContacts: additionalContacts.map(normalizeCustomContact),
      hasSignedUp: false,
      firstLoginAfterSettingNewPermanentPasswordUtcMilli: null,
      isSuspended: false,
      banner: [],
      cpaAdminCompanyAdminContactMobilePhoneNumber: toOptionalString(
        administrator.mobilePhone,
      ),
      cpaAdminCompanyAdminContactTitle: toOptionalString(administrator.title),
      cpaAdminCompanyAdminContactNumber: toOptionalString(
        administrator.companyPhone,
      ),
      cpaAdminCompanyAdminContactNumberExtension: toOptionalString(
        administrator.ext,
      ),
      primaryContactMobilePhoneNumber: toOptionalString(
        primaryVoting.mobilePhone,
      ),
      primaryContactName: toOptionalString(primaryVoting.name),
      primaryContactTitle: toOptionalString(primaryVoting.title),
      primaryContactEmail: toOptionalString(primaryVoting.email),
      primaryContactPhoneNumber: toOptionalString(primaryVoting.companyPhone),
      primaryContactPhoneNumberExtension: toOptionalString(primaryVoting.ext),
      cpaEmployeeAdoptionContactMobilePhoneNumber: toOptionalString(
        employeeAdoption.mobilePhone,
      ),
      cpaEmployeeAdoptionContactName: toOptionalString(employeeAdoption.name),
      cpaEmployeeAdoptionContactEmail: toOptionalString(employeeAdoption.email),
      cpaEmployeeAdoptionContactTitle: toOptionalString(employeeAdoption.title),
      cpaEmployeeAdoptionContactNumber: toOptionalString(
        employeeAdoption.companyPhone,
      ),
      cpaEmployeeAdoptionContactNumberExtension: toOptionalString(
        employeeAdoption.ext,
      ),
      lifecycleStatus,
      cmpExtension: {
        accountsPayableContactMobilePhoneNumber: toOptionalString(
          accountsPayable.mobilePhone,
        ),
        accountsPayableContactName: toOptionalString(accountsPayable.name),
        accountsPayableContactTitle: toOptionalString(accountsPayable.title),
        accountsPayableContactEmail: toOptionalString(accountsPayable.email),
        accountsPayableContactNumber: toOptionalString(
          accountsPayable.companyPhone,
        ),
        accountsPayableContactNumberExtension: toOptionalString(
          accountsPayable.ext,
        ),
        accountsPayableBillingAddress: {
          streetAddress: toOptionalString(
            formData.billingAddress.streetAddress,
          ),
          city: toOptionalString(formData.billingAddress.city),
          province: toOptionalString(formData.billingAddress.provinceOrState),
          postalCode: toOptionalString(formData.billingAddress.postalOrZipCode),
          country: toOptionalString(formData.billingAddress.country),
        },
        marketingCommunicationContactMobilePhoneNumber: toOptionalString(
          marketingCommunication.mobilePhone,
        ),
        marketingCommunicationContactName: toOptionalString(
          marketingCommunication.name,
        ),
        marketingCommunicationContactTitle: toOptionalString(
          marketingCommunication.title,
        ),
        marketingCommunicationContactEmail: toOptionalString(
          marketingCommunication.email,
        ),
        marketingCommunicationContactNumber: toOptionalString(
          marketingCommunication.companyPhone,
        ),
        marketingCommunicationContactNumberExtension: toOptionalString(
          marketingCommunication.ext,
        ),
        hrEmployeeEngagementContactMobilePhoneNumber: toOptionalString(
          hrEmployeeEngagement.mobilePhone,
        ),
        hrEmployeeEngagementContactName: toOptionalString(
          hrEmployeeEngagement.name,
        ),
        hrEmployeeEngagementContactTitle: toOptionalString(
          hrEmployeeEngagement.title,
        ),
        hrEmployeeEngagementContactEmail: toOptionalString(
          hrEmployeeEngagement.email,
        ),
        hrEmployeeEngagementContactNumber: toOptionalString(
          hrEmployeeEngagement.companyPhone,
        ),
        hrEmployeeEngagementContactNumberExtension: toOptionalString(
          hrEmployeeEngagement.ext,
        ),
        salesBusinessDevelopmentContactMobilePhoneNumber: toOptionalString(
          sales.mobilePhone,
        ),
        salesBusinessDevelopmentContactName: toOptionalString(sales.name),
        salesBusinessDevelopmentContactTitle: toOptionalString(sales.title),
        salesBusinessDevelopmentContactEmail: toOptionalString(sales.email),
        salesBusinessDevelopmentContactNumber: toOptionalString(
          sales.companyPhone,
        ),
        salesBusinessDevelopmentContactNumberExtension: toOptionalString(
          sales.ext,
        ),
        chamberPerksAppHeardAbout: toOptionalString(
          formData.insights.chamberPerksAppHeardAbout,
        ),
        chamberPerksAppDownloaded: toOptionalString(
          formData.insights.chamberPerksAppDownloaded,
        ),
        boardDirectorInterest: toOptionalString(
          formData.insights.boardDirectorInterest,
        ),
        committeeInterest: toOptionalString(
          formData.insights.committeeInterest,
        ),
        governanceGroupIds: formData.insights.governanceGroupIds,
        referralTracking: referralTracking.map((referral) => ({
          id: referral.id,
          referralDate: toOptionalString(referral.referralDate),
          becameMemberDate: toOptionalString(referral.becameMemberDate),
          companyReferralName: toOptionalString(referral.companyReferralName),
          contactName: toOptionalString(referral.contactName),
          email: toOptionalString(referral.email),
          contactNumber: toOptionalString(referral.contactNumber),
          contactNumberExtension: toOptionalString(
            referral.contactNumberExtension,
          ),
        })),
        affiliatedChamberMember: {
          isAffiliatedChamberMember:
            formData.affiliatedChamberMember.isAffiliatedChamberMember,
          chamberName: toOptionalString(
            formData.affiliatedChamberMember.chamberName,
          ),
        },
        affinityProgramEnrollments: formData.affinityProgramEnrollments.map(
          (enrollment) => ({
            id: enrollment.id,
            affinityProgramPartnerId: toOptionalString(
              enrollment.affinityProgramPartnerId,
            ),
            affinityProgramPartnerName: toOptionalString(
              enrollment.affinityProgramPartnerName,
            ),
            idNumber: toOptionalString(enrollment.idNumber),
            startDate: toOptionalString(enrollment.startDate),
            billingOption: toOptionalString(enrollment.billingOption),
            status: toOptionalString(enrollment.status),
            notes: toOptionalString(enrollment.notes),
          }),
        ),
        association: {
          isAssociateCompanyMember:
            formData.association.isAssociateCompanyMember,
          primaryMemberCompanyId: toOptionalString(
            formData.association.primaryMemberCompanyId,
          ),
        },
        membership: {
          ...(usesExplicitThirdPartyBillingSnapshot
            ? {
                thirdPartyBillingSnapshot: {
                  customerName:
                    explicitThirdPartyBillingSnapshot.customerName.trim(),
                  contactName:
                    explicitThirdPartyBillingSnapshot.contactName.trim() ||
                    undefined,
                  contactEmail:
                    explicitThirdPartyBillingSnapshot.contactEmail.trim(),
                  contactPhone:
                    explicitThirdPartyBillingSnapshot.contactPhone.trim() ||
                    undefined,
                  addressLine1:
                    explicitThirdPartyBillingSnapshot.addressLine1.trim(),
                  addressLine2:
                    explicitThirdPartyBillingSnapshot.addressLine2.trim() ||
                    undefined,
                  city: explicitThirdPartyBillingSnapshot.city.trim(),
                  province: explicitThirdPartyBillingSnapshot.province.trim(),
                  postalCode:
                    explicitThirdPartyBillingSnapshot.postalCode.trim(),
                  country: explicitThirdPartyBillingSnapshot.country.trim(),
                },
              }
            : {}),
          membershipTypeId: toOptionalString(
            formData.membership.membershipTypeId,
          ),
          selectedAdditionalCostIds:
            formData.membership.selectedAdditionalCostIds,
          paymentSchedule,
          membershipDiscount: {
            enabled: membershipDiscountEnabled,
            code: membershipDiscountEnabled
              ? toOptionalString(membershipDiscount.code)
              : undefined,
            description: membershipDiscountEnabled
              ? toOptionalString(membershipDiscount.description)
              : undefined,
            amountInCents: membershipDiscountEnabled
              ? Math.round(getNumber(membershipDiscount.amount) * 100)
              : 0,
          },
          paymentResponsibility: selectedPaymentResponsibility,
          isFreeLifetime:
            formData.membership.billingResponsibility === "freeLifetime",
          thirdPartyPayerName: selectedMembershipIsThirdPartyPaid
            ? toOptionalString(selectedThirdPartyBilling.thirdPartyPayerName)
            : null,
          thirdPartyPaymentNotes: selectedMembershipIsThirdPartyPaid
            ? toOptionalString(selectedThirdPartyBilling.thirdPartyPaymentNotes)
            : null,
          fullTimeEmployees: getNumber(formData.membership.fullTimeEmployees),
          partTimeEmployees: getNumber(formData.membership.partTimeEmployees),
          paymentChoice: buildPaymentChoice(),
        },
        companyDetails: buildCreateMemberCompanyDetailsInput(
          formData.companyDetails,
          { showCharityRegistrationNumber, legacyBoardOrCommitteeInterest },
        ),
        applicationMetadata: {
          dashboardAccessRequestedContactIds: getCreateMemberDashboardAccessRequests(formData),
          country: toOptionalString(formData.address.country),
          primaryVotingSameAsMainCompanyContact:
            primaryVoting.sameAsMainCompanyContact,
          administratorSameAsCompanyPrimaryContact:
            administrator.sameAsCompanyPrimaryContact,
          accountsPayableSameAsCompanyPrimaryContact:
            accountsPayable.sameAsCompanyPrimaryContact,
          employeeAdoptionSameAsCompanyPrimaryContact:
            employeeAdoption.sameAsCompanyPrimaryContact,
          marketingCommunicationSameAsCompanyPrimaryContact:
            marketingCommunication.sameAsCompanyPrimaryContact,
          hrEmployeeEngagementSameAsCompanyPrimaryContact:
            hrEmployeeEngagement.sameAsCompanyPrimaryContact,
          marketingSameAsCompanyPrimaryContact:
            administrator.sameAsCompanyPrimaryContact,
          hrSameAsCompanyPrimaryContact:
            employeeAdoption.sameAsCompanyPrimaryContact,
          salesSameAsCompanyPrimaryContact: sales.sameAsCompanyPrimaryContact,
          communicationPreferences: formData.insights.communicationPreferences,
        },
        profile: {
          internalNotes: toOptionalString(formData.profile.internalNotes),
          customFields: formData.profile.customFields
            .filter((field) => field.fieldName.trim())
            .map((field) => ({
              id: field.id,
              fieldName: field.fieldName.trim(),
              value: field.value,
            })),
        },
      },
    };
  };

  const handleBackendDuplicateMatches = (
    matches: Pick<
      CmpDuplicateMatchReturn,
      "id" | "companyName" | "lifecycleStatus" | "email" | "matchReasons"
    >[],
  ) => {
    setDuplicateMatchesToShow(mapDuplicateMatches(matches));
    setDuplicateDialogOpen(true);
  };

  const openDuplicateExistingRecord = () => {
    if (!duplicateRecordId) {
      return;
    }

    setDuplicateDialogOpen(false);
    setDuplicateMatchesToShow([]);
    setFormStateWithBaseline((currentState) => ({
      ...currentState,
      savedBaseline: currentState.current,
    }));
    storeUpdateMemberNavigationMemberId(duplicateRecordId);
    navigate(RoutePaths.chamberAdmin_members_update, {
      state: getUpdateMemberNavigationState(duplicateRecordId),
    });
  };

  const setValidationWarning = (message: string) => {
    const warning = { severity: "warning" as const, message };

    setStatusMessage(warning);
    setFooterStatusMessage(warning);
  };

  const requestMemberLoginEmailConflictDecision = (
    conflict: MemberLoginEmailConflict,
  ) =>
    new Promise<MemberLoginEmailConflictDecision>((resolve) => {
      const requestId = nextMemberLoginEmailConflictRequestIdRef.current + 1;
      nextMemberLoginEmailConflictRequestIdRef.current = requestId;

      // The outer single-submit guard makes overlap impossible. This fallback
      // still settles an older request before a newer dialog can replace it.
      pendingMemberLoginEmailConflictDecisionRef.current?.resolve("cancel");
      pendingMemberLoginEmailConflictDecisionRef.current = {
        requestId,
        resolve,
      };
      setMemberLoginEmailRetryInFlight(false);
      setMemberLoginEmailConflictDialog({
        requestId,
        checkedLoginEmail: conflict.checkedLoginEmail,
        suggestedLoginEmail: conflict.suggestedLoginEmail,
      });
    });

  const resolveMemberLoginEmailConflictDecision = (
    decision: MemberLoginEmailConflictDecision,
  ) => {
    const dialog = memberLoginEmailConflictDialog;
    const pendingDecision = pendingMemberLoginEmailConflictDecisionRef.current;

    if (
      !dialog ||
      !pendingDecision ||
      pendingDecision.requestId !== dialog.requestId
    ) {
      return;
    }

    pendingMemberLoginEmailConflictDecisionRef.current = null;
    if (decision === "use-alternative") {
      setMemberLoginEmailRetryInFlight(true);
    } else {
      setMemberLoginEmailConflictDialog(null);
    }
    pendingDecision.resolve(decision);
  };

  const handleSubmitApplication = async () => {
    if (!chamberSettings) {
      setStatusMessage({
        severity: "warning",
        message:
          `${noun.Singular} membership settings are still loading. Try again in a moment.`,
      });
      return;
    }

    if (hasNoConfiguredMembershipModels) {
      setStatusMessage({
        severity: "warning",
        message:
          `You cannot create a member yet because no membership type is turned on. Turn on at least one membership type in ${noun.Singular} Set Up > Membership Settings, then come back.`,
      });
      return;
    }

    if (!validateApplication()) {
      const directoryStepErrors = getDirectoryStepErrors();
      const contactStepErrors = getContactStepErrors();
      const insightsStepErrors = getInsightsStepErrors();
      const hasDirectoryStepErrors =
        Object.keys(directoryStepErrors).length > 0;
      const hasContactStepErrors = Object.keys(contactStepErrors).length > 0;
      const hasInsightsStepErrors = Object.keys(insightsStepErrors).length > 0;

      const stepWithErrors = hasDirectoryStepErrors
        ? 1
        : hasContactStepErrors
          ? 2
          : hasInsightsStepErrors
            ? 3
            : 4;
      setActiveCreateMemberStep(stepWithErrors);
      scrollToCreateMemberTop();
      if (!hasDirectoryStepErrors && hasContactStepErrors) showContactStepDialog();
      setValidationWarning(
        `Fix the fields marked in red on Step ${stepWithErrors}, ${createMemberSteps[stepWithErrors - 1].title}. Then select Create Member/Preview Invoice.`,
      );
      return;
    }

    if (!chamberId) {
      setStatusMessage({
        severity: "warning",
        message: `Your ${noun.singular} details are still loading. Wait a moment, then try again.`,
      });
      return;
    }

    // This duplicates the visible validation as a final GraphQL-boundary
    // guard. An empty or ambiguous schedule can never reach the mutation.
    if (createPaymentScheduleMutationGate.blocked) {
      setErrors((current) => ({
        ...current,
        paymentSchedule: createPaymentScheduleMutationGate.error,
      }));
      setActiveCreateMemberStep(4);
      setValidationWarning(
        "Select a Membership Payment Option, then select Create Member/Preview Invoice.",
      );
      return;
    }

    // Commit only the exact snapshot sent to the backend. If a user changes a
    // field while the request is in flight, that later edit must stay dirty.
    const submittedFormData = formData;

    await runSingleMemberApplicationSubmission({
      inFlightRef: memberApplicationSubmissionInFlightRef,
      submit: async () => {
        setApplicationSubmissionInFlight(true);

        try {
          setCreatedInvoicePaymentRequestCapabilities([]);
          setInvoiceDeliveryFeedback(null);
          const updateMemberReturnUrl = `${window.location.origin}${RoutePaths.chamberAdmin_members_update}`;
          const applicationInput = {
            chamberId,
            initialStaffNote: formData.initialStaffNote || undefined,
            data: buildMemberInput(
              CmpMemberLifecycleStatusEnum.Pending,
              createPaymentScheduleMutationGate.paymentSchedule,
            ),
            ...(convertibleSourceProspect
              ? { sourceProspectId: convertibleSourceProspect.id }
              : {}),
            ...(prefillMemberId ? { sourceMemberId: prefillMemberId } : {}),
            isCmpMemberModuleTestData: shouldMarkCmpModuleTestData,
            stripeCancelUrl: updateMemberReturnUrl,
            stripeSuccessUrl: updateMemberReturnUrl,
          };
          const authSession = await fetchAuthSession();
          const idTokenSubject = authSession.tokens?.idToken?.payload?.sub;
          const applicationActorId =
            authSession.userSub ||
            (typeof idTokenSubject === "string" ? idTokenSubject : "");
          const loginAvailabilityFlow =
            await runMemberLoginAvailabilityRetryFlow({
              initialInput: applicationInput,
              submit: async (applicationInputForAttempt) => {
                // The retry input contains the exact approved suggestion, so
                // preparation must run again to mint a new fingerprinted
                // operation instead of reusing the first request identity.
                const preparedSubmission =
                  await prepareMemberApplicationSubmissionInput({
                    actorId: applicationActorId,
                    applicationInput: applicationInputForAttempt,
                    chamberId,
                  });
                const mutationFlow =
                  await runCreatedMemberApplicationMutationFlow({
                    input: preparedSubmission.input,
                    submit: async (input) => {
                      const result = await submitCmpMemberApplication({
                        variables: { input },
                      });

                      return result.data?.submitCmpMemberApplication;
                    },
                  });

                return { ...mutationFlow, preparedSubmission };
              },
              getSubmissionEvidence: ({ mutationResult }) => mutationResult,
              getInputLoginEmail: (input) => input.data.adminConsoleLoginEmail,
              applySuggestedLoginEmail: applySuggestedAdministratorLoginEmail,
              requestConfirmation: requestMemberLoginEmailConflictDecision,
            });

          if (loginAvailabilityFlow.status === "cancelled") {
            return;
          }

          const {
            mutationResult,
            paymentRequestResolution,
            preparedSubmission,
          } = loginAvailabilityFlow.result;

          clearCompletedMemberApplicationOperation({
            operationId: preparedSubmission.operationId,
            storageKey: preparedSubmission.storageKey,
          });

          if (mutationResult.blockedByDuplicate) {
            handleBackendDuplicateMatches(mutationResult.duplicateMatches);
            return;
          }

          try { if (browserDraftKey) sessionStorage.removeItem(browserDraftKey); }
          catch { setDraftNotice("The member was created. A saved copy of this form is still in this browser. Do not use it to create the member again."); }
          storeUpdateMemberNavigationMemberId(mutationResult.member?.id);
          const usedSuggestedLoginEmail =
            loginAvailabilityFlow.usedSuggestedLoginEmail;
          const committedFormData = usedSuggestedLoginEmail
            ? applySuggestedAdministratorEmailToFormState(
                submittedFormData,
                usedSuggestedLoginEmail,
              )
            : submittedFormData;
          setFormStateWithBaseline((currentState) => {
            const currentAdministratorEmail =
              currentState.current.contacts.administrator.email;
            const submittedAdministratorEmail =
              submittedFormData.contacts.administrator.email;
            return {
              current:
                usedSuggestedLoginEmail &&
                currentAdministratorEmail === submittedAdministratorEmail
                  ? applySuggestedAdministratorEmailToFormState(
                      currentState.current,
                      usedSuggestedLoginEmail,
                    )
                  : currentState.current,
              savedBaseline: committedFormData,
            };
          });
          setPreviewStripeCheckoutUrl(mutationResult.stripeCheckout?.url || "");
          // BILL-003/spec §8: the backend returns a second full-amount add-on
          // invoice whenever the committed application selected add-ons.
          const nextInvoicePreview = mutationResult.invoice || null;
          const nextAddOnInvoicePreview = mutationResult.addOnInvoice || null;

          setCreatedInvoicePaymentRequestCapabilities(
            mutationResult.paymentRequestCapabilities,
          );
          setInvoicePreview(nextInvoicePreview);
          setAddOnInvoicePreview(nextAddOnInvoicePreview);
          setInvoicePreviewOpen(false);
          // Design create-member success pop-up. Closing it opens the invoice
          // preview when the backend returned an invoice.
          setCreatedSuccess({
            name: submittedFormData.directory.companyName,
            amount:
              typeof nextInvoicePreview?.totalAmountInCents === "number"
                ? formatCurrency(nextInvoicePreview.totalAmountInCents / 100)
                : "",
            hasInvoice: !!nextInvoicePreview,
          });

          const alreadySentInvoices = paymentRequestResolution.alreadySent;

          if (alreadySentInvoices.length > 0) {
            setInvoiceDeliveryFeedback({
              severity: "info",
              message:
                paymentRequestResolution.sendable.length === 0
                  ? "These invoices were already emailed to the member. They will not be sent again."
                  : `The ${getMemberApplicationPaymentRequestTargetLabel(
                      alreadySentInvoices[0].kind,
                    ).toLowerCase()} was already emailed to the member. Select ${getCreatedInvoiceSendButtonLabel(
                      paymentRequestResolution.sendable,
                    )} to email only the invoice that was not sent yet.`,
            });
          }

          // The success pop-up confirms the creation. Keep an inline note only
          // when the committed payment request state needs staff attention.
          let submittedStatusMessage = "";

          if (
            mutationResult.invoice &&
            paymentRequestResolution.sendable.length === 0
          ) {
            if (paymentRequestResolution.blocked.length > 0) {
              submittedStatusMessage =
                "The member and the invoice are saved. This invoice cannot be emailed from this screen. To review it, go to Invoices & Payments > Manage all Invoices & Payments.";
            } else if (paymentRequestResolution.unverified.length > 0) {
              submittedStatusMessage =
                getUnverifiedCreatedInvoiceContinuationCopy({
                  canManageMembers,
                });
            } else if (paymentRequestResolution.alreadySent.length > 0) {
              submittedStatusMessage =
                "The member and the invoice are saved. The invoice was already emailed to the member.";
            } else {
              submittedStatusMessage =
                "The member and the invoice are saved. Nothing is owed on this invoice, so there is nothing to email.";
            }
          }

          setStatusMessage(
            submittedStatusMessage
              ? { severity: "info", message: submittedStatusMessage }
              : null,
          );
          setFooterStatusMessage(null);
        } catch (error) {
          setStatusMessage({
            severity: "error",
            message: getUserFacingErrorText(
              error,
              `We could not confirm that the member was created. Before you try again, open Manage Members and check if this member is already there. If not, select Create Member/Preview Invoice again. If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`,
            ),
          });
        } finally {
          setMemberLoginEmailConflictDialog(null);
          setMemberLoginEmailRetryInFlight(false);
          setApplicationSubmissionInFlight(false);
        }
      },
    });
  };

  const getInvoiceActionTestFlag = (invoice: CmpInvoiceRecordFieldsFragment) =>
    !!invoice.isCmpInvoiceModuleTestData ||
    !!invoice.isCmpMemberModuleTestData ||
    shouldMarkCmpModuleTestData;

  const handleDownloadPreviewInvoicePdf = async () => {
    if (!invoicePreview) {
      return;
    }

    // BILL-003/spec §8: a member with add-ons has TWO invoices (membership
    // invoice + full-amount add-on invoice) on every schedule, so print both
    // PDFs.
    const invoicesToDownload = [
      invoicePreview,
      ...(addOnInvoicePreview ? [addOnInvoicePreview] : []),
    ];

    try {
      for (const invoiceToDownload of invoicesToDownload) {
        const result = await downloadCmpInvoicePdf({
          variables: {
            input: {
              chamberId,
              invoiceId: invoiceToDownload.id,
              isCmpInvoiceModuleTestData:
                getInvoiceActionTestFlag(invoiceToDownload),
            },
          },
        });
        const download = result.data?.downloadCmpInvoicePdf.download;

        if (!download) {
          throw new Error("Invoice PDF download did not return a file.");
        }

        downloadBase64File(download);
      }
    } catch (error) {
      setStatusMessage({
        severity: "error",
        message: getUserFacingErrorText(
          error,
          buildStaffErrorFallback("create the invoice PDF"),
        ),
      });
    }
  };

  // One success confirmation: the pop-up. No duplicate inline banner.
  const showInvoiceSuccess = ({ message }: { message: string }) => {
    setInvoicePreviewOpen(false);
    setInvoiceSuccessMessage(message);
  };

  const handleCloseCreatedSuccess = () => {
    const openPreview = !!createdSuccess?.hasInvoice && !!invoicePreview;

    setCreatedSuccess(null);
    if (openPreview) setInvoicePreviewOpen(true);
  };

  const handleApproveAndSendInvoice = async () => {
    if (!invoicePreview || pendingCreatedInvoiceDeliveryTargets.length === 0) {
      return;
    }

    const deliveryResult = await runCreatedMemberPaymentRequestDeliveryFlow({
      paymentRequestResolution: createdInvoicePaymentRequestResolution,
      send: async ({ invoice }) => {
        const result = await sendCmpInvoicePaymentRequest({
          variables: {
            input: {
              chamberId,
              invoiceId: invoice.id,
              isCmpInvoiceModuleTestData: getInvoiceActionTestFlag(invoice),
            },
          },
        });

        return result.data?.sendCmpInvoicePaymentRequest;
      },
      wasDeliveryConfirmed: (result) =>
        wasOutboundEmailDeliveryConfirmed(
          result?.actionCompletedSuccessfully,
          result?.outboundEmail,
        ),
      getFailure: ({ kind }, result) =>
        new UserFacingError(
          getOutboundEmailFailureMessage({
            email: result?.outboundEmail,
            label:
              kind === "membership"
                ? "Membership invoice payment request"
                : "Add-on invoice payment request",
          }),
        ),
    });
    const deliveryMessages = deliveryResult.completed.map(
      ({ deliveryResult: result, invoice, kind }) => {
        const outboundEmail = result?.outboundEmail;
        const updatedInvoice = result?.detail?.invoice;
        const invoiceWithCommittedDelivery = updatedInvoice
          ? mergeCommittedMemberInvoiceDelivery({
              mutationInvoice: invoice,
              committedInvoice: updatedInvoice,
            })
          : {
              ...invoice,
              paymentRequestState: CmpPaymentRequestStateEnum.Sent,
              lastPaymentRequestSentAtUtcMilli:
                result?.outboundEmail?.sentAtUtcMilli || Date.now(),
            };

        if (kind === "membership") {
          setInvoicePreview(invoiceWithCommittedDelivery);
        } else {
          setAddOnInvoicePreview(invoiceWithCommittedDelivery);
        }

        const label =
          kind === "membership"
            ? "Membership invoice payment request"
            : "Add-on invoice payment request";

        return outboundEmail
          ? getOutboundEmailSuccessMessage({ email: outboundEmail, label })
          : getOutboundEmailFailureMessage({ email: outboundEmail, label });
      },
    );

    if (deliveryResult.failure) {
      const message = [
        ...deliveryMessages,
        getUserFacingErrorText(
          deliveryResult.failure.error,
          buildStaffErrorFallback("email the invoice"),
        ),
        // The button label after this failure names the invoices still unsent.
        `Select ${getCreatedInvoiceSendButtonLabel(
          pendingCreatedInvoiceDeliveryTargets.filter(
            (target) =>
              !deliveryResult.completed.some(
                (completed) => completed.kind === target.kind,
              ),
          ),
        )} to try again. An invoice that was already sent is not sent again.`,
      ].join(" ");

      setInvoiceDeliveryFeedback({ severity: "error", message });
      setStatusMessage({ severity: "warning", message });
      return;
    }

    const message = deliveryMessages.join(" ");

    if (!message) {
      setInvoiceDeliveryFeedback({
        severity: "info",
        message:
          "These invoices were already emailed to the member. We did not send them again.",
      });
      return;
    }

    showInvoiceSuccess({ message });
  };

  const scrollToCreateMemberTop = () => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const scrollableParent = getScrollableParent(
          createMemberRootRef.current,
        );

        scrollableParent?.scrollTo({ top: 0, behavior: "smooth" });
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
    });
  };

  const handleNextFromDirectoryStep = () => {
    if (!validateDirectoryStep()) {
      setValidationWarning(
        "Review the highlighted Step 1 fields before continuing.",
      );
      return;
    }

    setStatusMessage(null);
    setFooterStatusMessage(null);
    setActiveCreateMemberStep(2);
    scrollToCreateMemberTop();
  };

  const showContactStepDialog = () => {
    const rows = getCreateMemberContactDialogRows(formData);
    if (!rows.length) return false;
    setContactAccessTried(true);
    setMissingFieldRows(rows);
    return true;
  };

  const handleNextFromContactStep = () => {
    if (!validateContactStep()) {
      if (!showContactStepDialog()) setValidationWarning(
        "Review the highlighted Step 2 fields before continuing.",
      );
      return;
    }

    setContactAccessTried(false);
    setStatusMessage(null);
    setFooterStatusMessage(null);
    setActiveCreateMemberStep(3);
    scrollToCreateMemberTop();
  };

  const handleNextFromInsightsStep = () => {
    if (!validateInsightsStep()) {
      setValidationWarning(
        "Review the highlighted Step 3 fields before continuing.",
      );
      return;
    }

    setStatusMessage(null);
    setFooterStatusMessage(null);
    setActiveCreateMemberStep(4);
    scrollToCreateMemberTop();
  };

  const handleBackToStepOne = () => {
    setStatusMessage(null);
    setFooterStatusMessage(null);
    setActiveCreateMemberStep(1);
    scrollToCreateMemberTop();
  };

  const handleBackToStepTwo = () => {
    setStatusMessage(null);
    setFooterStatusMessage(null);
    setActiveCreateMemberStep(2);
    scrollToCreateMemberTop();
  };

  const handleBackToStepThree = () => {
    setStatusMessage(null);
    setFooterStatusMessage(null);
    setActiveCreateMemberStep(3);
    scrollToCreateMemberTop();
  };

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (!["image/png", "image/jpeg"].includes(file.type)) {
      setLogoError("Upload a PNG or JPG image.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setLogoError("Logo must be 5MB or smaller.");
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      markDirty();
      setLogoError("");
      updateSection("logo", {
        fileName: file.name,
        previewUrl: reader.result as string,
      });
    };

    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    markDirty();
    setLogoError("");
    updateSection("logo", { fileName: "", previewUrl: "" });
  };

  const handleAffinityEnrollmentDraftChange = (
    field: keyof AffinityProgramEnrollmentDraft,
    value: string,
  ) => {
    setAffinityEnrollmentDraft((current) => ({
      ...current,
      [field]: value,
    }));
    setAffinityEnrollmentError("");
  };

  const handleStartAffinityEnrollmentSave = () => {
    const selectedProgram = availableAffinityProgramPartners.find(
      (program) =>
        program.id === affinityEnrollmentDraft.affinityProgramPartnerId,
    );

    if (!selectedProgram) {
      setAffinityEnrollmentError("Select an Affinity Program Partner.");
      return;
    }

    setAffinityEnrollmentError("");
    setPendingAffinityEnrollment({
      id: createLocalId("affinity-enrollment"),
      affinityProgramPartnerId: selectedProgram.id,
      affinityProgramPartnerName: selectedProgram.companyName,
      idNumber: affinityEnrollmentDraft.idNumber,
      startDate: affinityEnrollmentDraft.startDate,
      billingOption: affinityEnrollmentDraft.billingOption,
      status: "Active",
      notes: affinityEnrollmentDraft.notes,
    });
  };

  const handleConfirmAffinityEnrollment = () => {
    if (!pendingAffinityEnrollment) {
      return;
    }

    markDirty();
    setFormData((current) => ({
      ...current,
      affinityProgramEnrollments: [
        ...current.affinityProgramEnrollments,
        pendingAffinityEnrollment,
      ],
    }));
    setAffinityEnrollmentDraft(createEmptyAffinityProgramEnrollmentDraft());
    setPendingAffinityEnrollment(null);
  };

  const handleUpdateEditingAffinityEnrollment = (
    field: keyof AffinityProgramEnrollment,
    value: string,
  ) => {
    setEditingAffinityEnrollment((current) =>
      current ? { ...current, [field]: value } : current,
    );
  };

  const handleSaveEditingAffinityEnrollment = () => {
    if (!editingAffinityEnrollment) {
      return;
    }

    updateAffinityEnrollment(editingAffinityEnrollment);
    setEditingAffinityEnrollment(null);
  };

  const showCategoryEmptyState =
    !categoriesLoading && !categoriesError && directoryCategories.length === 0;
  const isDirectoryStep = activeCreateMemberStep === 1;
  const isContactStep = activeCreateMemberStep === 2;
  const isInsightsStep = activeCreateMemberStep === 3;
  const isMembershipBillingStep = activeCreateMemberStep === 4;
  const prospectPrefillIsApplying =
    !!convertibleSourceProspect &&
    appliedProspectPrefillIdRef.current !== convertibleSourceProspect.id;

  if (
    prospectIdFromQuery &&
    (!chamberId ||
      (!sourceProspect && prospectDetailLoading) ||
      prospectPrefillIsApplying)
  ) {
    return (
      <div
        className="flex min-h-[240px] items-center justify-center gap-3 text-sm text-gray-700"
        role="status"
      >
        <CircularProgress size={24} />
        Loading prospect details…
      </div>
    );
  }

  const prospectConversionBlock =
    prospectIdFromQuery && prospectDetailError
      ? {
          severity: "error" as const,
          message:
            "The prospect details could not be loaded. Return to Manage Prospects and try again.",
        }
      : prospectIdFromQuery &&
          sourceProspect?.status === CmpProspectStatusEnum.Converted
        ? {
            severity: "info" as const,
            message:
              "This prospect is already a member. To see the member, go to Members > Manage Members.",
          }
        : prospectIdFromQuery && !sourceProspect
          ? {
              severity: "error" as const,
              message:
                "We could not find this prospect. It may have been deleted. Select Return to Manage Prospects and select the prospect again.",
            }
          : null;

  if (prospectConversionBlock) {
    return (
      <div className="mx-auto w-full max-w-[1180px] space-y-5 pt-6">
        <Alert severity={prospectConversionBlock.severity}>
          {prospectConversionBlock.message}
        </Alert>
        <Button
          type="button"
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(RoutePaths.chamberAdmin_prospects_manage)}
          sx={wizardOutlinedButtonSx}
        >
          Return to Manage Prospects
        </Button>
      </div>
    );
  }

  return (
    // Step state is only ever set to the literals 1-4 (see the
    // handleNextFrom*/handleBackTo* handlers), so the old
    // `activeCreateMemberStep <= 4` guard around main/footer/dialogs was
    // vestigial and was dropped in the FormPageShell rewire.
    <>
      <MemberDesignSurface className="member-create-page">
        <FormPageShell
          mainClassName="w-full"
          mainPaddingTopClassName="pt-[24px]"
          contentClassName="space-y-[24px]"
          rootRef={createMemberRootRef}
          headingId={`create-member-step-${activeCreateMemberStep}`}
          heading={createMemberSteps[activeCreateMemberStep - 1]?.title ?? ""}
          beforeMain={
            <UnsavedChangesPrompt
              // Kept on the "save" pop-up (difference from design (15)
              // navFormPage): Create Member has no Save button, and Save
              // Changes is the only way to keep a browser draft.
              when={isDirty && browserDraftFingerprint !== savedDraftFingerprintRef.current}
              onSave={saveBrowserDraft}
              saveError={draftSaveError}
              saving={
                applicationSubmissionBusy ||
                downloadingInvoicePdf ||
                sendingInvoiceRequest
              }
            />
          }
          stepper={
            <MemberStepProgress
              activeStep={activeCreateMemberStep}
              ariaLabel="Create member steps"
              desktopLayout="content"
            />
          }
          alerts={
            <>
              {draftNotice && <Alert severity="info" onClose={() => setDraftNotice("")}>{draftNotice}</Alert>}
              {statusMessage && (
                <Alert
                  severity={statusMessage.severity}
                  className="mb-6"
                  onClose={() => setStatusMessage(null)}
                >
                  {statusMessage.message}
                </Alert>
              )}
              {isMembershipBillingStep && chamberSettingsLoading && (
                <Alert severity="info" className="mb-6">
                  Loading {noun.singular} membership settings for payment and package
                  options.
                </Alert>
              )}
              {isMembershipBillingStep && chamberSettingsError && (
                <Alert severity="warning" className="mb-6">
                  We could not load your membership settings, so the membership
                  and billing options are not shown. Refresh the page to try again.
                </Alert>
              )}
              {isMembershipBillingStep && hasNoConfiguredMembershipModels && (
                <Alert severity="warning" className="mb-6">
                  {`You cannot create a member yet because no membership type is turned on. Turn on at least one membership type in ${noun.Singular} Set Up > Membership Settings, then come back.`}
                </Alert>
              )}
              {prefillMemberId && (
                <Alert severity="info" className="mb-6">
                  This form was filled in from an existing member. Review the
                  details, then select Create Member/Preview Invoice on Step 4.
                </Alert>
              )}
              {convertibleSourceProspect && (
                <Alert severity="info" className="mb-6">
                  This form was filled in from the prospect{" "}
                  <strong>“{convertibleSourceProspect.companyName}”</strong>.
                  The prospect stays in Manage Prospects until you create the
                  member.
                </Alert>
              )}
            </>
          }
          actionBarAlert={
            footerStatusMessage && (
              <Alert
                severity={footerStatusMessage.severity}
                className="mb-4"
                onClose={() => setFooterStatusMessage(null)}
              >
                {footerStatusMessage.message}
              </Alert>
            )
          }
          actionBar={
            isDirectoryStep ? (
              <>
                {/* Step 1 mock footer: white bordered Cancel button at left. */}
                <Button
                  type="button"
                  variant="outlined"
                  component={RouterLink}
                  to={
                    convertibleSourceProspect
                      ? RoutePaths.chamberAdmin_prospects_manage
                      : RoutePaths.chamberAdmin_members_manage
                  }
                  sx={{
                    ...wizardOutlinedButtonSx,
                    borderColor: "#C7C7C7",
                    borderRadius: "8px",
                    fontSize: 13,
                    fontWeight: 600,
                    letterSpacing: "normal",
                    lineHeight: "17px !important",
                    minWidth: 0,
                    padding: "12px 18px",
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="contained"
                  endIcon={<CreateMemberNavigationArrow />}
                  onClick={handleNextFromDirectoryStep}
                  sx={{
                    ...wizardGoldButtonSx,
                    letterSpacing: "normal",
                    lineHeight: "17px !important",
                    minWidth: 0,
                    textAlign: "left",
                  }}
                >
                  <span>
                    Next Step:{"\u00a0 "}Member Contacts
                  </span>
                </Button>
              </>
            ) : isContactStep ? (
              <>
                <Button
                  type="button"
                  variant="outlined"
                  startIcon={<CreateMemberNavigationArrow previous />}
                  onClick={handleBackToStepOne}
                  sx={{
                    ...wizardOutlinedButtonSx,
                    minWidth: 260,
                    justifyContent: "flex-start",
                    textAlign: "left",
                  }}
                >
                  <span className="leading-5">
                    Previous:{"\u00a0 "}Member Directory Information
                  </span>
                </Button>
                <Button
                  type="button"
                  variant="contained"
                  endIcon={<CreateMemberNavigationArrow />}
                  onClick={handleNextFromContactStep}
                  sx={{
                    ...wizardGoldButtonSx,
                    minWidth: 220,
                    textAlign: "left",
                  }}
                >
                  <span className="leading-5">
                    Next Step:{"\u00a0 "}Member Insights
                  </span>
                </Button>
              </>
            ) : isInsightsStep ? (
              <>
                <Button
                  type="button"
                  variant="outlined"
                  startIcon={<CreateMemberNavigationArrow previous />}
                  onClick={handleBackToStepTwo}
                  sx={{
                    ...wizardOutlinedButtonSx,
                    minWidth: 260,
                    justifyContent: "flex-start",
                    textAlign: "left",
                  }}
                >
                  <span className="leading-5">
                    Previous:{"\u00a0 "}Member Contacts
                  </span>
                </Button>
                <Button
                  type="button"
                  variant="contained"
                  endIcon={<CreateMemberNavigationArrow />}
                  onClick={handleNextFromInsightsStep}
                  sx={{
                    ...wizardGoldButtonSx,
                    minWidth: 260,
                    textAlign: "left",
                  }}
                >
                  <span className="leading-5">
                    Next Step:{"\u00a0 "}Membership Type and Billing
                  </span>
                </Button>
              </>
            ) : isMembershipBillingStep ? (
              <>
                <Button
                  type="button"
                  variant="outlined"
                  startIcon={<CreateMemberNavigationArrow previous />}
                  onClick={handleBackToStepThree}
                  disabled={applicationSubmissionBusy}
                  sx={{
                    ...wizardOutlinedButtonSx,
                    minWidth: 240,
                    justifyContent: "flex-start",
                    textAlign: "left",
                  }}
                >
                  <span className="leading-5">
                    Previous:{"\u00a0 "}Member Insights
                  </span>
                </Button>
                <Button
                  type="button"
                  variant="contained"
                  onClick={handleSubmitApplication}
                  disabled={
                    applicationSubmissionBusy ||
                    hasNoConfiguredMembershipModels ||
                    !chamberSettings
                  }
                  startIcon={
                    applicationSubmissionBusy ? (
                      <CircularProgress size={20} sx={{ color: "white" }} />
                    ) : undefined
                  }
                  sx={{ ...wizardGoldButtonSx, minWidth: 260 }}
                >
                  {applicationSubmissionBusy
                    ? "Creating..."
                    : "Create Member/Preview Invoice"}
                </Button>
              </>
            ) : null
          }
        >
          {isInsightsStep && (
            <>
              <SectionShell
                id="member-insights"
                title="Member Insights"
                description={
                  <span className="flex flex-col gap-[6px]">
                    <span>Enter the details below to help your {noun.singular} build stronger relationships, improve member retention, identify interests, and measure member participation.</span>
                    <span className="font-bold leading-[normal]">This section is for internal use only.</span>
                  </span>
                }
                variant="design"
                contentGap={18}
              >
                <SearchableSelectField
                  id="year-member-was-established"
                  label="Year member was established"
                  value={formData.companyDetails.yearEstablished}
                  onChange={(value) =>
                    updateSection("companyDetails", { yearEstablished: value })
                  }
                  options={getMemberEstablishedYearOptions(
                    formData.companyDetails.yearEstablished,
                  )}
                  error={getError(errors, "yearEstablished")}
                  placeholder="Select year member was established"
                  searchPlaceholder="Search Year"
                />
                <MemberOwnershipDiversityField
                  selected={formData.companyDetails.memberOwnershipDiversity}
                  onChange={(memberOwnershipDiversity) =>
                    updateSection("companyDetails", { memberOwnershipDiversity })
                  }
                />
                <SearchableSelectField
                  id="how-did-member-hear-about-us"
                  label="How did member hear about us?"
                  value={formData.companyDetails.howDidYouHearAboutUs.length === 1 ? formData.companyDetails.howDidYouHearAboutUs[0] : ""}
                  helperText={formData.companyDetails.howDidYouHearAboutUs.length > 1 ? `Saved answers: ${formData.companyDetails.howDidYouHearAboutUs.join(", ")}. Select one answer to replace them.` : undefined}
                  onChange={(value) =>
                    updateSection("companyDetails", {
                      howDidYouHearAboutUs: value ? [value] : [],
                    })
                  }
                  options={[
                    ...(formData.companyDetails.howDidYouHearAboutUs.length === 1
                      ? [{ value: "", label: "Not specified" }]
                      : []),
                    ...hearAboutOptions.map((option) => ({ value: option, label: option })),
                  ]}
                  placeholder="Select how you heard about us"
                />

                <SearchableSelectField
                  id="primary-reason-member-joined"
                  label="Primary reason member joined"
                  value={formData.companyDetails.primaryReasonsForJoining.length === 1 ? formData.companyDetails.primaryReasonsForJoining[0] : ""}
                  helperText={formData.companyDetails.primaryReasonsForJoining.length > 1 ? `Saved answers: ${formData.companyDetails.primaryReasonsForJoining.join(", ")}. Select one answer to replace them.` : undefined}
                  onChange={(value) =>
                    updateSection("companyDetails", {
                      primaryReasonsForJoining: value ? [value] : [],
                    })
                  }
                  options={reasonForJoiningOptions.map((option) => ({
                    value: option,
                    label: option,
                  }))}
                  placeholder="Select your reason for joining"
                />

                <MultiOptionGroup
                  label="The social media platform this member is active on?"
                  options={socialPlatformOptions}
                  selected={formData.companyDetails.activeSocialPlatforms}
                  onChange={(value) =>
                    updateSection("companyDetails", {
                      activeSocialPlatforms: updateMemberSocialPlatforms(formData.companyDetails.activeSocialPlatforms, value),
                    })
                  }
                  columns="inline"
                />
                <RadioChoiceGroup
                  label="Have you heard about the Chamber Perks App™?"
                  options={yesNoOptions}
                  value={formData.insights.chamberPerksAppHeardAbout}
                  onChange={(value) =>
                    updateSection("insights", {
                      chamberPerksAppHeardAbout: value,
                    })
                  }
                />
                <RadioChoiceGroup
                  label="Have you downloaded the Chamber Perks App™?"
                  options={yesNoOptions}
                  value={formData.insights.chamberPerksAppDownloaded}
                  onChange={(value) =>
                    updateSection("insights", {
                      chamberPerksAppDownloaded: value,
                    })
                  }
                />
                <InterestOptionMatrix
                  label={`Select if member has shown interest in applying to become a ${getCmpBoardWording(noun).boardDirector} or join committees?`}
                  rows={[
                    {
                      id: "board-director-interest",
                      // Chamber keeps its current label; a board of trade
                      // reads "board of trade director" (getCmpBoardWording).
                      label: noun.isBoardOfTrade
                        ? `Interested in becoming a ${getCmpBoardWording(noun).boardDirector}`
                        : `Interested in becoming a ${noun.singular} board of directors`,
                      affirmativeValue:
                        "Interested in becoming a chamber board director",
                      value: formData.insights.boardDirectorInterest,
                      onChange: (value) =>
                        updateSection("insights", {
                          boardDirectorInterest: value,
                        }),
                    },
                    {
                      id: "committee-interest",
                      label: "Interested in joining a committee",
                      affirmativeValue: "Interested in joining a committee",
                      value: formData.insights.committeeInterest,
                      onChange: (value) =>
                        updateSection("insights", {
                          committeeInterest: value,
                        }),
                    },
                  ]}
                />
                <MultiOptionGroup
                  label={`Select the method(s) this member wishes to receive communication from our ${noun.singular}.`}
                  options={communicationPreferenceOptions}
                  selected={formData.insights.communicationPreferences}
                  onChange={(value) =>
                    updateSection("insights", {
                      communicationPreferences: value,
                    })
                  }
                  error={getError(errors, "communicationPreferences")}
                  required
                  columns="single"
                />
              </SectionShell>

              <SectionShell
                id="member-referral-tracking"
                title="Member Referral Tracking"
                description={`Track member referrals, monitor conversion rates, and recognize members who help grow your ${noun.singular} community.`} descriptionLineHeight="normal"
                variant="design"
                contentGap={18}
              >
                <div className="space-y-6">
                  {formData.referralTracking.map((referral) => (
                    <div
                      key={referral.id}
                      className="grid grid-cols-1 gap-5 md:grid-cols-2"
                    >
                      <FormCalendarDateField
                        id={`${referral.id}-referral-date`}
                        label="Member Referral Date"
                        value={referral.referralDate}
                        onChange={(value) =>
                          updateReferralTracking(
                            referral.id,
                            "referralDate",
                            value,
                          )
                        }
                        placeholder="Enter Referral Date"
                        iconPosition="right"
                      />
                      <FormCalendarDateField
                        id={`${referral.id}-became-member-date`}
                        label="Became A Member Date (Will Automatically Populate If Member Joins)"
                        value={referral.becameMemberDate}
                        onChange={(value) =>
                          updateReferralTracking(
                            referral.id,
                            "becameMemberDate",
                            value,
                          )
                        }
                        placeholder="Date the Member Referral Joined"
                        iconPosition="right"
                      />
                      <FormTextField
                        id={`${referral.id}-company-referral-name`}
                        label="Company Referral Name"
                        value={referral.companyReferralName}
                        onChange={(value) =>
                          updateReferralTracking(
                            referral.id,
                            "companyReferralName",
                            value,
                          )
                        }
                        placeholder="Enter Company Referral Name"
                        error={getError(
                          errors,
                          `referralTracking.${referral.id}.companyReferralName`,
                        )}
                        required
                      />
                      <FormTextField
                        id={`${referral.id}-contact-name`}
                        label="Contact Name"
                        value={referral.contactName}
                        onChange={(value) =>
                          updateReferralTracking(
                            referral.id,
                            "contactName",
                            value,
                          )
                        }
                        placeholder="Enter Contact Name"
                      />
                      <div className="grid grid-cols-1 gap-[20px] md:col-span-2 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_76px]">
                        <FormTextField
                          id={`${referral.id}-email`}
                          label="Email"
                          value={referral.email}
                          onChange={(value) =>
                            updateReferralTracking(referral.id, "email", value)
                          }
                          placeholder="Enter Contact Email"
                          error={getError(
                            errors,
                            `referralTracking.${referral.id}.email`,
                          )}
                        />
                        <FormPhoneField
                          id={`${referral.id}-contact-number`}
                          label="Contact Number"
                          value={referral.contactNumber}
                          onChange={(value) =>
                            updateReferralTracking(
                              referral.id,
                              "contactNumber",
                              value,
                            )
                          }
                          placeholder="Enter Contact Number"
                        />
                        <FormTextField
                          id={`${referral.id}-contact-number-extension`}
                          label="Ext."
                          value={referral.contactNumberExtension}
                          onChange={(value) =>
                            updateReferralTracking(
                              referral.id,
                              "contactNumberExtension",
                              value,
                            )
                          }
                          placeholder="0000"
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <Button
                  type="button"
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={addReferralTracking}
                  sx={wizardGoldButtonSx}
                >
                  Add Another Referral
                </Button>
              </SectionShell>

              <SectionShell
                id="associate-member-company"
                title="Associate Member Company"
                description="Select if this member is an Associated Member Company and select the Primary Member Company from drop-down below." descriptionLineHeight="normal"
                variant="design"
                contentGap={16}
              >
                <FormControlLabel
                  sx={{ alignItems: "flex-start", margin: 0 }}
                  control={
                    <Checkbox
                      checked={formData.association.isAssociateCompanyMember}
                      onChange={(event) =>
                        updateSection("association", {
                          isAssociateCompanyMember: event.target.checked,
                          primaryMemberCompanyId: event.target.checked
                            ? formData.association.primaryMemberCompanyId
                            : "",
                        })
                      }
                    />
                  }
                  label={
                    <span>
                      <span className="block text-sm font-bold text-gray-950">
                        Associate Member Company
                      </span>
                      <span className="mt-1 block max-w-5xl text-[13px] leading-[1.5] text-black">
                        Associate Members must be affiliated with an active
                        Primary Member company. The Associate company must have
                        a minimum of 50% common ownership with the Primary
                        Member company and must fall under the lower of the two
                        membership fee categories.
                      </span>
                    </span>
                  }
                />
                <div>
                  <SearchableSelectField
                    searchPlaceholder="Search Member Company"
                    id="primary-member-company"
                    label="Please select the affiliated Primary Member company below."
                    value={formData.association.primaryMemberCompanyId}
                    onChange={(value) =>
                      updateSection("association", {
                        primaryMemberCompanyId: value,
                      })
                    }
                    options={primaryMemberCompanyOptions.map((company) => ({
                      value: company.id,
                      label: company.name,
                    }))}
                    placeholder={
                      primaryCompaniesLoading
                        ? "Loading member companies"
                        : "Select the Primary Member Company"
                    }
                    disabled={
                      primaryCompaniesLoading ||
                      primaryMemberCompanyOptions.length === 0
                    }
                    helperText={
                      !primaryCompaniesLoading &&
                      primaryMemberCompanyOptions.length === 0
                        ? "No members to choose from. Only Active members can be a Primary Member Company."
                        : undefined
                    }
                    error={getError(errors, "primaryMemberCompanyId")}
                  />
                </div>
              </SectionShell>

              <SectionShell
                id="affiliated-chamber-member-company"
                title="Affiliated Chamber Member Company"
                description="Select if this member is an Affiliated with another Chamber and enter the chamber name in the box below" descriptionLineHeight="normal"
                variant="design"
                contentGap={16}
              >
                <FormControlLabel
                  sx={{ alignItems: "flex-start", margin: 0 }}
                  control={
                    <Checkbox
                      checked={
                        formData.affiliatedChamberMember
                          .isAffiliatedChamberMember
                      }
                      onChange={(event) =>
                        updateSection("affiliatedChamberMember", {
                          isAffiliatedChamberMember: event.target.checked,
                        })
                      }
                    />
                  }
                  label={
                    <span>
                      <span className="block text-sm font-bold text-gray-950">
                        Affiliated Chamber Member
                      </span>
                      <span className="mt-1 block max-w-5xl text-[13px] leading-[1.5] text-black">
                        This member is affiliated with the chamber below and
                        therefore maybe eligible for special membership pricing
                        with your {noun.singular} if enabled.
                      </span>
                    </span>
                  }
                />
                <div>
                  <FormTextField
                    id="affiliated-chamber-name"
                    label="Enter the Affiliated Chamber of Commerce Name"
                    value={formData.affiliatedChamberMember.chamberName}
                    onChange={(value) =>
                      updateSection("affiliatedChamberMember", {
                        chamberName: value,
                      })
                    }
                    placeholder="Chamber of Commerce Name"
                  />
                </div>
              </SectionShell>

              <SectionShell
                id="affinity-program-partner"
                title="Affinity Program Partner"
                description="Track the Affinity programs this member is enrolled in, including program identification numbers, enrollment dates, billing preferences, and participation status."
                variant="design"
                contentGap={18}
              >
                {affinityProgramsError && (
                  <Alert severity="warning" className="flex flex-col gap-2.5">
                    Unable to load Affinity Program Partners from {noun.Singular} Set
                    Up. Try refreshing before adding a program.
                  </Alert>
                )}
                {!affinityProgramsLoading &&
                  !affinityProgramsError &&
                  affinityProgramOptions.length === 0 && (
                    <Alert severity="info" className="flex flex-col gap-2.5">
                      {`No Affinity Program Partners are set up yet. To add one, go to ${noun.Singular} Set Up > Affinity Program Set Up.`}
                    </Alert>
                  )}
                {affinityEnrollmentError && (
                  <Alert severity="error" className="flex flex-col gap-2.5">
                    {affinityEnrollmentError}
                  </Alert>
                )}
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.1fr)_repeat(3,minmax(0,1fr))]">
                  <SearchableSelectField
                    searchPlaceholder="Search Programs"
                    loading={affinityProgramsLoading}
                    id="affinity-program-partner-select"
                    label="Affinity Program Partner"
                    description="Select from the available programs"
                    value={affinityEnrollmentDraft.affinityProgramPartnerId}
                    onChange={(value) =>
                      handleAffinityEnrollmentDraftChange(
                        "affinityProgramPartnerId",
                        value,
                      )
                    }
                    options={affinityProgramOptions}
                    placeholder={
                      affinityProgramsLoading
                        ? "Loading affinity programs"
                        : "Select an Affinity program"
                    }
                    error={affinityEnrollmentError}
                  />
                  <FormTextField
                    id="affinity-program-id-number"
                    label="ID Number"
                    description="Enter the program identification number."
                    descriptionClassName="mb-1.5 text-xs leading-[1.5] text-[#727272]"
                    value={affinityEnrollmentDraft.idNumber}
                    onChange={(value) =>
                      handleAffinityEnrollmentDraftChange("idNumber", value)
                    }
                    placeholder="Enter ID number"
                  />
                  <FormCalendarDateField
                    id="affinity-program-start-date"
                    label="Start Date"
                    description="Enter the program participation started."
                    value={affinityEnrollmentDraft.startDate}
                    onChange={(value) =>
                      handleAffinityEnrollmentDraftChange("startDate", value)
                    }
                    placeholder="Select Date"
                    iconPosition="right"
                  />
                  <SearchableSelectField
                    searchPlaceholder="Search"
                    id="affinity-program-billing-option"
                    label="Billing Option"
                    description="How this program will be billed to members"
                    value={affinityEnrollmentDraft.billingOption}
                    onChange={(value) =>
                      handleAffinityEnrollmentDraftChange(
                        "billingOption",
                        value,
                      )
                    }
                    options={affinityBillingOptions.map((option) => ({
                      value: option,
                      label: option,
                    }))}
                    placeholder="Select billing option"
                  />
                </div>
                <FormTextField
                  id="affinity-program-notes"
                  label={
                    <>
                      Notes <span className="font-normal">(Optional)</span>
                    </>
                  }
                  description="Additional information about this program."
                  descriptionClassName="mb-1.5 text-xs leading-[1.5] text-[#727272]"
                  value={affinityEnrollmentDraft.notes}
                  onChange={(value) =>
                    handleAffinityEnrollmentDraftChange("notes", value)
                  }
                  placeholder="Enter any notes about this program"
                  multiline
                  rows={3}
                />
                <div className="flex flex-wrap items-center justify-between gap-5">
                  <p className="text-sm leading-6 text-black">
                    Click <strong>Save</strong> to display details below in the
                    Enrolled Affinity Programs table where you can update
                    status, billing options and notes.
                  </p>
                  <Button
                    type="button"
                    variant="contained"
                    className="member-affinity-save"
                    onClick={handleStartAffinityEnrollmentSave}
                    disabled={
                      affinityProgramsLoading ||
                      affinityProgramOptions.length === 0
                    }
                    sx={{
                      backgroundColor: cpaGold,
                      color: "white",
                      fontWeight: 800,
                      textTransform: "none",
                      "&:hover": { backgroundColor: cpaGoldDark },
                    }}
                  >
                    Save
                  </Button>
                </div>

                <div id="enrolled-affinity-programs" className="mt-1.5">
                  <h4 className="text-[17px] font-bold">
                    Enrolled Affinity Programs
                  </h4>
                  <p className="mt-1.5 text-sm leading-[1.55]">
                    Select edit to update and review all Affinity programs this
                    member is currently enrolled in. {noun.Plural} should adjust
                    start date to reflect actual start date for this member if
                    previously enrolled.
                  </p>
                  <div className="mt-[18px]">
                    {formData.affinityProgramEnrollments.length === 0 ? (
                      <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-5 py-6 text-sm text-black">
                        No Affinity Programs have been enrolled for this member
                        yet.
                      </div>
                    ) : (
                      <div className="overflow-x-auto rounded-[10px] border border-[#ECECEC]">
                        <table className="min-w-[900px] w-full text-left text-[13px] text-black">
                          <thead>
                            <tr className="bg-[#F7F7F8] text-[13px] font-bold text-[#0A0A0A]">
                              <th className="px-[14px] py-3">
                                Affinity Program Partner
                              </th>
                              <th className="px-[14px] py-3">ID Number</th>
                              <th className="px-[14px] py-3">Start Date</th>
                              <th className="px-[14px] py-3">
                                Billing Options
                              </th>
                              <th className="px-[14px] py-3">Status</th>
                              <th className="px-[14px] py-3">Notes</th>
                              <th className="px-[14px] py-3 text-right">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {formData.affinityProgramEnrollments.map(
                              (enrollment) => (
                                <tr
                                  key={enrollment.id}
                                  className="border-t border-gray-300 align-top"
                                >
                                  <td className="px-[14px] py-[13px] font-semibold">
                                    {enrollment.affinityProgramPartnerName}
                                  </td>
                                  <td className="px-[14px] py-[13px]">
                                    {enrollment.idNumber || "-"}
                                  </td>
                                  <td className="px-[14px] py-[13px]">
                                    {formatDateInputValueLong(
                                      enrollment.startDate,
                                    )}
                                  </td>
                                  <td className="px-[14px] py-[13px]">
                                    {enrollment.billingOption
                                      ? renderAffinityBillingOptionLabel(
                                          enrollment.billingOption,
                                        )
                                      : "-"}
                                  </td>
                                  <td className="px-[14px] py-[13px]">
                                    <AffinityEnrollmentStatusIndicator
                                      status={enrollment.status}
                                    />
                                  </td>
                                  <td className="px-[14px] py-[13px]">
                                    {enrollment.notes || "-"}
                                  </td>
                                  <td className="px-[14px] py-[13px] text-right">
                                    <Button
                                      type="button"
                                      variant="contained"
                                      size="small"
                                      startIcon={<EditOutlinedIcon />}
                                      onClick={() =>
                                        setEditingAffinityEnrollment(enrollment)
                                      }
                                      sx={wizardGoldButtonSx}
                                    >
                                      Edit
                                    </Button>
                                  </td>
                                </tr>
                              ),
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </SectionShell>
              <SectionShell
                id="create-member-seo-tags"
                title="SEO and AIO Tags"
                description="Add tags that help this member's directory listing get found by search engines and AI tools. Type a tag and press Enter, or select from the suggestions."
                variant="design"
                contentGap={16}
              >
                <TagInputField
                  id="create-member-seo-tags-input"
                  value={formData.directory.seoTags}
                  onChange={(seoTags) =>
                    updateSection("directory", { seoTags })
                  }
                  helperFontSize={12}
                />
              </SectionShell>
              <GovernanceInvolvementCard
                id="member-governance-involvement"
                governance={governanceSettingsData?.getChamberMemberProSettings.governance}
                selected={formData.insights.governanceGroupIds}
                onChange={(governanceGroupIds) => updateSection("insights", { governanceGroupIds })}
                disabled={applicationSubmissionBusy}
                loading={governanceSettingsLoading}
                error={
                  governanceSettingsError
                    ? getUserFacingErrorText(
                        governanceSettingsError,
                        buildStaffErrorFallback("load the governance groups")
                      )
                    : undefined
                }
              />
              <MemberStaffNotesCard
                notes={[]}
                draftComment={staffNoteDraft}
                onDraftCommentChange={setStaffNoteDraft}
                onAdd={(body) => {
                  markDirty();
                  setFormData((current) => ({
                    ...current,
                    initialStaffNote: body,
                  }));
                  setInitialStaffNoteCreatedAt(Date.now());
                }}
                pendingNote={
                  formData.initialStaffNote
                    ? {
                        body: formData.initialStaffNote,
                        createdAtUtcMilli: initialStaffNoteCreatedAt,
                      }
                    : null
                }
                authorDisplayName={
                  [authenticatedUser?.firstName, authenticatedUser?.lastName]
                    .filter(Boolean)
                    .join(" ") ||
                  authenticatedUser?.email ||
                  ""
                }
                authorUserId={authenticatedUser?.id || ""}
                chamberTimeZone={chamberSettings?.setup.timeZone}
              />
            </>
          )}

          {isMembershipBillingStep && !chamberSettings && (
            // Membership/payment options are settings-driven with no mock
            // fallback (MEM-CREATE-022): hold the step behind a loading (or
            // error) state until Chamber Set Up data is available.
            <SectionShell
              id="membership-billing-loading"
              title="Membership Type & Billing"
              description={
                chamberSettingsError
                  ? "We could not load your membership settings, so the membership and billing options are not shown. Refresh the page to try again."
                  : `Membership types, payment schedules, and billing options come from your ${noun.Singular} Set Up.`
              }
              variant="design"
            >
              {!chamberSettingsError && (
                <div className="flex items-center gap-3 py-4 text-sm text-black">
                  <CircularProgress size={22} sx={{ color: cpaGold }} />
                  Loading membership and billing options from {noun.Singular} Set Up.
                </div>
              )}
            </SectionShell>
          )}

          {isMembershipBillingStep && !!chamberSettings && (
            <>
              <MemberBillingStep
                appearance="design"
                value={formData.membership}
                membershipOptions={membershipModels.map((model) => ({
                  value: model.id,
                  label: model.name,
                }))}
                afterMembershipTypeField={
                  showCharityRegistrationNumber ? (
                    <FormTextField
                      id={MEMBER_CHARITY_REGISTRATION_NUMBER_FIELD_ID}
                      label={MEMBER_CHARITY_REGISTRATION_NUMBER_LABEL}
                      description={MEMBER_CHARITY_REGISTRATION_NUMBER_HELPER}
                      descriptionClassName="mb-[6px] text-sm leading-[normal] text-[#727272]"
                      placeholder={MEMBER_CHARITY_REGISTRATION_NUMBER_PLACEHOLDER}
                      value={formData.companyDetails.charityRegistrationNumber || ""}
                      onChange={(charityRegistrationNumber) =>
                        updateSection("companyDetails", {
                          charityRegistrationNumber,
                        })
                      }
                      containerClassName="mb-0 mt-5 min-w-0 max-w-[420px]"
                      disabled={applicationSubmissionBusy}
                    />
                  ) : null
                }
                selectedMembershipSummary={{
                  code: feeBreakdown.membershipCode,
                  description:
                    selectedMembershipModel?.description ||
                    "Investment amount is automatically populated based on the membership selected.",
                  amountLabel: formatCurrency(
                    feeBreakdown.membershipInvestmentAmount,
                  ),
                  // Spec §7: when prorated signup pricing lowers the
                  // first-year fee, show both numbers instead of silently
                  // substituting the prorated price.
                  ...(feeBreakdown.proratedDiscount > 0
                    ? {
                        proration: {
                          proratedAmountLabel: formatCurrency(
                            feeBreakdown.membershipInvestmentAmount,
                          ),
                          regularAmountLabel: formatCurrency(
                            feeBreakdown.baseAnnualAmount,
                          ),
                        },
                      }
                    : {}),
                }}
                paymentScheduleOptions={availablePaymentScheduleOptions}
                paymentMethodOptions={onboardingFeePaymentMethods.map(
                  (method) => ({
                    value: method.id,
                    label: method.label,
                  }),
                )}
                billingOptions={visibleMembershipBillingOptions}
                addOns={selectedMembershipAddOns.map((addOn) => ({
                  // BILL-003: add-ons are always displayed at the full
                  // (annual) amount — they can never be paid in monthly
                  // installments, so no schedule-based price switch.
                  id: addOn.id,
                  code: addOn.code,
                  name: addOn.name,
                  description: addOn.description,
                  amountLabel: formatCurrency(addOn.annualAmount),
                }))}
                addOnsTotalLabel={formatCurrency(feeBreakdown.additionalCosts)}
                invoiceTotalLabel={formatCurrency(
                  feeBreakdown.currentAmountDue,
                )}
                isMonthlySchedule={
                  selectedPaymentSchedule ===
                  ChamberMemberProPaymentScheduleEnum.Monthly
                }
                membershipInvoiceTotalLabel={formatCurrency(
                  feeBreakdown.membershipInvoiceTotal,
                )}
                membershipInvoiceTotalInDollars={
                  feeBreakdown.membershipInvoiceTotal
                }
                addOnInvoiceTotalLabel={formatCurrency(
                  feeBreakdown.addOnInvoiceTotal,
                )}
                showPaymentSchedule={shouldShowMembershipPaymentOption}
                showPaymentMethod={
                  hasPayableMemberFacingReceivable &&
                  !selectedMembershipIsThirdPartyPaid
                }
                paymentMethodTitle="Select Payment Method For Member-facing Invoices"
                paymentMethodDescription={`A positive one-time onboarding fee and/or add-on benefits invoice must use a payment method enabled in ${noun.Singular} Set Up, even when the membership itself has no charge.`}
                membershipDiscountEnabled={membershipDiscountSettingEnabled}
                oneTimeOnboardingFee={
                  // Hidden for a type that skips the fee (no $0.00 charge).
                  configuredOneTimeOnboardingFee?.enabled &&
                  !selectedMembershipModel?.excludeOneTimeOnboardingFee
                    ? {
                        code:
                          configuredOneTimeOnboardingFee.code || "ONBOARDING",
                        description:
                          configuredOneTimeOnboardingFee.description ||
                          "One-Time Onboarding Fee",
                        amountLabel: formatCurrency(feeBreakdown.onboardingFee),
                      }
                    : undefined
                }
                membershipTierMode={membershipTierMode}
                employeeCountsRequired
                errors={{
                  employeeCount: getError(errors, "employeeCount"),
                  fullTimeEmployees: getError(errors, "fullTimeEmployees"),
                  partTimeEmployees: getError(errors, "partTimeEmployees"),
                  membershipTypeId: getError(errors, "membershipTypeId"),
                  paymentSchedule: getMemberPaymentScheduleDisplayError({
                    formError: getError(errors, "paymentSchedule"),
                    resolution: paymentScheduleResolution,
                  }, noun),
                  membershipDiscountCode: getError(
                    errors,
                    "membershipDiscountCode",
                  ),
                  membershipDiscountDescription: getError(
                    errors,
                    "membershipDiscountDescription",
                  ),
                  membershipDiscountAmount: getError(
                    errors,
                    "membershipDiscountAmount",
                  ),
                  billingResponsibility: getError(
                    errors,
                    "billingResponsibility",
                  ),
                  paymentMethod: getError(errors, "paymentMethod"),
                  emailCommunicationConsentConfirmed: getError(
                    errors,
                    "emailCommunicationConsentConfirmed",
                  ),
                }}
                thirdPartyBillingErrors={getInvoicePartyFieldErrors(
                  errors,
                  "thirdPartyBillingSnapshot.",
                )}
                onFullTimeEmployeesChange={(value) => {
                  setErrors((current) =>
                    getCreateMemberErrorsAfterRequiredValueChange(
                      current,
                      "fullTimeEmployees",
                      value,
                    ),
                  );
                  updateSection("membership", { fullTimeEmployees: value });
                }}
                onPartTimeEmployeesChange={(value) => {
                  setErrors((current) =>
                    getCreateMemberErrorsAfterRequiredValueChange(
                      current,
                      "partTimeEmployees",
                      value,
                    ),
                  );
                  updateSection("membership", { partTimeEmployees: value });
                }}
                onMembershipTypeChange={(value) => {
                  const nextMembershipModel = membershipModels.find(
                    (model) => model.id === value,
                  );
                  const nextMembershipIsNoCharge =
                    nextMembershipModel?.paymentResponsibility ===
                    ChamberMemberProMembershipModelPaymentResponsibilityEnum.NoCharge;

                  setErrors(getCreateMemberErrorsAfterMembershipTypeChange);

                  updateSection("membership", {
                    membershipTypeId: value,
                    selectedAdditionalCostIds: [],
                    billingResponsibility: nextMembershipIsNoCharge
                      ? "complimentary"
                      : formData.membership.billingResponsibility ===
                            "complimentary" ||
                          formData.membership.billingResponsibility ===
                            "freeLifetime"
                        ? "memberPays"
                        : formData.membership.billingResponsibility,
                    paymentMethod:
                      nextMembershipIsNoCharge &&
                      (!hasPayableOneTimeOnboardingFee ||
                        nextMembershipModel?.excludeOneTimeOnboardingFee)
                        ? "free"
                        : formData.membership.paymentMethod === "free"
                          ? ""
                          : formData.membership.paymentMethod,
                  });
                }}
                onPaymentScheduleChange={(value) => {
                  setErrors((current) => {
                    const nextErrors = { ...current };
                    delete nextErrors.paymentSchedule;
                    return nextErrors;
                  });
                  updateSection("membership", {
                    paymentSchedule: value,
                  });
                }}
                onPaymentMethodChange={(value) =>
                  updateSection("membership", {
                    paymentMethod: value as PaymentMethodType,
                  })
                }
                onMembershipDiscountChange={(membershipDiscount) =>
                  updateSection("membership", {
                    membershipDiscount: {
                      ...formData.membership.membershipDiscount,
                      ...membershipDiscount,
                    },
                  })
                }
                onAdditionalCostToggle={(id, checked) => {
                  const selectedIds =
                    formData.membership.selectedAdditionalCostIds;

                  updateSection("membership", {
                    selectedAdditionalCostIds: checked
                      ? [...selectedIds, id]
                      : selectedIds.filter((selectedId) => selectedId !== id),
                  });
                }}
                onBillingResponsibilityChange={(value) => {
                  const billingResponsibility =
                    value as BillingResponsibilityOption;
                  const currentSnapshot =
                    formData.membership.thirdPartyBillingSnapshot;
                  const affiliatedCompanyName =
                    selectedPrimaryMemberCompany?.name || "";

                  updateSection("membership", {
                    billingResponsibility,
                    ...(billingResponsibility === "affiliatedPrimaryCompany" &&
                    !currentSnapshot.customerName.trim()
                      ? {
                          thirdPartyBillingSnapshot: {
                            ...currentSnapshot,
                            customerName: affiliatedCompanyName,
                          },
                          thirdPartyPayerName: affiliatedCompanyName,
                        }
                      : {}),
                  });
                }}
                onThirdPartyBillingSnapshotChange={(snapshot) => {
                  setErrors((currentErrors) =>
                    Object.fromEntries(
                      Object.entries(currentErrors).filter(
                        ([key]) =>
                          !key.startsWith("thirdPartyBillingSnapshot."),
                      ),
                    ),
                  );
                  updateSection("membership", {
                    thirdPartyBillingSnapshot: snapshot,
                    thirdPartyPayerName: snapshot.customerName,
                  });
                }}
                onAutoRenewChange={(checked) =>
                  updateSection("membership", { autoRenew: checked })
                }
                onEmailConsentChange={(checked) => {
                  setErrors((current) =>
                    getCreateMemberErrorsAfterEmailConsentChange(
                      current,
                      checked,
                    ),
                  );
                  updateSection("membership", {
                    emailCommunicationConsentConfirmed: checked,
                  });
                }}
              />
            </>
          )}

          {isDirectoryStep && (
            <>
              {!prospectIdFromQuery ? <RejoiningMemberCard chamberId={chamberId} /> : null}
              <SectionShell
                id="directory-info"
                title="Member Directory Information"
                description="Enter the member information as it will appear on both the website and the Chamber Perks App™ directories" descriptionLineHeight="normal"
                variant="design"
              >
                <FormControlLabel
                  className="mb-6"
                  control={
                    <Checkbox
                      checked={!formData.directory.showInDirectory}
                      onChange={(event) =>
                        updateSection("directory", {
                          showInDirectory: !event.target.checked,
                        })
                      }
                    />
                  }
                  label={
                    <span className="text-sm leading-6 text-black">
                      Don't show in member directory
                    </span>
                  }
                />
                <FormTextField
                  id="company-name"
                  label="Member Name"
                  value={formData.directory.companyName}
                  onChange={(value) =>
                    updateSection("directory", { companyName: value })
                  }
                  placeholder="Enter Member Name as it will Appear in our Directories"
                  error={getError(errors, "companyName")}
                  required
                />
                {/* CMP Dashboard (21) :5931-5935 (IF11 MB3). */}
                <FormTextField
                  id="numbered-company"
                  label="Numbered Company"
                  description="This field will not be shown in member directory listings."
                  value={formData.companyDetails.numberedCompany || ""}
                  onChange={(numberedCompany) =>
                    updateSection("companyDetails", { numberedCompany })
                  }
                  placeholder="If applicable, enter your numbered company"
                />
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <FormTextField
                    id="company-website"
                    error={getError(errors, "companyWebsite")}
                    label="Member Website"
                    value={formData.directory.companyWebsite}
                    onChange={(value) =>
                      updateSection("directory", { companyWebsite: value })
                    }
                    placeholder="Enter Company Website URL"
                  />
                  <FormTextField
                    id="google-review"
                    error={getError(errors, "googleReviewUrl")}
                    label="Member Google Review URL"
                    value={formData.directory.googleReviewUrl}
                    onChange={(value) =>
                      updateSection("directory", { googleReviewUrl: value })
                    }
                    placeholder="Enter Google Review URL"
                  />
                </div>
                <div className="grid grid-cols-1 gap-5 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,0.8fr)_76px]">
                  <FormTextField
                    id="main-contact-name"
                    label="Main Contact Name"
                    value={formData.directory.mainCompanyContactName}
                    onChange={(value) =>
                      updateSection("directory", {
                        mainCompanyContactName: value,
                      })
                    }
                    placeholder="Enter Main Contact Name"
                  />
                  <FormTextField
                    id="main-contact-email"
                    label="Main Contact Email"
                    value={formData.directory.mainCompanyContactEmail}
                    onChange={(value) =>
                      updateSection("directory", {
                        mainCompanyContactEmail: value,
                      })
                    }
                    placeholder="Enter Company Contact Email"
                    error={getError(errors, "mainCompanyContactEmail")}
                  />
                  <FormPhoneField
                    id="main-contact-phone"
                    label="Main Phone"
                    value={formData.directory.mainCompanyPhone}
                    onChange={(value) =>
                      updateSection("directory", { mainCompanyPhone: value })
                    }
                    placeholder="Enter Company Phone"
                  />
                  <FormTextField
                    id="main-contact-ext"
                    label="Ext."
                    value={formData.directory.mainCompanyPhoneExt}
                    onChange={(value) =>
                      updateSection("directory", { mainCompanyPhoneExt: value })
                    }
                    placeholder="0000"
                  />
                </div>
                {/* IF11 MB4 / Q30: Toll-Free Phone and Fax Number under Main Phone for every
                    chamber (Mockup Thunderbay Chamber Set Up :4197-4202, Update Member Form :134-142). */}
                <div className="grid grid-cols-1 gap-5 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,0.8fr)_76px]">
                  <FormTextField
                    id="member-toll-free-phone"
                    label="Toll-Free Phone"
                    value={formData.directory.tollFreePhone || ""}
                    onChange={(value) => updateSection("directory", { tollFreePhone: formatStaffTollFreeInput(value) })}
                    placeholder="Enter Toll-Free Phone"
                    inputMode="tel"
                    error={getError(errors, "tollFreePhone")}
                  />
                  <FormTextField
                    id="member-fax-number"
                    label="Fax Number"
                    value={formData.directory.faxNumber || ""}
                    onChange={(value) => updateSection("directory", { faxNumber: formatStaffFaxInput(value) })}
                    placeholder="Enter Fax Number"
                    inputMode="tel"
                    error={getError(errors, "faxNumber")}
                  />
                </div>
                <FormTextField
                  id="company-description"
                  label="Member Description"
                  value={formData.directory.companyDescription}
                  onChange={(value) =>
                    updateSection("directory", { companyDescription: value })
                  }
                  placeholder="Enter Member Details About Your Company"
                  multiline
                  rows={4}
                  description={
                    <>
                      <span className="block">
                        Complete the member's company description to improve
                        their visibility in both the website and Chamber Perks
                        App™ directories. A detailed, keyword-rich profile helps
                        customers, members, and AI-powered search tools better
                        understand your business, improving search ability and
                        increasing your chances of being found online.
                      </span>
                      <span className="mt-1 block">
                        <strong>Need help?</strong> Click Generate and let IDA,
                        our AI-powered assistant, create a professional, SEO and
                        AI-friendly description based on your member's name,
                        website URL, and the contents of your description field.
                        Review and edit the suggested description before using
                        it.
                      </span>
                    </>
                  }
                  descriptionClassName="mb-1.5 text-[13px] leading-[1.5] text-[#0A0A0A]"
                  maxLength={2000}
                  characterCountAlign="left"
                  characterCountAccessory={
                    <IdaGenerateControl
                      onClick={() =>
                        void memberIda.generate(
                          IdaGenerateMode.CompanyDescription,
                        )
                      }
                      loading={
                        memberIda.mode === IdaGenerateMode.CompanyDescription
                      }
                      disabled={Boolean(memberIda.mode)}
                    />
                  }
                />
                <div className="grid grid-cols-1 gap-7 lg:grid-cols-2">
                  <LogoUploadField
                    id="create-member-logo"
                    fileName={formData.logo.fileName}
                    previewUrl={formData.logo.previewUrl}
                    error={logoError}
                    onUpload={handleLogoChange}
                    onRemove={removeLogo}
                  />
                  <div className="min-w-0">
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                      <p className="text-sm font-bold text-gray-950">
                        Website and Chamber Perks App™ Directory Categories{" "}
                        <span className="text-red-500">*</span>
                      </p>
                    </div>
                    <p className="mb-1.5 text-[13px] leading-[1.5] text-[#0A0A0A]">
                      Select 1 to 2 categories from the dropdown that best
                      represent the member.
                      <br />
                      <strong>Need help?</strong> Click Generate and let IDA
                      AI-powered search tool to recommend the most relevant
                      categories, ensuring your member is grouped with similar
                      companies for better visibility.
                    </p>
                    {categoriesError ? (
                      <Alert severity="warning">
                        Could not load directory categories. Refresh the page
                        and try again.
                      </Alert>
                    ) : showCategoryEmptyState ? (
                      <Alert severity="warning">
                        No directory categories are available, so you cannot
                        finish this step. To add them, email{" "}
                        {STAFF_SUPPORT_EMAIL}.
                      </Alert>
                    ) : (
                      <SearchableSelectField
                        id="directory-categories"
                        multiple
                        value={formData.directory.directoryCategoryIds}
                        options={directoryCategories.map((category) => ({
                          value: category.id,
                          label: category.name,
                        }))}
                        onChange={(values) => {
                          updateSection("directory", {
                            directoryCategoryIds: directoryCategories
                              .filter((category) =>
                                values.includes(category.id),
                              )
                              .map((category) => category.id),
                          });
                          setErrors((current) => ({
                            ...current,
                            directoryCategoryIds: "",
                          }));
                        }}
                        maxSelected={2}
                        placeholder="Select Directory Categories"
                        searchPlaceholder="Search Categories"
                        loading={categoriesLoading}
                        error={errors.directoryCategoryIds}
                      />
                    )}
                    <div className="mt-2 flex justify-end">
                      <IdaGenerateControl
                        onClick={() =>
                          void memberIda.generate(
                            IdaGenerateMode.DirectoryCategories,
                          )
                        }
                        loading={
                          memberIda.mode === IdaGenerateMode.DirectoryCategories
                        }
                        disabled={
                          Boolean(memberIda.mode) ||
                          categoriesLoading ||
                          Boolean(categoriesError)
                        }
                      />
                    </div>
                    {memberIda.categoryFeedback && (
                      <p role="status" className="mt-1 text-xs text-[#727272]">
                        {memberIda.categoryFeedback}
                      </p>
                    )}
                    {categoriesLoading && (
                      <p className="mt-2 text-sm text-black">
                        Loading directory categories.
                      </p>
                    )}
                  </div>
                </div>
              </SectionShell>
            </>
          )}

          {isDirectoryStep && (
            <>
              <SectionShell
                id="company-address"
                title="Member Address"
                description="Provide the member's company address. This will appear in both our website and the Chamber Perks App™ directories. Unless you click box below to not show." descriptionLineHeight="normal"
                variant="design"
              >
                <div>
                  <FormTextField
                    onAddressSelect={(address) => updateMemberAddress(googleAddressToMemberFields(address))}
                    id="street-address"
                    label="Street Address"
                    value={formData.address.streetAddress}
                    onChange={(value) =>
                      updateMemberAddress({ streetAddress: value })
                    }
                    placeholder="Enter Street Address, Example: 123 Maple E.g."
                    error={getError(errors, "streetAddress")}
                    required={!formData.address.hideStreetAddressInDirectories}
                  />
                </div>
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <FormTextField
                    id="city"
                    label="City"
                    value={formData.address.city}
                    onChange={(value) =>
                      updateMemberAddress({ city: value })
                    }
                    placeholder="Enter City Name"
                    error={getError(errors, "city")}
                    required
                  />
                  <SearchableSelectField
                    id="province-state"
                    label="Province/State"
                    value={formData.address.provinceOrState}
                    onChange={(value) =>
                      updateMemberAddress({ provinceOrState: value })
                    }
                    options={provinceOptions.map((province) => ({
                      value: province,
                      label: province,
                    }))}
                    placeholder="Select Province/State"
                    error={getError(errors, "provinceOrState")}
                    required
                  />
                  <FormTextField
                    id="postal-code"
                    label="Postal/Zip Code"
                    value={formData.address.postalOrZipCode}
                    onChange={(value) =>
                      updateMemberAddress({ postalOrZipCode: value })
                    }
                    placeholder="Enter the Postal/Zip Code"
                    error={getError(errors, "postalOrZipCode")}
                    required={!formData.address.hideStreetAddressInDirectories}
                  />
                  <FormCountryField
                    id="country"
                    label="Country"
                    value={formData.address.country}
                    onChange={(value) =>
                      updateMemberAddress({ country: value })
                    }
                    error={getError(errors, "country")}
                    required
                  />
                </div>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.address.hideStreetAddressInDirectories}
                      onChange={(event) =>
                        updateSection("address", {
                          hideStreetAddressInDirectories: event.target.checked,
                        })
                      }
                    />
                  }
                  label={
                    <span className="text-sm leading-6 text-black">
                      Check this box if you don’t want the address to appear in
                      any {noun.singular} directories. City and province fields will
                      only show.
                    </span>
                  }
                />
              </SectionShell>

              <SectionShell
                id="social-links"
                title="Links to Social Media"
                description="Enter the member’s social media links to display on both our website and the Chamber Perks App™ directories. Copy and paste your URL from each social media account below." descriptionLineHeight="normal"
                variant="design"
              >
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <FormTextField
                    id="facebook-url"
                    error={getError(errors, "facebookUrl")}
                    label="Facebook Business Page URL"
                    value={formData.socialLinks.facebookUrl}
                    onChange={(value) =>
                      updateSection("socialLinks", { facebookUrl: value })
                    }
                    placeholder="Enter Facebook URL"
                  />
                  <FormTextField
                    id="instagram-url"
                    error={getError(errors, "instagramUrl")}
                    label="Instagram Account URL"
                    value={formData.socialLinks.instagramUrl}
                    onChange={(value) =>
                      updateSection("socialLinks", { instagramUrl: value })
                    }
                    placeholder="Enter Instagram URL"
                  />
                  <FormTextField
                    id="threads-url"
                    error={getError(errors, "threadsUrl")}
                    label="Threads Account URL"
                    value={formData.socialLinks.threadsUrl}
                    onChange={(value) =>
                      updateSection("socialLinks", { threadsUrl: value })
                    }
                    placeholder="Enter Threads URL"
                  />
                  <FormTextField
                    id="linkedin-url"
                    error={getError(errors, "linkedinUrl")}
                    label="LinkedIn Page URL"
                    value={formData.socialLinks.linkedinUrl}
                    onChange={(value) =>
                      updateSection("socialLinks", { linkedinUrl: value })
                    }
                    placeholder="Enter LinkedIn URL"
                  />
                  <FormTextField
                    id="x-url"
                    error={getError(errors, "xTwitterUrl")}
                    label="X (Twitter) Account URL"
                    value={formData.socialLinks.xTwitterUrl}
                    onChange={(value) =>
                      updateSection("socialLinks", { xTwitterUrl: value })
                    }
                    placeholder="Enter X (Twitter) URL"
                  />
                  <FormTextField
                    id="tiktok-url"
                    error={getError(errors, "tikTokUrl")}
                    label="TikTok Account URL"
                    value={formData.socialLinks.tikTokUrl}
                    onChange={(value) =>
                      updateSection("socialLinks", { tikTokUrl: value })
                    }
                    placeholder="Enter TikTok URL"
                  />
                  <FormTextField
                    id="youtube-url"
                    error={getError(errors, "youtubeUrl")}
                    label="Youtube Channel URL"
                    value={formData.socialLinks.youtubeUrl}
                    onChange={(value) =>
                      updateSection("socialLinks", { youtubeUrl: value })
                    }
                    placeholder="Enter Youtube URL"
                  />
                </div>
              </SectionShell>
            </>
          )}

          {isContactStep && (
            <>
              <section id="company-contacts" className="scroll-mt-28">
                <h3 className="text-[17px] font-bold leading-6 text-black">
                  Get to Know Your Member
                </h3>
                <p className="mt-2 text-sm leading-[1.55] text-black">
                  Enter all the required and optional member contacts, so our
                  {noun.singular} can connect with the right people, improve
                  communication, and provide more relevant support and value for
                  this member. This information helps your {noun.singular} better
                  communicate with and serve your member.
                </p>
                <p className="mt-2.5 text-sm font-bold leading-[1.55] text-black">
                  The below contacts are provided for internal use by our
                  {noun.singular} and will not be made public or shared on any
                  directories unless entered in as the Main Member contact.
                </p>
              </section>

              <div className="space-y-6">
                {createMemberContactSections.map((section) => (
                  <ContactCard
                    key={section.role}
                    title={section.title}
                    description={applyCmpOrganizationNoun(section.description, noun)}
                    notice={internalContactNotice}
                    contact={formData.contacts[section.role]}
                    errors={errors}
                    errorPrefix={section.role}
                    sameAsLabel={section.sameAsLabel}
                    onSameAsChange={
                      section.role === "primaryVoting"
                        ? copyMainContactToPrimary
                        : () =>
                            copyPrimaryContactToRole(
                              section.role as Exclude<
                                ContactRole,
                                "primaryVoting"
                              >,
                            )
                    }
                    onChange={(field, value) =>
                      updateContact(section.role, field, value)
                    }
                    nameLabel={section.nameLabel}
                    phoneLabel={section.phoneLabel}
                    emailPlaceholder={section.emailPlaceholder}
                    namePlaceholder={section.namePlaceholder}
                    phonePlaceholder={section.phonePlaceholder}
                    requiredFields={section.requiredFields}
                    accessNameMissing={contactAccessTried && isContactAccessDetailsMissing(getCreateContactValidationInput(formData, section)) && !formData.contacts[section.role].name.trim()}
                    accessEmailMissing={contactAccessTried && isContactAccessDetailsMissing(getCreateContactValidationInput(formData, section)) && !hasEmailShape(formData.contacts[section.role].email.trim())}
                    accessControl={section.role === "primaryVoting" ? undefined : (() => {
                      const sameAsPrimary = isCreateContactSameAsPrimary(formData, section.role);
                      const checked = !sameAsPrimary && !!formData.dashboardAccessRequestedContactIds?.includes(section.role);
                      return (
                        <MemberContactAccessGrant
                          contactId={section.role}
                          label={applyCmpOrganizationNoun(createMemberContactAccessGrantLabel, noun)}
                          checked={checked}
                          disabled={(sameAsPrimary && !checked) || applicationSubmissionBusy}
                          nudge={!sameAsPrimary && contactAccessTried && isContactAccessDetailsMissing(getCreateContactValidationInput(formData, section))
                            ? contactAccessMissingDetailsMessage
                            : undefined}
                          note={sameAsPrimary ? contactSameAsPrimaryAccessMessage : undefined}
                          nudgeTitle={sameAsPrimary ? contactSameAsPrimaryAccessMessage : undefined}
                          onChange={(enabled) => setContactDashboardAccessRequest(section.role, enabled)}
                        />
                      );
                    })()}
                  >
                    {section.includesBillingAddress && (
                      <div className="flex flex-col gap-5">
                        <h4 className="mt-1.5 text-[15px] font-bold text-black">
                          Billing/Mailing Address
                        </h4>
                        <FormControlLabel
                          sx={{ margin: 0 }}
                          control={
                            <Checkbox
                              checked={!!formData.billingAddress.sameAsMemberAddress}
                              onChange={(event) =>
                                toggleBillingSameAsMemberAddress(
                                  event.target.checked,
                                )
                              }
                            />
                          }
                          label={
                            <span className="text-sm leading-6 text-black">
                              Check here if same as Member Address
                            </span>
                          }
                        />
                        <FormTextField
                          onAddressSelect={(address) => updateBillingAddress(googleAddressToMemberFields(address))}
                          id="billing-street-address"
                          label="Street Address"
                          value={formData.billingAddress.streetAddress}
                          onChange={(value) =>
                            updateBillingAddress({ streetAddress: value })
                          }
                          placeholder="Enter Street Address, Example 123 Maple E.g."
                          error={getError(
                            errors,
                            "billingAddress.streetAddress",
                          )}
                          required
                        />
                        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                          <FormTextField
                            id="billing-city"
                            label="City"
                            value={formData.billingAddress.city}
                            onChange={(value) =>
                              updateBillingAddress({ city: value })
                            }
                            placeholder="Enter City Name"
                            error={getError(errors, "billingAddress.city")}
                            required
                          />
                          <SearchableSelectField
                            id="billing-province-state"
                            label="Province/State"
                            value={formData.billingAddress.provinceOrState}
                            onChange={(value) =>
                              updateBillingAddress({ provinceOrState: value })
                            }
                            options={provinceOptions.map((province) => ({
                              value: province,
                              label: province,
                            }))}
                            placeholder="Select Province/State"
                            error={getError(
                              errors,
                              "billingAddress.provinceOrState",
                            )}
                            required
                          />
                          <FormTextField
                            id="billing-postal-code"
                            label="Postal/Zip Code"
                            value={formData.billingAddress.postalOrZipCode}
                            onChange={(value) =>
                              updateBillingAddress({ postalOrZipCode: value })
                            }
                            placeholder="Enter the Postal/Zip Code"
                            error={getError(
                              errors,
                              "billingAddress.postalOrZipCode",
                            )}
                            required
                          />
                          <FormCountryField
                            id="billing-country"
                            label="Country"
                            value={formData.billingAddress.country}
                            onChange={(value) =>
                              updateBillingAddress({ country: value })
                            }
                            error={getError(errors, "billingAddress.country")}
                            required
                          />
                        </div>
                      </div>
                    )}
                  </ContactCard>
                ))}
              </div>
              {/* CMP Dashboard (21) :6189-6229 (IF11 MB6): Additional
                  Contacts are added here and saved with the member. */}
              <div className="mt-6 flex flex-col gap-6" data-create-member-additional-contacts>
                <CustomContactCards
                  value={additionalContacts}
                  onChange={setAdditionalContacts}
                  onSave={async (next) => {
                    setAdditionalContacts(next);
                    return true;
                  }}
                  createMode
                />
              </div>
            </>
          )}
        </FormPageShell>
      </MemberDesignSurface>
      <MissingRequiredFieldsDialog open={missingFieldRows.length > 0} rows={missingFieldRows} onClose={() => setMissingFieldRows([])} />
      <DesignDialog
        open={Boolean(pendingAffinityEnrollment)}
        onClose={() => setPendingAffinityEnrollment(null)}
        title="Affinity Program Partner"
        message="Do you want to add this Affinity Program to this member?"
        icon="warning"
        actions={
          <>
            <DesignDialogButton onClick={handleConfirmAffinityEnrollment}>
              Proceed
            </DesignDialogButton>
            <DesignDialogButton
              tone="dark"
              onClick={() => setPendingAffinityEnrollment(null)}
            >
              Cancel
            </DesignDialogButton>
          </>
        }
      />

      <DesignDialog
        open={Boolean(editingAffinityEnrollment)}
        onClose={() => setEditingAffinityEnrollment(null)}
        title={`Update ${editingAffinityEnrollment?.affinityProgramPartnerName || ""}`}
        size="wide"
        actions={
          <>
            <DesignDialogButton
              tone="dark"
              onClick={() => setEditingAffinityEnrollment(null)}
            >
              Cancel
            </DesignDialogButton>
            <DesignDialogButton onClick={handleSaveEditingAffinityEnrollment}>
              Update
            </DesignDialogButton>
          </>
        }
      >
        <MemberDesignSurface ref={affinityDialogContentRef} className="mt-[18px] w-full text-left">
          {editingAffinityEnrollment && (
            <div className="pt-2">
              {/* Mock (Annotation E): the three billing options stack
                  vertically with the "(DO NOT INVOICE MEMBER)" prefix bold. */}
              <RadioChoiceGroup
                label="Billing Options"
                options={affinityBillingOptions}
                value={editingAffinityEnrollment.billingOption}
                onChange={(value) =>
                  handleUpdateEditingAffinityEnrollment("billingOption", value)
                }
                columns="single"
                renderOptionLabel={renderAffinityBillingOptionLabel}
              />
              <FormSelectField
                id="edit-affinity-status"
                label="Status"
                menuContainer={() => affinityDialogContentRef.current}
                value={editingAffinityEnrollment.status}
                onChange={(value) =>
                  handleUpdateEditingAffinityEnrollment("status", value)
                }
                options={affinityEnrollmentStatusOptions.map((option) => ({
                  value: option,
                  label: <AffinityEnrollmentStatusIndicator status={option} />,
                }))}
                placeholder="Select status"
                // Show the same color dot beside the selected status as the
                // enrolled table does (MEM-CREATE-015).
                selectRenderValue={(value) =>
                  value ? (
                    <AffinityEnrollmentStatusIndicator status={value} />
                  ) : (
                    "Select status"
                  )
                }
              />
              <FormTextField
                id="edit-affinity-notes"
                label="Notes"
                value={editingAffinityEnrollment.notes}
                onChange={(value) =>
                  handleUpdateEditingAffinityEnrollment("notes", value)
                }
                placeholder="Enter Notes"
                multiline
                rows={5}
              />
            </div>
          )}
        </MemberDesignSurface>
      </DesignDialog>

      {invoicePreviewOpen &&
        createPortal(
          <TrapFocus open>
            <div
              role="dialog"
              aria-modal="true"
              aria-label={
                addOnInvoicePreview ? "Invoice Previews" : "Invoice Preview"
              }
              tabIndex={-1}
              className="fixed inset-0 z-[10000] overflow-y-auto bg-[#EFF3F8] p-[16px] outline-none md:p-[40px]"
              style={{ fontFamily: '"DM Sans", sans-serif' }}
              onKeyDown={(event) => {
                if (event.key === "Escape") setInvoicePreviewOpen(false);
              }}
            >
              <MemberDesignSurface className="mx-auto max-w-[1070px]">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                  <InvoicePreviewZoomBar
                    zoom={invoiceZoom}
                    onZoomChange={setInvoiceZoom}
                  />
                  <button
                    type="button"
                    autoFocus
                    aria-label="Close invoice preview"
                    onClick={() => setInvoicePreviewOpen(false)}
                    className="flex h-[28px] w-[28px] items-center justify-center rounded-full bg-[#F21B1B] text-white"
                  >
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      aria-hidden="true"
                    >
                      <path d="m3 3 10 10M13 3 3 13" />
                    </svg>
                  </button>
                </div>
                <div className="mb-[22px] flex flex-wrap items-start justify-end gap-4">
                  <Button
                    type="button"
                    variant="contained"
                    startIcon={
                      downloadingInvoicePdf ? (
                        <CircularProgress size={18} sx={{ color: "white" }} />
                      ) : (
                        <PrintOutlinedIcon />
                      )
                    }
                    onClick={handleDownloadPreviewInvoicePdf}
                    disabled={!invoicePreview || downloadingInvoicePdf}
                    sx={{
                      backgroundColor: cpaGold,
                      color: "white",
                      fontWeight: 800,
                      textTransform: "none",
                      "&:hover": { backgroundColor: cpaGoldDark },
                    }}
                  >
                    {downloadingInvoicePdf ? "Preparing..." : "Print PDF"}
                  </Button>
                  <MemberCreatedInvoicePersistenceNotice
                    sendButtonLabel={
                      canSendCreatedInvoicePaymentRequest
                        ? createdInvoiceSendButtonLabel
                        : undefined
                    }
                  />
                  {canSendCreatedInvoicePaymentRequest && (
                    <div className="max-w-[220px]">
                      <Button
                        type="button"
                        variant="contained"
                        fullWidth
                        startIcon={
                          sendingInvoiceRequest ? (
                            <CircularProgress
                              size={18}
                              sx={{ color: "white" }}
                            />
                          ) : (
                            <SendOutlinedIcon />
                          )
                        }
                        onClick={handleApproveAndSendInvoice}
                        disabled={!invoicePreview || sendingInvoiceRequest}
                        sx={{
                          backgroundColor: cpaGold,
                          color: "white",
                          fontWeight: 800,
                          textTransform: "none",
                          "&:hover": { backgroundColor: cpaGoldDark },
                        }}
                      >
                        {sendingInvoiceRequest
                          ? "Sending..."
                          : createdInvoiceSendButtonLabel}
                      </Button>
                      <p className="mt-2 text-center text-[11px] font-medium leading-4 text-gray-500">
                        Emails the invoice to the member.
                      </p>
                    </div>
                  )}
                </div>
                <div
                  ref={invoiceViewportRef}
                  className="w-full overflow-x-auto"
                >
                  <div
                    style={{
                      width: 1070 * invoiceScale,
                      height: invoicePageHeight
                        ? invoicePageHeight * invoiceScale
                        : undefined,
                      // Centred below 100%, as in the other invoice previews.
                      margin: "0 auto",
                    }}
                  >
                    <div
                      ref={invoicePageRef}
                      data-invoice-preview-page
                      style={{
                        width: 1070,
                        transform: `scale(${invoiceScale})`,
                        transformOrigin: "top left",
                      }}
                    >
                      {invoicePreview && (
                        <div className="pt-6">
                          <MemberInvoiceDeliveryFeedback
                            feedback={invoiceDeliveryFeedback}
                          />
                          {/* Functional dialog-level note (not document content), kept
                  from the old hand-built body and relocated above the paper. */}
                          {previewStripeCheckoutUrl && (
                            <p className="mb-4 text-sm text-black">
                              A secure card payment page is ready for this member.
                            </p>
                          )}
                          {blockedCreatedInvoiceDeliveryTargets.length > 0 && (
                            <Alert severity="info" className="mb-4">
                              {blockedCreatedInvoiceDeliveryTargets.map(
                                ({ invoice, kind, reason }) => (
                                  <p key={invoice.id}>
                                    <strong>
                                      {getMemberApplicationPaymentRequestTargetLabel(
                                        kind,
                                      )}
                                      :
                                    </strong>{" "}
                                    {reason}
                                  </p>
                                ),
                              )}
                            </Alert>
                          )}
                          {unverifiedCreatedInvoiceDeliveryTargets.length >
                            0 && (
                            <Alert severity="warning" className="mb-4">
                              {getUnverifiedCreatedInvoiceContinuationCopy({
                                canManageMembers,
                              })}
                            </Alert>
                          )}
                          {/* POL-017: the shared Manage Invoices master document renders
                  the preview (SCR-MC-01/PAR-MC-32) in place of the old
                  hand-built simplified invoice body. */}
                          <InvoicePaper
                            chamber={chamber}
                            detail={invoiceDetailFromRecord(invoicePreview)}
                            row={manageInvoiceRowFromRecord(invoicePreview)}
                          />

                          {/* BILL-003: second, separate invoice for add-on benefits at
                  full amount (never part of the installment plan). */}
                          {addOnInvoicePreview && (
                            <div className="mt-8 border-t-2 border-black pt-6">
                              <div className="mb-6">
                                <p className="text-xl font-bold text-black">
                                  Add-on Benefits Invoice
                                </p>
                                <p className="mt-2 text-sm leading-6 text-black">
                                  Add-on benefits are invoiced separately at
                                  full amount and cannot be paid in
                                  installments.
                                </p>
                              </div>
                              {/* The shared InvoicePaper renders the add-on document's
                      "Additional Benefits & Services Billing Details" block
                      natively off invoiceKind (spec §4), so no override. */}
                              <InvoicePaper
                                chamber={chamber}
                                detail={invoiceDetailFromRecord(
                                  addOnInvoicePreview,
                                )}
                                row={manageInvoiceRowFromRecord(
                                  addOnInvoicePreview,
                                )}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </MemberDesignSurface>
            </div>
          </TrapFocus>,
          document.body,
        )}

      <ActionSuccessDialog
        message={invoiceSuccessMessage}
        onClose={() => setInvoiceSuccessMessage("")}
      />
      <DesignDialog
        open={Boolean(contactCopyBlockedMessage)}
        onClose={() => setContactCopyBlockedMessage("")}
        title="Contact Information Needed"
        message={contactCopyBlockedMessage}
        icon="warning"
        actions={
          <DesignDialogButton
            tone="dark"
            onClick={() => setContactCopyBlockedMessage("")}
          >
            Close
          </DesignDialogButton>
        }
      />

      <IdaGenerateDialog
        open={memberIda.dialogOpen}
        value={memberIda.description}
        onChange={memberIda.setDescription}
        onUse={() => {
          updateSection("directory", {
            companyDescription: memberIda.description.slice(0, 2000),
          });
          memberIda.setDialogOpen(false);
        }}
        onRegenerate={() =>
          void memberIda.generate(IdaGenerateMode.CompanyDescription)
        }
        onClose={() => memberIda.setDialogOpen(false)}
        loading={memberIda.mode === IdaGenerateMode.CompanyDescription}
        error={memberIda.error}
      />
      <ActionSuccessDialog
        message={
          createdSuccess ? getCreateMemberSuccessMessage(createdSuccess) : ""
        }
        highlights={
          createdSuccess
            ? [createdSuccess.name, createdSuccess.amount].filter(Boolean)
            : []
        }
        onClose={handleCloseCreatedSuccess}
      />
      <MemberLoginEmailConflictDialog
        open={!!memberLoginEmailConflictDialog}
        loading={memberLoginEmailRetryInFlight}
        checkedLoginEmail={memberLoginEmailConflictDialog?.checkedLoginEmail}
        suggestedLoginEmail={
          memberLoginEmailConflictDialog?.suggestedLoginEmail || ""
        }
        onCancel={() => resolveMemberLoginEmailConflictDecision("cancel")}
        onUseAlternative={() =>
          resolveMemberLoginEmailConflictDecision("use-alternative")
        }
      />

      <DesignDialog
        open={duplicateDialogOpen}
        onClose={() => setDuplicateDialogOpen(false)}
        title="Possible Duplicate Member"
        message="A member with the same name or email already exists, so you cannot create this member. Select Open Existing Record to review it, or change the member name or email."
        icon="warning"
        actions={
          <>
            <DesignDialogButton
              tone="dark"
              onClick={() => {
                setDuplicateDialogOpen(false);
                setDuplicateMatchesToShow([]);
              }}
            >
              Cancel
            </DesignDialogButton>
            <DesignDialogButton
              disabled={!duplicateRecordId}
              onClick={openDuplicateExistingRecord}
            >
              Open Existing Record
            </DesignDialogButton>
          </>
        }
      >
        <div className="mt-[18px] w-full text-left text-sm">
          <div className="space-y-3">
            {duplicateDialogMatches.map((match) => (
              <div
                key={match.id}
                className="rounded-md border border-gray-200 bg-gray-50 p-4"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-bold text-gray-950">{match.name}</p>
                  <Chip label={match.status} size="small" />
                </div>
                <p className="mt-1 text-sm text-gray-600">{match.email}</p>
                {match.matchReasons && match.matchReasons.length > 0 && (
                  <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-amber-700">
                    {match.matchReasons.map(getDuplicateMatchReasonLabel).join(", ")}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </DesignDialog>
    </>
  );
};

// A Rejoining Member pick reloads Create Member with that member's prefill
// (IF11 MB9): the key starts a fresh form for each prefilled member.
const CreateMember = () => {
  const location = useLocation();
  const prefillId = (location.state as CreateMemberLocationState | null)?.cmpPrefillMember?.id || "";
  return <CreateMemberPage key={prefillId} />;
};

export default CreateMember;
